import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, ReactNode } from 'react';
import { z } from 'zod';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../_shared/hooks';
import { Button, Card, Input, Textarea, Badge, EmptyState } from '../_shared/components';
import clayArch from './assets/clay-arch.png';
import recarveIcon from './assets/icon-recarve.png';
import './styles.css';
import type {
  AlternateAccount,
  AssignmentRow,
  CarveConfig,
  EligibilityStats,
  ExpansionComponent,
  ExpansionModel,
  LockConfig,
  RepOverride,
  RunResult,
} from './engine/types';
import type { ConfigDiffEntry, RubricItem } from './config';
import { DEFAULT_EXPANSION_MODEL, EXPANSION_RUBRIC, fiscalQuarterLabel } from './config';
import { rampDateFor } from './engine/ramp';

const API = '/api/tools/book-carve';

// The Google Sheet export is hidden on prod until the Sheets API is enabled on the Terra Google
// OAuth project (calls currently fail with 403 SERVICE_DISABLED). Preview keeps the button.
const GSHEET_EXPORT_HIDDEN = window.location.hostname.endsWith('terra.clay.com');

interface CarveRole {
  email: string | null;
  name: string | null;
  isAdmin: boolean;
  isOps: boolean;
  isEgsManager: boolean;
  isSgsManager: boolean;
  isHeadOfCx: boolean;
}
interface Rep { name: string; accounts: number; arr: number; manager?: string | null; region?: string | null; isManager?: boolean }
const REGIONS = ['US-W', 'US-E', 'EMEA'] as const;

/** "Tune the logic" LLM chat on the carve page — hidden for now; flip to bring it back. */
const SHOW_TUNE_CHAT = false;
interface UniverseAccount {
  accountId: string;
  accountName: string;
  currentGs: string;
  arr: number;
  daysToRenewal?: number | null;
  renewalQuarter?: string | null;
  expansionScore?: number | null;
  expansionPercentile?: number | null;
  daysCurrentOwner?: number | null;
  healthRating?: string | null;
  healthDescription?: string | null;
  sentiment?: string | null;
  sentimentReason?: string | null;
  /** Which segment's universe the account came from. Only meaningful on a cross-segment pool. */
  segment?: 'ENT' | 'STRAT';
}
interface MustKeepEntry { accountId: string; accountName: string; gs: string }
interface MustMoveEntry { accountId: string; accountName: string; reason: string | null }
// 'keepsilent' = Keep Book AND excluded from handoff-supply forecasts (plain 'keep' still supplies).
type RepAction = 'rebalance' | 'keep' | 'keepsilent' | 'empty' | 'nonew';
interface NewBook { name: string; targetCount: number; targetArr?: number; pinnedText: string; region?: string; manager?: string; startDate?: string; rampDate?: string; assigneeName?: string; assigneeSfdcUserId?: string }

/** Trailing-12mo new-business stats from the snapshot (drives the Full Book estimate default rate). */
interface NewBizStats { medianAcv: number; assignedTrailing12mo: number; repCount: number; perRepPerQuarter: number }
interface BalanceDim { on: boolean; importance: number }
type BalanceKey = 'accounts' | 'arr' | 'renewal' | 'expansion';
type BalanceDims = Record<BalanceKey, BalanceDim>;
// Internal optimizer coefficient an importance of 50 maps to (coef = importance/50 * base).
// Presenting all four dimensions on a common 0-100 scale fixes the "2/25/50/4 looks mismatched" confusion.
const BALANCE_BASE_COEF: Record<BalanceKey, number> = { accounts: 2, arr: 25, renewal: 50, expansion: 4 };
const IMPORTANCE_DEFAULT = 50;
// Seeded importance for a fresh carve (50 stays the coefficient anchor: coef = importance/50 * base).
const IMPORTANCE_DEFAULTS: Record<BalanceKey, number> = { accounts: 60, arr: 40, renewal: 50, expansion: 50 };
const coefToImportance = (key: BalanceKey, coef: number): number => Math.round((coef / BALANCE_BASE_COEF[key]) * IMPORTANCE_DEFAULT);
const importanceToCoef = (key: BalanceKey, imp: number): number => (imp / IMPORTANCE_DEFAULT) * BALANCE_BASE_COEF[key];
// Objective renewal-quarter weights (q2/q3/q4) at importance 50; scaled by the renewal importance.
const BASE_QUARTER_COEF = 400;
// Default fully-ramped existing-GS targets per segment (also seed New Books to Carve defaults).
const SEGMENT_TARGETS: Record<'egs' | 'sgs', { count: number; startCount: number; arr: number }> = {
  // count = fully-ramped team target; startCount = seats carved now (the rest fills via new business).
  egs: { count: 20, startCount: 13, arr: 1_600_000 },
  sgs: { count: 10, startCount: 7, arr: 2_100_000 },
};
interface CarveListItem {
  id: string;
  title: string | null;
  status: string;
  createdByEmail: string | null;
  createdAt: string;
  universeFetchedAt: string | null;
  team: 'egs' | 'sgs';
  latestMetrics: RunResult['metrics'] | null;
}
interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  proposal: { config: CarveConfig; diff: ConfigDiffEntry[]; summaryMd: string } | null;
  runId: string | null;
  createdAt: string;
}
interface CarveDetail {
  id: string;
  title: string | null;
  status: string;
  finalizedAt?: string | null;
  finalizedRunId?: string | null;
  finalizedByEmail?: string | null;
  currentConfig: CarveConfig;
  baseConfig: CarveConfig;
  /** Config captured at carve creation — the "Reset Rules" baseline for the build-settings editor. */
  initialConfig: CarveConfig;
  team: 'egs' | 'sgs';
  currentSummaryMd: string;
  universeFetchedAt: string | null;
  reps: Rep[];
  newBizStats?: NewBizStats | null;
  accountCount: number;
  messages: ChatMessage[];
  latestRun: { id: string; result: RunResult; createdAt: string } | null;
  variants?: { id: string; label: string; result: RunResult; createdAt: string }[];
}
/** A finalized book summary for the homepage tabs (from GET /finalized). */
interface FinalizedBook {
  id: string;
  title: string | null;
  team: 'egs' | 'sgs';
  finalizedAt: string | null;
  finalizedBy: string | null;
  perGs: {
    gs: string;
    manager: string | null;
    isNewOwner: boolean;
    accounts: number;
    arr: number;
    region: string | null;
    assignee: string | null;
    assigneeSfdcUserId: string | null;
    /** New books only: layered ramp date (manual override wins over the startDate derivation). */
    rampDate: string | null;
    accountList: { accountId: string; accountName: string; arr: number; preGs: string; changed: boolean }[];
    /** Reserved expected-new-business seats (month-named placeholders written at finalize). */
    seats?: { name: string; month: string | null }[];
  }[];
  /** Config + growth stats of the finalized run, for the per-rep fill projection chart. */
  config: CarveConfig | null;
  newBizStats: NewBizStats | null;
}
interface Proposal {
  id: string;
  summaryMd: string | null;
  rationale: string | null;
  diff: ConfigDiffEntry[];
  status: string;
  createdByEmail: string | null;
  createdAt: string;
  tally: { eligibleCount: number; approvals: number; rejections: number; threshold: number; passed: boolean };
  myVote: 'approve' | 'reject' | null;
}

/** Comma-formatted ARR text input props (display "1,600,000", parse digits on change). */
function arrInputProps(val: number, set: (n: number) => void) {
  return {
    type: 'text' as const,
    inputMode: 'numeric' as const,
    value: val ? val.toLocaleString() : '',
    onChange: (e: ChangeEvent<HTMLInputElement>) =>
      set(Math.max(0, Number(e.target.value.replace(/[^0-9]/g, '')) || 0)),
  };
}

function money(n: number | null | undefined): string {
  if (n == null) return '—';
  return `$${Math.round(n).toLocaleString()}`;
}
function timeAgo(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// ───────────────────────── Projection settings (fill forecasting) ─────────────────────────

/** The three forecast regions. Every month's amount is stored per region; the total is derived. */
const FORECAST_REGIONS = ['US-W', 'US-E', 'EMEA'] as const;
type ForecastRegion = (typeof FORECAST_REGIONS)[number];
type RegionAmounts = Record<ForecastRegion, number>;

/** One forecast month: expected NEW accounts per region for the whole segment. */
interface ProjectionMonth { month: string; amounts: RegionAmounts } // month = 'yyyy-mm'

/** Team-scoped projection settings (GET/POST /projection-settings): segment-wide expected NEW
 *  accounts per calendar month and region (finance's forecast — the fill charts never exceed it),
 *  the front-load weighting applied while a new book is under target, and the fully-ramped book
 *  size that seeds new-carve settings. */
interface ProjectionSettings {
  months: ProjectionMonth[];
  newBookBias: number;
  /** Fully-ramped book size; seeds "Target accounts / rep" when starting a new carve. */
  targetAccounts?: number | null;
  /** Existing-rep count from the segment's latest carve snapshot (null before any carve exists). */
  repCount?: number | null;
}

function totalAmounts(a: RegionAmounts): number {
  return FORECAST_REGIONS.reduce((sum, r) => sum + (a[r] || 0), 0);
}

/** Spread a segment total evenly across the three regions (integer split, remainder west→east). */
function spreadTotal(total: number): RegionAmounts {
  const base = Math.floor(total / FORECAST_REGIONS.length);
  let remainder = total - base * FORECAST_REGIONS.length;
  const out = {} as RegionAmounts;
  for (const r of FORECAST_REGIONS) {
    out[r] = base + (remainder > 0 ? 1 : 0);
    remainder -= 1;
  }
  return out;
}

/** Normalize a rep/book region string onto a forecast region (null when unknown). */
function forecastRegionOf(region: string | null | undefined): ForecastRegion | null {
  const r = (region ?? '').trim().toUpperCase();
  return (FORECAST_REGIONS as readonly string[]).includes(r) ? (r as ForecastRegion) : null;
}

const MS_DAY = 86_400_000;
function monthKeyUTC(ms: number): string {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}
function daysInMonthUTC(ms: number): number {
  const d = new Date(ms);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
}
function nextMonthKey(key: string): string {
  const [y, m] = key.split('-').map(Number);
  return monthKeyUTC(Date.UTC(y, m, 1)); // m is 1-based, so Date.UTC(y, m) = next month
}
function monthKeyLabel(key: string): string {
  const [y, m] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}
/** Seats only work for arrivals that are 90+ days old by ramp. True when that window is already
 *  entirely in the past (ramp − 90d lands before the current month), so no seat can ever fill. */
function seatWindowClosed(rampDate: string): boolean {
  if (!rampDate) return false;
  const d = new Date(`${rampDate.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return false;
  d.setUTCDate(d.getUTCDate() - 90);
  return monthKeyUTC(d.getTime()) < monthKeyUTC(Date.now());
}

/** Per-region expected new accounts for the month containing ms. The last scheduled month
 *  carries forward past the end of the schedule; the first applies before its start. */
function scheduleAmountsAt(months: ProjectionMonth[], ms: number): RegionAmounts {
  if (!months.length) return { 'US-W': 0, 'US-E': 0, EMEA: 0 };
  const sorted = [...months].sort((a, b) => a.month.localeCompare(b.month));
  const key = monthKeyUTC(ms);
  let amounts = sorted[0].amounts;
  for (const m of sorted) {
    if (m.month <= key) amounts = m.amounts;
    else break;
  }
  return amounts;
}

/** Segment-wide total (all regions) for the month containing ms. */
function scheduleTotalAt(months: ProjectionMonth[], ms: number): number {
  return totalAmounts(scheduleAmountsAt(months, ms));
}

/** Footer label for the schedule: "30/mo" when flat, "30→90/mo by Feb '27" when it steps —
 *  the sims consume the whole monthly schedule, so a current-month-only label misleads. */
function scheduleTrajectoryLabel(months: ProjectionMonth[], ms: number): string {
  const nowTotal = scheduleTotalAt(months, ms);
  if (!months.length) return `${nowTotal}/mo`;
  const last = [...months].sort((a, b) => a.month.localeCompare(b.month))[months.length - 1];
  const lastTotal = totalAmounts(last.amounts);
  if (lastTotal === nowTotal) return `${nowTotal}/mo`;
  return `${nowTotal}→${lastTotal}/mo by ${monthKeyLabel(last.month)}`;
}

/** One book in the joint team fill simulation. */
interface TeamFillBook {
  name: string;
  seed: number;
  target: number;
  /** Ramp date (ms) for new books; null = active now. */
  rampMs: number | null;
  /** New books get the bias weighting while under their target. */
  isNew: boolean;
  /** Forecast region the book draws from (null = unknown; forces the summed-pool fallback). */
  region: ForecastRegion | null;
  /** Reserved expected-new-business seats (already inside seed). Pre-ramp, overflow handoff units
   * fill up to this many seats — shown as the held-for-transfer band that merges at ramp. */
  seats?: number;
}

const SIM_HORIZON_DAYS = 3653; // ~10 years

/**
 * Joint fill simulation under the overflow-handoff model. Each day, each region's forecast amount
 * is split across that region's ACTIVE books by weight — new below-target books get `newBookBias`,
 * everyone else weight 1 — so per-region gains always sum to exactly the forecast.
 *
 * Overflow handoffs: a weight-1 member who is already at/above target does not keep the catch —
 * the unit flows to the pool's earliest-ramping PENDING book with open seats (its held band steps
 * up; the seats merge into its line at ramp). Only when no pending book can take the unit does the
 * catcher's own line step past target (they carry the overage until a destination opens). Pending
 * books take no direct share — their pre-ramp fill comes exclusively from these overflow units
 * (fresh arrivals held for them, or tenured handoffs at ramp — same quantity, different form).
 * `fillDay` records the first crossing of target (null if never within the ~10y horizon).
 *
 * Region fallback: if any book has no region, the whole team draws from a single summed pool
 * instead, so nobody silently gets zero. `regionMode` reports which ran.
 */
function simulateTeamFill(opts: {
  projection: ProjectionSettings;
  books: TeamFillBook[];
  startMs?: number;
}): {
  byBook: Map<string, { steps: { day: number; count: number }[]; fillDay: number | null; held: { day: number; count: number }[] }>;
  regionMode: boolean;
} {
  const { projection, books } = opts;
  const simStart = opts.startMs ?? Date.now();
  const bias = projection.newBookBias > 0 ? projection.newBookBias : 1;
  const regionMode = books.length > 0 && books.every((b) => b.region !== null);
  const state = books.map((b) => ({
    book: b,
    count: b.seed,
    claimed: 0,
    acc: 0,
    fillDay: b.seed >= b.target ? 0 : (null as number | null),
    steps: [] as { day: number; count: number }[],
    // Pre-ramp incoming handoffs/holds queued for this book (chart band; merges at ramp).
    held: [] as { day: number; count: number }[],
  }));
  for (let day = 0; day < SIM_HORIZON_DAYS; day++) {
    const t = simStart + day * MS_DAY;
    const amounts = scheduleAmountsAt(projection.months, t);
    const days = daysInMonthUTC(t);
    const isActive = (s: (typeof state)[number]): boolean => s.book.rampMs === null || s.book.rampMs <= t;
    const weightOf = (s: (typeof state)[number]): number => {
      if (!isActive(s)) return 0; // pending books fill from overflow only
      return s.book.isNew && s.count < s.book.target ? bias : 1;
    };
    // One pool per region (or a single summed pool in fallback mode), each conserved independently.
    const pools: { pool: number; members: (typeof state)[number][] }[] = regionMode
      ? FORECAST_REGIONS.map((r) => ({ pool: (amounts[r] || 0) / days, members: state.filter((s) => s.book.region === r) }))
      : [{ pool: totalAmounts(amounts) / days, members: state }];
    for (const { pool, members } of pools) {
      if (!(pool > 0) || members.length === 0) continue;
      // Earliest-ramping pending book in this pool with open seats — the overflow destination.
      // Same-ramp-month books split units round-robin (fewest-claimed-first), matching the
      // server-side seat ledger's tie-split.
      const overflowDest = (): (typeof state)[number] | null => {
        let dest: (typeof state)[number] | null = null;
        for (const s of members) {
          if (isActive(s)) continue;
          if (s.claimed >= (s.book.seats ?? 0)) continue;
          if (!dest) {
            dest = s;
            continue;
          }
          const sm = monthKeyUTC(s.book.rampMs ?? 0);
          const dm = monthKeyUTC(dest.book.rampMs ?? 0);
          if (sm < dm || (sm === dm && s.claimed < dest.claimed)) dest = s;
        }
        return dest;
      };
      const weights = members.map(weightOf);
      const sumW = weights.reduce((a, w) => a + w, 0);
      if (!(sumW > 0)) continue;
      for (let i = 0; i < members.length; i++) {
        if (weights[i] <= 0) continue;
        const s = members[i];
        s.acc += (pool * weights[i]) / sumW;
        while (s.acc >= 1) {
          s.acc -= 1;
          // Overflow: an at/above-target weight-1 catcher hands the unit to a pending book. New
          // below-target books (bias weight) always keep theirs — that's them filling to target.
          const overflows = !(s.book.isNew && s.count < s.book.target) && s.count >= s.book.target;
          const dest = overflows ? overflowDest() : null;
          if (dest) {
            dest.claimed += 1;
            dest.held.push({ day: day + 1, count: dest.claimed });
          } else {
            s.count += 1;
            s.steps.push({ day: day + 1, count: s.count });
            if (s.fillDay === null && s.count >= s.book.target) s.fillDay = day + 1;
          }
        }
      }
    }
  }
  return { byBook: new Map(state.map((s) => [s.book.name, { steps: s.steps, fillDay: s.fillDay, held: s.held }])), regionMode };
}

/** Build the joint-simulation book list from result/finalized rows + the carve config (targets + ramps). */
function teamBooksFrom(
  rows: { gs: string; isNewOwner: boolean; totalPost: number; region?: string | null; seats?: number }[],
  config: CarveConfig | null,
): TeamFillBook[] {
  return rows.map((r) => {
    const ownerCfg = config?.newOwners?.find((o) => o.name === r.gs);
    const isNew = r.isNewOwner || !!ownerCfg;
    // A manual ramp override (homepage "release") wins over the startDate derivation.
    const rampIso = isNew ? (ownerCfg?.rampDate ?? rampDateFor(ownerCfg?.startDate)) : null;
    const target = isNew
      ? (config?.existingTargetCount ?? Math.max(r.totalPost, ownerCfg?.targetCount ?? r.totalPost))
      : (config?.perRepOverrides?.[r.gs]?.targetCount ?? config?.existingTargetCount ?? r.totalPost);
    const region = forecastRegionOf(r.region ?? config?.repRegions?.[r.gs] ?? ownerCfg?.region);
    // Reserved seats (the book's dummy placeholders, already inside seed) drive the pre-ramp
    // held-for-transfer claim series in the fill charts. The row's own seat count (from the run's
    // dummy rows) wins — the run derives seats now, so the config value is only a legacy fallback.
    const seats = config?.includeDummyAccounts !== false
      ? Math.max(0, Math.round(r.seats ?? ownerCfg?.expectedAccounts ?? 0))
      : 0;
    return { name: r.gs, seed: r.totalPost, target, rampMs: rampIso ? Date.parse(`${rampIso}T00:00:00Z`) : null, isNew, region, seats };
  });
}

const GoogleConnectResponseSchema = z.object({ redirectUrl: z.string().nullish() });
// The export route answers with a problem body: `detail` carries the sentence worth showing and
// `title` is a short fallback label, so only `detail` is read. `message` is the pre-problem body.
const SheetExportErrorSchema = z.object({ detail: z.string().optional(), message: z.string().optional() });
const SheetExportResponseSchema = z.object({ url: z.string() });

export default function BookCarve() {
  const { apiFetch, apiFetchRaw } = useApi();
  const json = useCallback(
    async <T,>(path: string, init?: RequestInit & { timeoutMs?: number }): Promise<T> => {
      const res = await apiFetch(`${API}${path}`, { timeoutMs: 60_000, ...init });
      return (await res.json()) as T;
    },
    [apiFetch],
  );
  const postJson = useCallback(
    <T,>(path: string, body: unknown, timeoutMs = 60_000) =>
      json<T>(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), timeoutMs }),
    [json],
  );

  const [access, setAccess] = useState<{ allowed: boolean; reason: string; role: CarveRole } | null>(null);

  useEffect(() => {
    json<{ allowed: boolean; reason: string; role: CarveRole }>('/access')
      .then(setAccess)
      .catch(() => setAccess({ allowed: false, reason: 'Unable to verify access.', role: {} as CarveRole }));
  }, [json]);

  if (!access) return <div className="bc-loading">Loading Book Carve…</div>;
  if (!access.allowed) {
    return (
      <div className="bc-root">
        <EmptyState title="No access" description={access.reason} />
      </div>
    );
  }

  return (
    <div className="bc-root">
      <header className="bc-header">
        <div className="bc-brand">
          <img className="bc-logo" src={clayArch} alt="" aria-hidden="true" />
          <div>
            <p className="bc-eyebrow">GS book redistribution</p>
            <h1 className="bc-title">Book Carve</h1>
            <p className="bc-subtitle">Seed new-hire books and rebalance enterprise GS accounts.</p>
          </div>
        </div>
      </header>
      <Routes>
        <Route index element={<HomeRoute json={json} postJson={postJson} apiFetchRaw={apiFetchRaw} role={access.role} />} />
        <Route path=":carveId" element={<CarveRoute json={json} postJson={postJson} apiFetchRaw={apiFetchRaw} />} />
      </Routes>
    </div>
  );
}

// Home route: opening a carve pushes its id onto the URL (/tools/book-carve/<id>).
function HomeRoute({ json, postJson, apiFetchRaw, role }: {
  json: <T,>(p: string, i?: RequestInit & { timeoutMs?: number }) => Promise<T>;
  postJson: <T,>(p: string, b: unknown, t?: number) => Promise<T>;
  apiFetchRaw: ReturnType<typeof useApi>['apiFetchRaw'];
  role: CarveRole;
}) {
  const navigate = useNavigate();
  return <Home json={json} postJson={postJson} apiFetchRaw={apiFetchRaw} role={role} onOpen={(id) => navigate(id)} />;
}

// Carve route: the carve id comes straight off the URL, so a cold-loaded deep link works.
function CarveRoute({ json, postJson, apiFetchRaw }: {
  json: <T,>(p: string, i?: RequestInit & { timeoutMs?: number }) => Promise<T>;
  postJson: <T,>(p: string, b: unknown, t?: number) => Promise<T>;
  apiFetchRaw: ReturnType<typeof useApi>['apiFetchRaw'];
}) {
  const navigate = useNavigate();
  const { carveId = '' } = useParams<{ carveId: string }>();
  return <CarveView id={carveId} json={json} postJson={postJson} apiFetchRaw={apiFetchRaw} onBack={() => navigate('..')} />;
}

// ────────────────────────────── Home ──────────────────────────────

function Home({
  json,
  postJson,
  apiFetchRaw,
  role,
  onOpen,
}: {
  json: <T,>(p: string, i?: RequestInit & { timeoutMs?: number }) => Promise<T>;
  postJson: <T,>(p: string, b: unknown, t?: number) => Promise<T>;
  apiFetchRaw: ReturnType<typeof useApi>['apiFetchRaw'];
  role: CarveRole;
  onOpen: (id: string) => void;
}) {
  const [carves, setCarves] = useState<CarveListItem[]>([]);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [canVote, setCanVote] = useState(false);
  const [base, setBase] = useState<{ summaryMd: string; config: CarveConfig } | null>(null);
  const [finalized, setFinalized] = useState<{ egs: FinalizedBook | null; sgs: FinalizedBook | null }>({ egs: null, sgs: null });
  const [finalizedTab, setFinalizedTab] = useState<'egs' | 'sgs'>('egs');
  const [allCarvesOpen, setAllCarvesOpen] = useState(false);
  const [expandedFinalRep, setExpandedFinalRep] = useState<Set<string>>(new Set());
  const toggleFinalRep = (gs: string) =>
    setExpandedFinalRep((prev) => {
      const next = new Set(prev);
      if (next.has(gs)) next.delete(gs); else next.add(gs);
      return next;
    });
  const [error, setError] = useState<string | null>(null);

  // Team projection settings (monthly new-account schedule + bias) shared by the settings card
  // and the Finalized Books fill charts, so a save updates the charts without a reload.
  const [projSettings, setProjSettings] = useState<Partial<Record<'egs' | 'sgs', ProjectionSettings>>>({});
  useEffect(() => {
    let cancelled = false;
    for (const team of ['egs', 'sgs'] as const) {
      json<{ months: ProjectionMonth[]; newBookBias: number; targetAccounts?: number | null; repCount?: number | null }>(`/projection-settings?team=${team}`)
        .then((d) => {
          if (!cancelled) setProjSettings((p) => ({ ...p, [team]: { months: d.months, newBookBias: d.newBookBias, targetAccounts: d.targetAccounts ?? null, repCount: d.repCount ?? null } }));
        })
        .catch(() => {});
    }
    return () => { cancelled = true; };
  }, [json]);
  // New books whose ramp date has passed, per team (from the finalized carve) — part of the
  // denominator in the Forecasting card's derived accts/rep/quarter readout.
  const rampedNewBooksByTeam = useMemo(() => {
    const todayIso = new Date().toISOString().slice(0, 10);
    const count = (b: FinalizedBook | null) =>
      (b?.config?.newOwners ?? []).filter((o) => {
        const r = o.rampDate ?? rampDateFor(o.startDate);
        return !!r && r <= todayIso;
      }).length;
    return { egs: count(finalized.egs), sgs: count(finalized.sgs) };
  }, [finalized]);

  // SFDC user directory (assignee picker) + pending, unsaved assignee edits keyed by book name.
  const canEditAssignee = role.isAdmin || role.isOps;
  const [sfdcUsers, setSfdcUsers] = useState<{ name: string; userId: string }[]>([]);
  const [pendingAssignees, setPendingAssignees] = useState<Map<string, { name: string; userId: string }>>(new Map());
  // Unsaved book/rep renames keyed by the book's original (current) name -> new name.
  const [pendingRenames, setPendingRenames] = useState<Map<string, string>>(new Map());
  // Unsaved ramp-date edits keyed by book name (yyyy-mm-dd). Pulling a date earlier "releases" the
  // book: the Clay flow assigns books whose RAMP_DATE is today (cert permitting).
  const [pendingRamps, setPendingRamps] = useState<Map<string, string>>(new Map());
  const [savingAssignees, setSavingAssignees] = useState(false);
  // Which finalized carve (if any) has the Adjust panel open.
  const [adjustCarveId, setAdjustCarveId] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    json<{ users: { name: string; userId: string }[] }>('/sfdc-users')
      .then((d) => { if (!cancelled) setSfdcUsers(d.users); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [json]);
  const setAssigneeEdit = (book: string, name: string) => {
    const match = sfdcUsers.find((u) => u.name.trim().toLowerCase() === name.trim().toLowerCase());
    setPendingAssignees((prev) => {
      const next = new Map(prev);
      next.set(book, { name, userId: match?.userId ?? '' });
      return next;
    });
  };
  const setRenameEdit = (book: string, name: string) => {
    setPendingRenames((prev) => {
      const next = new Map(prev);
      next.set(book, name);
      return next;
    });
  };
  const setRampEdit = (book: string, date: string) => {
    setPendingRamps((prev) => {
      const next = new Map(prev);
      next.set(book, date);
      return next;
    });
  };
  // Persist book renames first (they change the key the other endpoints match on), then assignee
  // links and ramp dates remapped through the renames, then refresh once.
  const saveFinalizedEdits = async (carveId: string) => {
    const renames = [...pendingRenames.entries()]
      .map(([from, to]) => ({ from, to: to.trim() }))
      .filter((r) => r.to && r.to !== r.from);
    const ramps = [...pendingRamps.entries()].filter(([, d]) => d.trim());
    if (renames.length === 0 && pendingAssignees.size === 0 && ramps.length === 0) return;
    setSavingAssignees(true);
    try {
      if (renames.length > 0) {
        await postJson(`/carves/${carveId}/rename-books`, { renames });
      }
      const renameMap = new Map(renames.map((r) => [r.from, r.to]));
      if (pendingAssignees.size > 0) {
        const assignments = [...pendingAssignees.entries()].map(([book, v]) => ({
          book: renameMap.get(book) ?? book,
          assigneeName: v.name.trim() || null,
          assigneeSfdcUserId: v.userId.trim() || null,
        }));
        await postJson(`/carves/${carveId}/link-assignees`, { assignments });
      }
      if (ramps.length > 0) {
        await postJson(`/carves/${carveId}/set-ramp-dates`, {
          ramps: ramps.map(([book, rampDate]) => ({ book: renameMap.get(book) ?? book, rampDate: rampDate.trim() })),
        });
      }
      setPendingAssignees(new Map());
      setPendingRenames(new Map());
      setPendingRamps(new Map());
      refresh();
    } catch (e) {
      setError(String(e));
    } finally {
      setSavingAssignees(false);
    }
  };

  // Single-account move on the finalized carve: one draft at a time (select a target owner in the
  // expanded account list, then confirm). Backend replaces the Snowflake row, revises history, and
  // sends the delta webhook.
  const [moveDraft, setMoveDraft] = useState<{ accountId: string; toOwner: string; effectiveDate: string } | null>(null);
  const [movingAccount, setMovingAccount] = useState(false);
  const moveAccount = async (carveId: string, accountName: string) => {
    if (!moveDraft) return;
    if (!window.confirm(`Move ${accountName} to ${moveDraft.toOwner}? This updates the finalized assignments table and notifies the Clay flow.`)) return;
    setMovingAccount(true);
    setError(null);
    try {
      await postJson(`/carves/${carveId}/move-account`, {
        accountId: moveDraft.accountId,
        toOwner: moveDraft.toOwner,
        effectiveDate: moveDraft.effectiveDate || undefined,
      }, 120_000);
      setMoveDraft(null);
      refresh();
    } catch (e) {
      setError(String(e));
    } finally {
      setMovingAccount(false);
    }
  };

  const refresh = useCallback(() => {
    json<{ carves: CarveListItem[] }>('/carves').then((d) => setCarves(d.carves)).catch((e) => setError(String(e)));
    json<{ proposals: Proposal[]; canVote: boolean }>('/proposals')
      .then((d) => { setProposals(d.proposals); setCanVote(d.canVote); })
      .catch(() => {});
    json<{ summaryMd: string; config: CarveConfig }>('/base-logic').then(setBase).catch(() => {});
    json<{ egs: FinalizedBook | null; sgs: FinalizedBook | null }>('/finalized').then(setFinalized).catch(() => {});
  }, [json]);

  useEffect(refresh, [refresh]);

  const vote = async (id: string, v: 'approve' | 'reject') => {
    await postJson(`/proposals/${id}/vote`, { vote: v }).catch((e) => setError(String(e)));
    refresh();
  };

  const canDelete = (c: CarveListItem) =>
    role.isAdmin || role.isOps || (!!role.email && c.createdByEmail === role.email);
  const deleteCarve = async (c: CarveListItem) => {
    if (!window.confirm(`Delete "${c.title ?? 'Untitled'}"? This removes the carve and its runs and chat. This cannot be undone.`)) return;
    await json(`/carves/${c.id}`, { method: 'DELETE' }).catch((e) => setError(String(e)));
    refresh();
  };
  const duplicateCarve = async (c: CarveListItem) => {
    try {
      const { id } = await postJson<{ id: string }>(`/carves/${c.id}/duplicate`, {});
      onOpen(id);
    } catch (e) {
      setError(String(e));
    }
  };
  const downloadFinalized = async (team: 'egs' | 'sgs', kind: 'summary' | 'accounts') => {
    const res = await apiFetchRaw(`${API}/finalized/${team}/export/${kind}`, { timeoutMs: 60_000 });
    if (!res.ok) {
      setError(`No finalized ${team === 'egs' ? 'EGS' : 'Strat'} book to export.`);
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `book_carve_${team}_${kind}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  // Export the finalized book to a new 2-tab Google Sheet owned by the caller. A 409 means the
  // user's Terra Google connection is missing the spreadsheets scope — run the one-time consent
  // flow (returning to this page), after which the export is one click.
  const [exportingSheet, setExportingSheet] = useState(false);
  const exportGsheet = async (team: 'egs' | 'sgs') => {
    setExportingSheet(true);
    setError(null);
    try {
      const res = await apiFetchRaw(`${API}/finalized/${team}/export/gsheet`, { method: 'POST', timeoutMs: 60_000 });
      if (res.status === 409) {
        const connect = await apiFetchRaw('/api/calendar/connect', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            returnTo: window.location.pathname,
            extraScopes: ['https://www.googleapis.com/auth/spreadsheets'],
          }),
        });
        const data = GoogleConnectResponseSchema.safeParse(await connect.json());
        if (data.success && data.data.redirectUrl) {
          window.location.href = data.data.redirectUrl;
          return;
        }
        setError('Could not start the Google connection flow.');
        return;
      }
      if (!res.ok) {
        const body = SheetExportErrorSchema.catch({}).parse(await res.json().catch(() => ({})));
        setError(body.detail || body.message || (res.status === 404 ? `No finalized ${team === 'egs' ? 'EGS' : 'Strat'} book to export.` : 'Google Sheet export failed.'));
        return;
      }
      const { url } = SheetExportResponseSchema.parse(await res.json());
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      setError(String(e));
    } finally {
      setExportingSheet(false);
    }
  };

  return (
    <div className="bc-home">
      <NewCarveSetup json={json} postJson={postJson} baseConfig={base?.config ?? null} onCreated={onOpen} />

      <Card className="bc-card-clay" noPadding>
        <div className="bc-card-pad-tight">
          <h2 className="bc-h2">
            <button type="button" className="bc-gs-toggle" onClick={() => setAllCarvesOpen((o) => !o)}>
              <span className="bc-caret bc-caret-lg">{allCarvesOpen ? '▾' : '▸'}</span>
              All Carves{carves.length ? ` (${carves.length})` : ''}
            </button>
          </h2>
          {allCarvesOpen && (
            carves.length === 0 ? (
              <div className="bc-empty">
                <img src={recarveIcon} alt="" aria-hidden="true" />
                <EmptyState title="No carves yet" description="Create one above to get started." />
              </div>
            ) : (
              <table className="bc-table">
                <thead>
                  <tr><th>Team</th><th>Title</th><th>Status</th><th>Changed</th><th>Created by</th><th>When</th><th /></tr>
                </thead>
                <tbody>
                  {carves.map((c) => (
                    <tr key={c.id}>
                      <td>
                        <span
                          className={`bc-seg-badge bc-seg-${c.team}`}
                          title={c.team === 'sgs' ? 'Strategic Growth Segment (SGS)' : 'Enterprise segment (EGS)'}
                        >
                          {c.team === 'sgs' ? 'SGS' : 'EGS'}
                        </span>
                      </td>
                      <td>{c.title ?? 'Untitled'}</td>
                      <td>
                        {c.latestMetrics === null
                          ? <span title="Never run — open it to finish the setup (any manager can add books) and run when ready."><Badge>draft</Badge></span>
                          : <Badge>{c.status}</Badge>}
                      </td>
                      <td>{c.latestMetrics ? `${c.latestMetrics.changedAccounts} moved` : '—'}</td>
                      <td className="bc-muted">{c.createdByEmail ?? '—'}</td>
                      <td className="bc-muted">{timeAgo(c.createdAt)}</td>
                      <td>
                        <div className="bc-row bc-carve-actions">
                          <Button variant="neutral" onClick={() => onOpen(c.id)}>Open</Button>
                          <Button variant="neutral" onClick={() => { void duplicateCarve(c); }}>Duplicate</Button>
                          {canDelete(c) && (
                            <button
                              type="button"
                              className="bc-icon-danger"
                              title="Delete carve"
                              aria-label={`Delete ${c.title ?? 'Untitled'}`}
                              onClick={() => { void deleteCarve(c); }}
                            >
                              🗑
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
        </div>
      </Card>

      <MustKeepCard json={json} postJson={postJson} apiFetchRaw={apiFetchRaw} />

      <MustMoveCard json={json} postJson={postJson} />

      <ManagerOverridesCard json={json} postJson={postJson} />

      <RepSettingsCard json={json} postJson={postJson} />

      <AccountExclusionsCard json={json} postJson={postJson} />

      <SegmentMovesCard json={json} postJson={postJson} />

      {proposals.length > 0 && (
        <Card>
          <div className="bc-card-pad">
            <h2 className="bc-h2">Proposed base-logic changes</h2>
            {proposals.map((p) => (
              <div key={p.id} className="bc-proposal">
                <div className="bc-proposal-head">
                  <Badge>{p.tally.approvals}/{p.tally.threshold} to pass · {p.tally.eligibleCount} eligible</Badge>
                  <span className="bc-muted">by {p.createdByEmail ?? 'unknown'} · {timeAgo(p.createdAt)}</span>
                </div>
                {p.rationale && <p className="bc-rationale">“{p.rationale}”</p>}
                <DiffList diff={p.diff} />
                {canVote ? (
                  <div className="bc-row">
                    <Button variant={p.myVote === 'approve' ? 'success' : 'neutral'} onClick={() => { void vote(p.id, 'approve'); }}>Approve</Button>
                    <Button variant={p.myVote === 'reject' ? 'danger' : 'neutral'} onClick={() => { void vote(p.id, 'reject'); }}>Reject</Button>
                    {p.myVote && <span className="bc-muted">You voted: {p.myVote}</span>}
                  </div>
                ) : (
                  <p className="bc-muted">Only EGS/SGS managers and the Head of CX can vote.</p>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      <ProjectionSettingsCard
        rampedNewBooks={rampedNewBooksByTeam}
        postJson={postJson}
        settings={projSettings}
        onSaved={(team, s) => setProjSettings((p) => ({ ...p, [team]: { ...p[team], ...s } }))}
      />

      <ExpectedHandoffsCard json={json} />

      <Card className="bc-card-clay">
        <div className="bc-card-pad">
          <div className="bc-row bc-spread">
            <h2 className="bc-h2">Finalized Books</h2>
            <div className="bc-tabs">
              <button className={`bc-tab${finalizedTab === 'egs' ? ' bc-tab-on' : ''}`} onClick={() => setFinalizedTab('egs')}>EGS</button>
              <button className={`bc-tab${finalizedTab === 'sgs' ? ' bc-tab-on' : ''}`} onClick={() => setFinalizedTab('sgs')}>Strat</button>
            </div>
          </div>
          {(() => {
            const book = finalizedTab === 'egs' ? finalized.egs : finalized.sgs;
            const label = finalizedTab === 'egs' ? 'EGS' : 'Strat';
            if (!book) {
              return <EmptyState title={`No finalized ${label} book`} description="Finalize a book from its Book Summary to see it here." />;
            }
            return (
              <div className="bc-finalized">
                <div className="bc-row bc-spread bc-finalized-head">
                  <div>
                    <h3 className="bc-h3">{book.title ?? 'Untitled carve'}</h3>
                    <p className="bc-muted">
                      Finalized {timeAgo(book.finalizedAt)}{book.finalizedBy ? ` by ${book.finalizedBy}` : ''}
                    </p>
                  </div>
                  <div className="bc-row">
                    {canEditAssignee && (() => {
                      const pendingCount = pendingAssignees.size + pendingRenames.size + pendingRamps.size;
                      return (
                        <Button
                          variant="primary"
                          disabled={pendingCount === 0 || savingAssignees}
                          onClick={() => { void saveFinalizedEdits(book.id); }}
                        >
                          {savingAssignees ? 'Saving…' : `Save${pendingCount ? ` (${pendingCount})` : ''}`}
                        </Button>
                      );
                    })()}
                    <Button variant="neutral" onClick={() => void downloadFinalized(book.team, 'summary')}>Download Summary CSV</Button>
                    <Button variant="neutral" onClick={() => void downloadFinalized(book.team, 'accounts')}>Download Accounts CSV</Button>
                    {!GSHEET_EXPORT_HIDDEN && (
                      <Button
                        variant="neutral"
                        disabled={exportingSheet}
                        onClick={() => void exportGsheet(book.team)}
                        title="Creates a new Google Sheet in your Drive with a Summary tab and an Accounts tab. First use asks you to grant Sheets access to your Terra Google connection."
                      >
                        {exportingSheet ? 'Exporting…' : 'Export to Google Sheet'}
                      </Button>
                    )}
                    {canEditAssignee && (
                      <Button
                        variant="neutral"
                        onClick={() => setAdjustCarveId((cur) => (cur === book.id ? null : book.id))}
                        title="Add or remove individual books on this finalized carve via a scoped mini-carve."
                      >
                        {adjustCarveId === book.id ? 'Close Adjust' : 'Adjust'}
                      </Button>
                    )}
                    <Button variant="neutral" onClick={() => onOpen(book.id)}>Open carve</Button>
                  </div>
                </div>
                {adjustCarveId === book.id && (
                  <AdjustmentPanel
                    carveId={book.id}
                    json={json}
                    postJson={postJson}
                    onApplied={refresh}
                    onClose={() => setAdjustCarveId(null)}
                  />
                )}
                <div className="bc-table-scroll">
                  <table className="bc-table">
                    <thead>
                      <tr><th>Rep</th><th>Manager</th><th>Assignee</th><th>Ramp Date</th><th>Accounts</th><th>ARR</th></tr>
                    </thead>
                    <tbody>
                      {[...book.perGs].sort((a, b) => Number(b.isNewOwner) - Number(a.isNewOwner)).map((g) => {
                        const open = expandedFinalRep.has(g.gs);
                        const pending = pendingAssignees.get(g.gs);
                        const assigneeVal = pending ? pending.name : (g.assignee ?? '');
                        // Only unmatched new-carve books can be relabeled (no SFDC hire linked yet).
                        const canRename = canEditAssignee && g.isNewOwner && !g.assigneeSfdcUserId;
                        const renameVal = pendingRenames.get(g.gs) ?? g.gs;
                        const seats = g.seats ?? [];
                        // Near-ramp seat nudge: seats not tied to a real arrival by ramp have no
                        // account behind them — surface it while there's still time to Adjust.
                        const rampDays = g.rampDate ? Math.ceil((Date.parse(`${g.rampDate}T00:00:00Z`) - Date.now()) / 86_400_000) : null;
                        const seatNudge = g.isNewOwner && seats.length > 0 && rampDays !== null && rampDays >= 0 && rampDays <= 21;
                        return (
                          <Fragment key={g.gs}>
                            <tr className={g.isNewOwner ? 'bc-final-new' : undefined}>
                              <td>
                                <div className="bc-row bc-gs-cell">
                                  <button type="button" className="bc-gs-toggle" onClick={() => toggleFinalRep(g.gs)}>
                                    <span className="bc-caret">{open ? '▾' : '▸'}</span>
                                    {canRename ? null : g.gs}
                                    {g.isNewOwner ? <Badge>new</Badge> : null}
                                  </button>
                                  {canRename && (
                                    <input
                                      className="bc-mgr-input"
                                      value={renameVal}
                                      onChange={(e) => setRenameEdit(g.gs, e.target.value)}
                                      title="Rename this placeholder book to the offer recipient's name. Saved to the finalized-assignments table; link them via Assignee once they appear in SFDC."
                                    />
                                  )}
                                </div>
                              </td>
                              <td className="bc-muted">{g.manager ?? '—'}</td>
                              <td>
                                {g.isNewOwner && canEditAssignee ? (
                                  <input
                                    className="bc-mgr-input"
                                    list="bc-final-assignee-list"
                                    placeholder="Link a hire…"
                                    value={assigneeVal}
                                    onChange={(e) => setAssigneeEdit(g.gs, e.target.value)}
                                    title="Link the real SFDC user (new hire) receiving this placeholder book. An exact name match records their SFDC id; saving writes it to the finalized-assignments table so the Clay match flow skips this book."
                                  />
                                ) : (
                                  <span className="bc-muted">{g.assignee ?? '—'}</span>
                                )}
                              </td>
                              <td>
                                {g.isNewOwner && canEditAssignee ? (
                                  <input
                                    className="bc-mgr-input bc-ramp-input"
                                    type="date"
                                    min={new Date().toISOString().slice(0, 10)}
                                    value={pendingRamps.get(g.gs) ?? g.rampDate ?? ''}
                                    onChange={(e) => setRampEdit(g.gs, e.target.value)}
                                    title="The day this book gets assigned: the Clay flow hands it to the assignee on this date (cert permitting). Pull it earlier to release the book ahead of the derived schedule. Saved to the finalized-assignments table."
                                  />
                                ) : (
                                  <span className="bc-muted">{g.rampDate ?? '—'}</span>
                                )}
                              </td>
                              <td>
                                {g.accounts}
                                {seats.length > 0 && (
                                  <span
                                    className="bc-muted bc-seat-note"
                                    title={`Reserved expected-new-business seats (open until the Clay routing flow ties a real arriving customer to each):\n${seats.map((s) => s.name).join('\n')}`}
                                  >
                                    {' '}· {seats.length} seat{seats.length === 1 ? '' : 's'}
                                  </span>
                                )}
                                {seatNudge && (
                                  <span
                                    className="bc-seat-nudge"
                                    title={`This book ramps in ${rampDays} day${rampDays === 1 ? '' : 's'} with ${seats.length} reserved seat${seats.length === 1 ? '' : 's'}. Check the finalized-assignments table for seats still open (CATEGORY = 'Expected New Business') — any unfilled at ramp have no real account behind them; use Adjust to pull the shortfall from ramped reps.`}
                                  >
                                    ⚠
                                  </span>
                                )}
                              </td>
                              <td>{money(g.arr)}</td>
                            </tr>
                            {open && (
                              <tr className="bc-sub-row">
                                <td colSpan={6}>
                                  <BookFillChart
                                    row={{ gs: g.gs, isNewOwner: g.isNewOwner, totalPost: g.accounts }}
                                    config={book.config}
                                    newBizStats={book.newBizStats}
                                    projection={projSettings[book.team]}
                                    teamBooks={teamBooksFrom(
                                      book.perGs.map((p) => ({ gs: p.gs, isNewOwner: p.isNewOwner, totalPost: p.accounts, region: p.region, seats: p.seats?.length })),
                                      book.config,
                                    )}
                                  />
                                  {g.accountList.length === 0 && seats.length === 0 ? (
                                    <p className="bc-muted">No accounts.</p>
                                  ) : (
                                    <table className="bc-table bc-finalized-accts">
                                      <thead>
                                        <tr><th>Account</th><th>From</th><th>ARR</th>{canEditAssignee && <th>Move</th>}</tr>
                                      </thead>
                                      <tbody>
                                        {g.accountList.map((a) => {
                                          const draft = moveDraft?.accountId === a.accountId ? moveDraft : null;
                                          const target = draft ? book.perGs.find((p) => p.gs === draft.toOwner) ?? null : null;
                                          return (
                                            <tr key={a.accountId}>
                                              <td>{a.accountName}</td>
                                              <td className="bc-muted">{a.changed ? a.preGs : '—'}</td>
                                              <td>{money(a.arr)}</td>
                                              {canEditAssignee && (
                                                <td>
                                                  <div className="bc-row bc-move-cell">
                                                    <select
                                                      value={draft?.toOwner ?? ''}
                                                      onChange={(e) =>
                                                        setMoveDraft(
                                                          e.target.value
                                                            ? { accountId: a.accountId, toOwner: e.target.value, effectiveDate: new Date().toISOString().slice(0, 10) }
                                                            : null,
                                                        )
                                                      }
                                                      title="Move just this account to another book/rep on the finalized carve. The finalized row is replaced, the change is logged in history, and the Clay flow is notified."
                                                    >
                                                      <option value="">Move to…</option>
                                                      {book.perGs
                                                        .filter((p) => p.gs !== g.gs)
                                                        .map((p) => (
                                                          <option key={p.gs} value={p.gs}>{p.gs}{p.isNewOwner ? ' (new)' : ''}</option>
                                                        ))}
                                                    </select>
                                                    {draft && target && !target.isNewOwner && (
                                                      <input
                                                        className="bc-mgr-input bc-ramp-input"
                                                        type="date"
                                                        min={new Date().toISOString().slice(0, 10)}
                                                        value={draft.effectiveDate}
                                                        onChange={(e) => setMoveDraft({ ...draft, effectiveDate: e.target.value })}
                                                        title="The day the account changes hands (sent to Clay as the assignment date). Moves to a new book use that book's ramp date instead."
                                                      />
                                                    )}
                                                    {draft && (
                                                      <Button
                                                        variant="primary"
                                                        disabled={movingAccount}
                                                        onClick={() => void moveAccount(book.id, a.accountName)}
                                                      >
                                                        {movingAccount ? 'Moving…' : 'Move'}
                                                      </Button>
                                                    )}
                                                  </div>
                                                </td>
                                              )}
                                            </tr>
                                          );
                                        })}
                                        {seats.map((s, si) => (
                                          <tr key={`seat-${si}`} className="bc-seat-row">
                                            <td title="Reserved expected-new-business seat — open until the Clay routing flow ties a real arriving customer to it (then it shows the real account).">{s.name}</td>
                                            <td className="bc-muted">{s.month ? monthKeyLabel(s.month) : 'seat'}</td>
                                            <td className="bc-muted">—</td>
                                            {canEditAssignee && <td />}
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  )}
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <datalist id="bc-final-assignee-list">
                  {sfdcUsers.map((u) => <option key={u.userId} value={u.name} />)}
                </datalist>
              </div>
            );
          })()}
        </div>
      </Card>

      {error && <p className="bc-error">{error}</p>}
      {!role.isAdmin && <p className="bc-muted bc-footnote">Signed in as {role.email}</p>}
    </div>
  );
}

// ────────────────────────────── Targeted adjustments (finalized carves) ──────────────────────────────

type AdjustRole = 'lock' | 'donor' | 'recipient' | 'both';
interface AdjustRosterEntry {
  name: string;
  type: 'rep' | 'pendingBook';
  accounts: number;
  arr: number;
  manager: string | null;
  region: string | null;
  rampDate: string | null;
  assignee: string | null;
  reassignedCount: number;
  pendingCount: number;
}
interface AdjustMove {
  accountId: string;
  accountName: string;
  from: string;
  to: string;
  arr: number;
  wasPending: boolean;
  category: 'New Carve' | 'Rebalance' | 'Removed from Carve';
}
interface AdjustReview {
  id: string;
  status: string;
  warnings: string[];
  engineWarnings: string[];
  moves: AdjustMove[] | null;
  summary: { gs: string; isNewOwner: boolean; totalPost: number; arrPost: number; accountsAdded: number; accountsLost: number }[];
}
interface AdjustNewBook { name: string; targetCount: string; startDate: string; region: string; expectedAccounts: string }
interface AdjustRemoval { book: string; mode: 'redistribute' | 'revert' }

const ROLE_LABELS: Record<AdjustRole, string> = {
  lock: 'Locked',
  donor: 'Donate only',
  recipient: 'Receive only',
  both: 'Donate + receive',
};

/**
 * "Adjust" flow on a finalized book: add or remove individual books via a scoped mini-carve.
 * Pick a group of owners (reps + in-transit books) and a role for each; everyone else is frozen.
 * Run produces a reviewable move list + warnings against a fresh universe; Apply writes a targeted
 * delta (Snowflake rows, history revisions, webhooks) and merges it into this frozen book in place.
 */
function AdjustmentPanel({ carveId, json, postJson, onApplied, onClose }: {
  carveId: string;
  json: <T,>(p: string, i?: RequestInit & { timeoutMs?: number }) => Promise<T>;
  postJson: <T,>(p: string, b: unknown, t?: number) => Promise<T>;
  onApplied: () => void;
  onClose: () => void;
}) {
  const [roster, setRoster] = useState<AdjustRosterEntry[] | null>(null);
  const [enforceRegion, setEnforceRegion] = useState(false);
  const [defaultTarget, setDefaultTarget] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [kind, setKind] = useState<'add' | 'remove' | 'both'>('add');
  // Group membership + per-member role/region. Default: nobody in the group; picking a member
  // seeds ramped reps to donate+receive and unramped in-transit books to Locked.
  const [members, setMembers] = useState<Map<string, { inGroup: boolean; role: AdjustRole; region: string }>>(new Map());
  const [newBooks, setNewBooks] = useState<AdjustNewBook[]>([]);
  const [removals, setRemovals] = useState<AdjustRemoval[]>([]);
  const [cutoverDate, setCutoverDate] = useState('');
  const [pins, setPins] = useState<{ accountId: string; gs: string }[]>([]);

  const [review, setReview] = useState<AdjustReview | null>(null);
  const [running, setRunning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState<{ updatedAccounts: number; reverted: number } | null>(null);
  // Setup edited since the last run → the shown result is stale and Apply is disabled.
  const [dirty, setDirty] = useState(false);
  const touch = () => { setDirty(true); };

  const todayIso = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    let cancelled = false;
    json<{ segment: string; enforceRegion: boolean; targetCount: number | null; roster: AdjustRosterEntry[] }>(
      `/carves/${carveId}/adjust-roster`,
      { timeoutMs: 120_000 },
    )
      .then((d) => {
        if (cancelled) return;
        setRoster(d.roster);
        setEnforceRegion(d.enforceRegion);
        setDefaultTarget(d.targetCount);
        const m = new Map<string, { inGroup: boolean; role: AdjustRole; region: string }>();
        for (const e of d.roster) {
          const unramped = e.type === 'pendingBook' || (e.rampDate != null && e.rampDate > new Date().toISOString().slice(0, 10));
          m.set(e.name, { inGroup: false, role: unramped ? 'lock' : 'both', region: e.region ?? '' });
        }
        setMembers(m);
      })
      .catch((e) => { if (!cancelled) setError(String(e)); });
    return () => { cancelled = true; };
  }, [carveId, json]);

  const setMember = (name: string, patch: Partial<{ inGroup: boolean; role: AdjustRole; region: string }>) => {
    setMembers((prev) => {
      const next = new Map(prev);
      const cur = next.get(name) ?? { inGroup: false, role: 'both' as AdjustRole, region: '' };
      next.set(name, { ...cur, ...patch });
      return next;
    });
    touch();
  };
  const toggleRegionGroup = (region: string) => {
    if (!roster) return;
    const inRegion = roster.filter((e) => (e.region ?? '') === region);
    const allIn = inRegion.every((e) => members.get(e.name)?.inGroup);
    setMembers((prev) => {
      const next = new Map(prev);
      for (const e of inRegion) {
        const cur = next.get(e.name)!;
        next.set(e.name, { ...cur, inGroup: !allIn });
      }
      return next;
    });
    touch();
  };

  const groupNames = useMemo(
    () => (roster ?? []).filter((e) => members.get(e.name)?.inGroup).map((e) => e.name),
    [roster, members],
  );

  const buildSetup = () => {
    const roles: Record<string, AdjustRole> = {};
    const repRegions: Record<string, string> = {};
    for (const name of groupNames) {
      const m = members.get(name)!;
      roles[name] = m.role; // removals override the role server-side (redistribute → donate-all)
      if (m.region) repRegions[name] = m.region;
    }
    return {
      kind,
      regions: [],
      roles,
      newBooks: newBooks.map((b) => ({
        name: b.name.trim(),
        targetCount: Number(b.targetCount) || 0,
        startDate: b.startDate || undefined,
        region: b.region || undefined,
        expectedAccounts: b.expectedAccounts ? Number(b.expectedAccounts) : undefined,
      })),
      removals,
      cutoverDate: cutoverDate || null,
      pins,
      repRegions,
    };
  };

  const run = async () => {
    setRunning(true);
    setError(null);
    try {
      const path = review?.id ? `/adjustments/${review.id}/run` : `/carves/${carveId}/adjustments`;
      const d = await postJson<AdjustReview>(path, { setup: buildSetup() }, 180_000);
      setReview(d);
      setDirty(false);
    } catch (e) {
      setError(String(e));
    } finally {
      setRunning(false);
    }
  };

  const apply = async () => {
    if (!review?.id) return;
    if (!window.confirm('Apply this adjustment? It rewrites the touched accounts in the finalized book and notifies the downstream Clay flow.')) return;
    setApplying(true);
    setError(null);
    try {
      const d = await postJson<{ updatedAccounts: number; reverted: number }>(`/adjustments/${review.id}/apply`, {}, 180_000);
      setApplied({ updatedAccounts: d.updatedAccounts, reverted: d.reverted });
      onApplied();
    } catch (e) {
      setError(String(e));
    } finally {
      setApplying(false);
    }
  };

  const close = () => {
    if (review?.id && review.status === 'draft' && !applied) {
      void postJson(`/adjustments/${review.id}/discard`, {}).catch(() => {});
    }
    onClose();
  };

  // Pin a move's account to a specific group member (server re-run required to take effect).
  const pinMove = (accountId: string, gs: string) => {
    setPins((prev) => [...prev.filter((p) => p.accountId !== accountId), ...(gs ? [{ accountId, gs }] : [])]);
    touch();
  };

  if (applied) {
    return (
      <div className="bc-adjust">
        <div className="bc-row bc-spread">
          <h3 className="bc-h3">Adjustment applied</h3>
          <Button variant="neutral" onClick={onClose}>Close</Button>
        </div>
        <p className="bc-muted">
          {applied.updatedAccounts} account row(s) updated{applied.reverted > 0 ? `, ${applied.reverted} pending move(s) reverted` : ''}.
          The finalized book above reflects the change; the Clay flow was notified.
        </p>
      </div>
    );
  }

  const removalCandidates = (roster ?? []).filter((e) => members.get(e.name)?.inGroup);
  const needsCutover = review?.moves?.some((m) => m.category === 'Rebalance') ?? false;

  return (
    <div className="bc-adjust">
      <div className="bc-row bc-spread">
        <div>
          <h3 className="bc-h3">Adjust finalized book</h3>
          <p className="bc-muted">
            A scoped mini-carve against this finalized book: pick a group, add or remove books, review the
            proposed moves, then apply. Everyone outside the group is frozen.
          </p>
        </div>
        <Button variant="neutral" onClick={close}>Cancel</Button>
      </div>
      {error && <p className="bc-error">{error}</p>}
      {!roster ? (
        <p className="bc-muted">Loading a fresh universe…</p>
      ) : (
        <>
          <div className="bc-row bc-adjust-kind">
            <label className="bc-muted">Adjustment</label>
            <select value={kind} onChange={(e) => { setKind(e.target.value as typeof kind); touch(); }}>
              <option value="add">Add a new book</option>
              <option value="remove">Remove a book</option>
              <option value="both">Add + remove</option>
            </select>
            <span className="bc-muted">Quick group by region:</span>
            {REGIONS.map((r) => (
              <Button key={r} variant="neutral" onClick={() => toggleRegionGroup(r)}>{r}</Button>
            ))}
          </div>

          <div className="bc-table-scroll bc-adjust-roster">
            <table className="bc-table">
              <thead>
                <tr><th>In group</th><th>Owner</th><th>Accounts</th><th>ARR</th><th>Manager</th><th>Region</th><th>Role</th></tr>
              </thead>
              <tbody>
                {roster.map((e) => {
                  const m = members.get(e.name) ?? { inGroup: false, role: 'both' as AdjustRole, region: '' };
                  const unramped = e.rampDate != null && e.rampDate > todayIso;
                  return (
                    <tr key={e.name} className={e.type === 'pendingBook' ? 'bc-final-new' : undefined}>
                      <td>
                        <input
                          type="checkbox"
                          checked={m.inGroup}
                          onChange={(ev) => setMember(e.name, { inGroup: ev.target.checked })}
                        />
                      </td>
                      <td>
                        {e.name}
                        {e.type === 'pendingBook' ? <Badge>in transit</Badge> : null}
                        {unramped && e.type !== 'pendingBook' ? <Badge>unramped</Badge> : null}
                        {e.pendingCount > 0 && (
                          <span className="bc-muted" title={`${e.pendingCount} account(s) pending cutover${e.reassignedCount ? `, ${e.reassignedCount} already reassigned` : ''}`}>
                            {' '}· {e.pendingCount} pending
                          </span>
                        )}
                      </td>
                      <td>{e.accounts}</td>
                      <td>{money(e.arr)}</td>
                      <td className="bc-muted">{e.manager ?? '—'}</td>
                      <td>
                        <select
                          value={m.region}
                          disabled={!m.inGroup}
                          onChange={(ev) => setMember(e.name, { region: ev.target.value })}
                        >
                          <option value="">—</option>
                          {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </td>
                      <td>
                        <select
                          value={m.role}
                          disabled={!m.inGroup || removals.some((r) => r.book === e.name)}
                          onChange={(ev) => setMember(e.name, { role: ev.target.value as AdjustRole })}
                          title={removals.some((r) => r.book === e.name) ? 'Removal overrides the role.' : undefined}
                        >
                          {(Object.keys(ROLE_LABELS) as AdjustRole[]).map((r) => (
                            <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {kind !== 'remove' && (
            <div className="bc-adjust-section">
              <div className="bc-row bc-spread">
                <h4 className="bc-h4">New books</h4>
                <Button
                  variant="neutral"
                  onClick={() => {
                    setNewBooks((prev) => [
                      ...prev,
                      { name: '', targetCount: String(defaultTarget ?? 10), startDate: '', region: '', expectedAccounts: '' },
                    ]);
                    touch();
                  }}
                >
                  + Add book
                </Button>
              </div>
              {newBooks.map((b, i) => (
                <div key={i} className="bc-row bc-adjust-newbook">
                  <Input placeholder="Book name (e.g. EGS Sept 1)" value={b.name}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => { setNewBooks((p) => p.map((x, j) => (j === i ? { ...x, name: e.target.value } : x))); touch(); }} />
                  <Input className="bc-adjust-num" placeholder="Target" value={b.targetCount} title="Target starting accounts"
                    onChange={(e: ChangeEvent<HTMLInputElement>) => { setNewBooks((p) => p.map((x, j) => (j === i ? { ...x, targetCount: e.target.value } : x))); touch(); }} />
                  <input className="bc-mgr-input bc-ramp-input" type="date" value={b.startDate} title="New hire start date (drives the ramp/assignment date)"
                    onChange={(e) => { setNewBooks((p) => p.map((x, j) => (j === i ? { ...x, startDate: e.target.value } : x))); touch(); }} />
                  {enforceRegion && (
                    <select value={b.region} onChange={(e) => { setNewBooks((p) => p.map((x, j) => (j === i ? { ...x, region: e.target.value } : x))); touch(); }}>
                      <option value="">Region…</option>
                      {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  )}
                  <button type="button" className="bc-icon-danger" title="Remove this book row"
                    onClick={() => { setNewBooks((p) => p.filter((_, j) => j !== i)); touch(); }}>🗑</button>
                  {b.startDate && <span className="bc-muted">ramps {rampDateFor(b.startDate) ?? '—'}</span>}
                </div>
              ))}
              {newBooks.length === 0 && <p className="bc-muted">No new books yet.</p>}
            </div>
          )}

          {kind !== 'add' && (
            <div className="bc-adjust-section">
              <div className="bc-row bc-spread">
                <h4 className="bc-h4">Remove books</h4>
                <Button
                  variant="neutral"
                  disabled={removalCandidates.length === 0}
                  title={removalCandidates.length === 0 ? 'Add the book to the group first.' : undefined}
                  onClick={() => { setRemovals((prev) => [...prev, { book: '', mode: 'redistribute' }]); touch(); }}
                >
                  + Remove a book
                </Button>
              </div>
              {removals.map((r, i) => {
                const entry = roster.find((e) => e.name === r.book);
                const canRevert = (entry?.pendingCount ?? 0) > 0;
                return (
                  <div key={i} className="bc-row bc-adjust-removal">
                    <select value={r.book} onChange={(e) => { setRemovals((p) => p.map((x, j) => (j === i ? { ...x, book: e.target.value } : x))); touch(); }}>
                      <option value="">Pick a book…</option>
                      {removalCandidates.map((e) => <option key={e.name} value={e.name}>{e.name}</option>)}
                    </select>
                    <select
                      value={r.mode}
                      onChange={(e) => { setRemovals((p) => p.map((x, j) => (j === i ? { ...x, mode: e.target.value as RemovalModeUi } : x))); touch(); }}
                    >
                      <option value="redistribute">Redistribute to the group</option>
                      <option value="revert" disabled={!canRevert}>
                        Revert pending moves{!canRevert ? ' (nothing pending)' : ''}
                      </option>
                    </select>
                    <button type="button" className="bc-icon-danger" title="Remove this removal row"
                      onClick={() => { setRemovals((p) => p.filter((_, j) => j !== i)); touch(); }}>🗑</button>
                    {entry && entry.reassignedCount > 0 && (
                      <span className="bc-muted">{entry.reassignedCount} account(s) already reassigned in SFDC stay put on revert.</span>
                    )}
                  </div>
                );
              })}
              {removals.length === 0 && <p className="bc-muted">No removals yet.</p>}
            </div>
          )}

          <div className="bc-row bc-adjust-foot">
            <label className="bc-muted" title="Assignment date for moves between existing reps (new books use their ramp date).">
              Cutover date
              <input className="bc-mgr-input bc-ramp-input" type="date" min={todayIso} value={cutoverDate}
                onChange={(e) => { setCutoverDate(e.target.value); touch(); }} />
            </label>
            <Button variant="primary" disabled={running || groupNames.length === 0} onClick={() => { void run(); }}>
              {running ? 'Running…' : review ? 'Re-run' : 'Run adjustment'}
            </Button>
          </div>

          {review && (
            <div className="bc-adjust-review">
              {[...review.warnings, ...review.engineWarnings].map((w, i) => (
                <p key={i} className="bc-warn">⚠ {w}</p>
              ))}
              {needsCutover && !cutoverDate && (
                <p className="bc-warn">⚠ This adjustment moves accounts between existing reps — set a cutover date before applying.</p>
              )}
              <h4 className="bc-h4">Proposed moves ({review.moves?.length ?? 0})</h4>
              {(review.moves ?? []).length === 0 ? (
                <p className="bc-muted">No accounts move.</p>
              ) : (
                <div className="bc-table-scroll bc-adjust-moves">
                  <table className="bc-table">
                    <thead>
                      <tr><th>Account</th><th>From</th><th>To</th><th>ARR</th><th>Type</th><th>Pin</th></tr>
                    </thead>
                    <tbody>
                      {(review.moves ?? []).map((mv) => (
                        <tr key={mv.accountId}>
                          <td>{mv.accountName}{mv.wasPending ? <Badge>re-route</Badge> : null}</td>
                          <td className="bc-muted">{mv.from}</td>
                          <td>{mv.to}</td>
                          <td>{money(mv.arr)}</td>
                          <td><Badge>{mv.category}</Badge></td>
                          <td>
                            <select
                              value={pins.find((p) => p.accountId === mv.accountId)?.gs ?? ''}
                              title="Pin this account to a specific group member and re-run."
                              onChange={(e) => pinMove(mv.accountId, e.target.value)}
                            >
                              <option value="">—</option>
                              {[...groupNames, ...newBooks.map((b) => b.name.trim()).filter(Boolean)].map((g) => (
                                <option key={g} value={g}>{g}</option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <h4 className="bc-h4">Group after adjustment</h4>
              <table className="bc-table">
                <thead>
                  <tr><th>Owner</th><th>Accounts</th><th>ARR</th><th>Added</th><th>Lost</th></tr>
                </thead>
                <tbody>
                  {review.summary.map((g) => (
                    <tr key={g.gs}>
                      <td>{g.gs}{g.isNewOwner ? <Badge>new</Badge> : null}</td>
                      <td>{g.totalPost}</td>
                      <td>{money(g.arrPost)}</td>
                      <td>{g.accountsAdded || '—'}</td>
                      <td>{g.accountsLost || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="bc-row bc-adjust-apply">
                {dirty && <span className="bc-muted">Setup changed — re-run before applying.</span>}
                <Button
                  variant="primary"
                  disabled={applying || dirty || (needsCutover && !cutoverDate)}
                  onClick={() => { void apply(); }}
                >
                  {applying ? 'Applying…' : 'Apply adjustment'}
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
type RemovalModeUi = AdjustRemoval['mode'];

// ────────────────────────────── Global must-keep (homepage) ──────────────────────────────

const segmentOfTeam = (team: 'egs' | 'sgs'): 'ENT' | 'STRAT' => (team === 'sgs' ? 'STRAT' : 'ENT');

/**
 * Marks an account that lives in the OTHER segment's universe. Cross-segment must-keep pins are
 * legitimate (the run resolves out-of-universe pins by ID), but they are unusual enough that the
 * operator must never create one without seeing it.
 */
function CrossSegmentBadge({ segment }: { segment: 'ENT' | 'STRAT' }) {
  return (
    <span
      className={`bc-seg-badge bc-seg-${segment === 'STRAT' ? 'sgs' : 'egs'}`}
      title={`Cross-segment: this account is in the ${segment} universe, not this tab's`}
    >
      {segment}
    </span>
  );
}

/**
 * Homepage manager for the team-scoped global must-keep list (account → rep pins applied to every
 * new carve). Full CRUD against /must-keep, shown as a sortable Account/Rep/Manager table with
 * manager + rep filters. Reps are fetched once per team when the card opens (they supply the
 * Manager column and the add flow's rep dropdown); the account pool stays lazy behind "+ Add
 * account" so the homepage stays light. An open "New carve" form keeps its own copy of the list
 * and refetches whenever it is opened or its team changes.
 */
function MustKeepCard({ json, postJson, apiFetchRaw }: {
  json: <T,>(p: string, i?: RequestInit & { timeoutMs?: number }) => Promise<T>;
  postJson: <T,>(p: string, b: unknown, t?: number) => Promise<T>;
  /** Raw fetch for the CSV export, which streams a file rather than JSON. */
  apiFetchRaw: ReturnType<typeof useApi>['apiFetchRaw'];
}) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'egs' | 'sgs'>('egs');
  const [entries, setEntries] = useState<MustKeepEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [loadingPool, setLoadingPool] = useState(false);
  const [reps, setReps] = useState<Rep[]>([]);
  const [accounts, setAccounts] = useState<UniverseAccount[]>([]);
  const [target, setTarget] = useState('');
  const [search, setSearch] = useState('');
  const [pending, setPending] = useState<UniverseAccount | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [sortKey, setSortKey] = useState<'account' | 'gs' | 'manager'>('account');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [mgrFilter, setMgrFilter] = useState('all');
  const [gsFilter, setGsFilter] = useState('all');
  const repPools = useRef<Partial<Record<'egs' | 'sgs', Rep[]>>>({});
  const acctPools = useRef<Partial<Record<'egs' | 'sgs', UniverseAccount[]>>>({});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setAdding(false);
    setTarget('');
    setSearch('');
    setPending(null);
    setSortKey('account');
    setSortDir('asc');
    setMgrFilter('all');
    setGsFilter('all');
    json<{ entries: MustKeepEntry[] }>(`/must-keep?team=${tab}`)
      .then((d) => { if (!cancelled) setEntries(d.entries); })
      .catch((e) => { if (!cancelled) setError(String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tab, json]);

  // Reps load as soon as the card is open: they populate the Manager column + filters (and the
  // add flow's rep dropdown). Fails soft — managers just show "—".
  useEffect(() => {
    if (!open) return;
    const cached = repPools.current[tab];
    if (cached) {
      setReps(cached);
      return;
    }
    let cancelled = false;
    json<{ reps: Rep[] }>(`/universe?team=${tab}`, { timeoutMs: 120_000 })
      .then((u) => {
        repPools.current[tab] = u.reps;
        if (!cancelled) setReps(u.reps);
      })
      .catch(() => { if (!cancelled) setReps([]); });
    return () => { cancelled = true; };
  }, [open, tab, json]);

  // The pool is deliberately CROSS-SEGMENT: the tab still decides which team's list the row is
  // written to and which reps the "Keep with…" dropdown offers, but an operator must be able to
  // pin an account from either universe (e.g. keep the ENT account Patreon with a Strat rep).
  const openAdd = async () => {
    setAdding(true);
    const cached = acctPools.current[tab];
    if (cached) {
      setAccounts(cached);
      return;
    }
    setLoadingPool(true);
    try {
      const a = await json<{ accounts: UniverseAccount[] }>(`/universe/accounts?team=${tab}&includeOtherSegment=1`, { timeoutMs: 120_000 });
      acctPools.current[tab] = a.accounts;
      setAccounts(a.accounts);
    } catch (e) {
      setError(String(e));
      setAdding(false);
    } finally {
      setLoadingPool(false);
    }
  };

  const managerOf = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of reps) if (r.manager) m.set(r.name, r.manager);
    return m;
  }, [reps]);

  // Filter options come from the entries themselves (only managers/reps that actually appear).
  const mgrOptions = useMemo(
    () => [...new Set(entries.map((e) => managerOf.get(e.gs)).filter((m): m is string => !!m))].sort((a, b) => a.localeCompare(b)),
    [entries, managerOf],
  );
  const gsOptions = useMemo(
    () => [...new Set(entries.map((e) => e.gs))]
      .filter((gs) => mgrFilter === 'all' || managerOf.get(gs) === mgrFilter)
      .sort((a, b) => a.localeCompare(b)),
    [entries, mgrFilter, managerOf],
  );

  const visibleEntries = useMemo(() => {
    const filtered = entries.filter((e) =>
      (mgrFilter === 'all' || managerOf.get(e.gs) === mgrFilter) && (gsFilter === 'all' || e.gs === gsFilter));
    const val = (e: MustKeepEntry) =>
      sortKey === 'gs' ? e.gs : sortKey === 'manager' ? (managerOf.get(e.gs) ?? '') : e.accountName;
    return [...filtered].sort((a, b) => {
      const cmp = val(a).localeCompare(val(b)) || a.accountName.localeCompare(b.accountName);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [entries, mgrFilter, gsFilter, sortKey, sortDir, managerOf]);

  const onSort = (key: 'account' | 'gs' | 'manager') => {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };
  const sortArrow = (key: string) => (key === sortKey ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');
  const pickMgrFilter = (v: string) => {
    setMgrFilter(v);
    if (v !== 'all' && gsFilter !== 'all' && managerOf.get(gsFilter) !== v) setGsFilter('all');
  };

  const pinnedIds = new Set(entries.map((e) => e.accountId));
  // The pool spans both segments, and the server orders this tab's segment first, so the top-8
  // cut still favours same-segment hits.
  const matches = adding && search.trim()
    ? accounts.filter((a) => !pinnedIds.has(a.accountId) && a.accountName.toLowerCase().includes(search.trim().toLowerCase())).slice(0, 8)
    : [];
  const crossSegmentOf = (a: UniverseAccount) => (a.segment && a.segment !== segmentOfTeam(tab) ? a.segment : null);
  const pendingCrossSegment = pending ? crossSegmentOf(pending) : null;

  const addEntry = async (a: UniverseAccount, gs: string) => {
    try {
      await postJson('/must-keep', { team: tab, accountId: a.accountId, accountName: a.accountName, gs });
      setEntries((es) => [...es.filter((e) => e.accountId !== a.accountId), { accountId: a.accountId, accountName: a.accountName, gs }]
        .sort((x, y) => x.accountName.localeCompare(y.accountName)));
      setSearch('');
      setPending(null);
    } catch (e) {
      setError(String(e));
    }
  };

  // Either order works: rep-first saves on account click; account-first stages the account and
  // saves when a rep is chosen.
  const pickAccount = (a: UniverseAccount) => {
    if (target) {
      void addEntry(a, target);
    } else {
      setPending(a);
      setSearch('');
    }
  };
  const pickRep = (gs: string) => {
    setTarget(gs);
    if (gs && pending) {
      void addEntry(pending, gs);
      setTarget('');
    }
  };

  const removeEntry = async (accountId: string) => {
    setRemoving(accountId);
    try {
      await json('/must-keep', { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ team: tab, accountId }) });
      setEntries((es) => es.filter((e) => e.accountId !== accountId));
    } catch (e) {
      setError(String(e));
    } finally {
      setRemoving(null);
    }
  };

  /**
   * Download the current tab's must-keep list as CSV. Respects the visible sort + active
   * manager/rep filters so an operator can slice → export (e.g. "just Suchita's reps' pins"),
   * which is why the filter/sort state travels as query params.
   *
   * Server-side rather than built from `visibleEntries`: the export carries the same account-detail
   * columns as the carve export (ARR, expansion, renewal, reassignment history, family), and this
   * card only holds account/rep/manager. The account pool that would fill the rest is lazy-loaded
   * behind "+ Add account" and is a reduced projection even then, so the join belongs on the server.
   * Can take a while on a cold universe — hence the generous timeout and the pending label.
   */
  const exportCsv = async () => {
    if (!visibleEntries.length) return;
    setExporting(true);
    setError(null);
    try {
      const params = new URLSearchParams({ team: tab, sort: sortKey, dir: sortDir });
      if (gsFilter !== 'all') params.set('gs', gsFilter);
      if (mgrFilter !== 'all') params.set('manager', mgrFilter);
      const res = await apiFetchRaw(`${API}/must-keep/export?${params.toString()}`, { timeoutMs: 120_000 });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const filterSuffix = mgrFilter !== 'all' ? `-${mgrFilter.replace(/[^A-Za-z0-9]+/g, '_')}`
        : gsFilter !== 'all' ? `-${gsFilter.replace(/[^A-Za-z0-9]+/g, '_')}`
          : '';
      link.href = url;
      link.download = `must-keep-${tab}${filterSuffix}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(String(e));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card className="bc-card-clay" noPadding>
      <div className="bc-card-pad-tight">
        <div className="bc-row bc-spread">
          <h2 className="bc-h2">
            <button type="button" className="bc-gs-toggle" onClick={() => setOpen((o) => !o)}>
              <span className="bc-caret bc-caret-lg">{open ? '▾' : '▸'}</span>
              Global Must Keep{loading ? '' : ` (${entries.length})`}
            </button>
          </h2>
          {open && (
            <div className="bc-row">
              <div className="bc-tabs">
                <button className={`bc-tab${tab === 'egs' ? ' bc-tab-on' : ''}`} onClick={() => setTab('egs')}>EGS</button>
                <button className={`bc-tab${tab === 'sgs' ? ' bc-tab-on' : ''}`} onClick={() => setTab('sgs')}>Strat</button>
              </div>
              <Button
                variant="ghost"
                disabled={loading || exporting || visibleEntries.length === 0}
                onClick={() => { void exportCsv(); }}
                title={
                  visibleEntries.length === 0
                    ? 'No entries to export'
                    : visibleEntries.length !== entries.length
                      ? `Export ${visibleEntries.length} filtered entries as CSV, with full account detail`
                      : `Export all ${entries.length} entries as CSV, with full account detail`
                }
              >
                {exporting ? 'Exporting…' : 'Export CSV'}
              </Button>
            </div>
          )}
        </div>
        {open && (
          <>
            <p className="bc-muted">Saved account → rep relationships applied automatically to every new carve.</p>
            {loading ? (
              <p className="bc-muted">Loading…</p>
            ) : entries.length === 0 ? (
              <EmptyState title="No must-keep accounts" description="Add one below, or save a pin from a carve's setup form." />
            ) : (
              <>
                <div className="bc-row bc-mustkeep-filters">
                  <select value={mgrFilter} onChange={(e) => pickMgrFilter(e.target.value)} title="Filter by manager">
                    <option value="all">All managers</option>
                    {mgrOptions.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <select value={gsFilter} onChange={(e) => setGsFilter(e.target.value)} title="Filter by rep">
                    <option value="all">All reps</option>
                    {gsOptions.map((gs) => <option key={gs} value={gs}>{gs}</option>)}
                  </select>
                  {visibleEntries.length !== entries.length && (
                    <span className="bc-muted">{visibleEntries.length} of {entries.length} shown</span>
                  )}
                </div>
                <div className="bc-table-scroll">
                  <table className="bc-table bc-table-sortable bc-mustkeep-table">
                    <thead>
                      <tr>
                        <th onClick={() => onSort('account')}>Account{sortArrow('account')}</th>
                        <th onClick={() => onSort('gs')}>Rep{sortArrow('gs')}</th>
                        <th onClick={() => onSort('manager')}>Manager{sortArrow('manager')}</th>
                        <th className="bc-mustkeep-action" aria-label="Actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {visibleEntries.map((e) => (
                        <tr key={e.accountId}>
                          <td>{e.accountName}</td>
                          <td>{e.gs}</td>
                          <td>{managerOf.get(e.gs) ?? '—'}</td>
                          <td className="bc-mustkeep-action">
                            <button
                              type="button"
                              className="bc-link bc-link-danger"
                              disabled={removing === e.accountId}
                              onClick={() => { void removeEntry(e.accountId); }}
                            >
                              {removing === e.accountId ? 'Removing…' : 'Remove'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            <div className="bc-mustkeep">
              {!adding ? (
                <Button variant="neutral" onClick={() => { void openAdd(); }}>+ Add account</Button>
              ) : loadingPool ? (
                <p className="bc-muted">Loading reps and accounts…</p>
              ) : (
                <>
                  <div className="bc-row">
                    <select value={target} onChange={(e) => pickRep(e.target.value)} title={pending ? `Keep ${pending.accountName} with` : 'Keep account with'}>
                      <option value="">Keep with…</option>
                      {[...reps].sort((a, b) => a.name.localeCompare(b.name)).map((r) => <option key={r.name} value={r.name}>{r.name}</option>)}
                    </select>
                    <Input placeholder="Search account by name…" value={search} onChange={(e) => setSearch(e.target.value)} />
                    <Button variant="ghost" onClick={() => { setAdding(false); setTarget(''); setSearch(''); setPending(null); }}>Cancel</Button>
                  </div>
                  {pending && (
                    <>
                      <div className="bc-pin-chips">
                        <span className="bc-chip">
                          {pendingCrossSegment && <CrossSegmentBadge segment={pendingCrossSegment} />}
                          {pending.accountName} → choose a rep…
                          <button type="button" onClick={() => setPending(null)} aria-label="Clear selected account">×</button>
                        </span>
                      </div>
                      {pendingCrossSegment && (
                        <p className="bc-notice">
                          Cross-segment pin: {pending.accountName} is {pendingCrossSegment === 'ENT' ? 'an' : 'a'} {pendingCrossSegment} account,
                          and will be kept with the {tab === 'sgs' ? 'Strat' : 'EGS'} rep you choose.
                        </p>
                      )}
                    </>
                  )}
                  {matches.length > 0 && (
                    <div className="bc-pin-results">
                      {matches.map((a) => {
                        const cross = crossSegmentOf(a);
                        return (
                          <button key={a.accountId} type="button" className="bc-pin-result" onClick={() => pickAccount(a)}>
                            <span>
                              {cross && <CrossSegmentBadge segment={cross} />}
                              {a.accountName}
                            </span>
                            <span className="bc-muted">{a.currentGs} · {money(a.arr)}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
            {error && <p className="bc-error">{error}</p>}
          </>
        )}
      </div>
    </Card>
  );
}

// ────────────────────────────── Must Move (homepage) ──────────────────────────────

/**
 * Homepage card for the global must-MOVE list (bookcarve_must_move). Semantically the inverse
 * of Must-Keep: whereas Must-Keep pins an account to a specific current rep, Must-Move forbids
 * the current rep from being the recipient at all — the optimizer places the account with any
 * other eligible rep. Applied automatically to every new carve via mergeLiveMustMoves. Any user
 * with access can edit; adding/removing an entry invalidates the deterministic run cache for
 * the affected segment (via runConfigHash `-mm…` suffix).
 *
 * Mirrors the MustKeepCard UI patterns (tabbed EGS/SGS, universe-backed account search,
 * filter + sort table, CSV export) — kept as its own component rather than a shared template
 * because the semantics differ (no target rep, optional reason field, one-column table).
 */
function MustMoveCard({ json, postJson }: {
  json: <T,>(p: string, i?: RequestInit & { timeoutMs?: number }) => Promise<T>;
  postJson: <T,>(p: string, b: unknown, t?: number) => Promise<T>;
}) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'egs' | 'sgs'>('egs');
  const [entries, setEntries] = useState<MustMoveEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [loadingPool, setLoadingPool] = useState(false);
  const [accounts, setAccounts] = useState<UniverseAccount[]>([]);
  const [search, setSearch] = useState('');
  const [reasonInput, setReasonInput] = useState('');
  const [pending, setPending] = useState<UniverseAccount | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const acctPools = useRef<Partial<Record<'egs' | 'sgs', UniverseAccount[]>>>({});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setAdding(false);
    setSearch('');
    setReasonInput('');
    setPending(null);
    setSortDir('asc');
    json<{ entries: MustMoveEntry[] }>(`/must-move?team=${tab}`)
      .then((d) => { if (!cancelled) setEntries(d.entries); })
      .catch((e) => { if (!cancelled) setError(String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tab, json]);

  const openAdd = async () => {
    setAdding(true);
    const cached = acctPools.current[tab];
    if (cached) {
      setAccounts(cached);
      return;
    }
    setLoadingPool(true);
    try {
      const a = await json<{ accounts: UniverseAccount[] }>(`/universe/accounts?team=${tab}`, { timeoutMs: 120_000 });
      acctPools.current[tab] = a.accounts;
      setAccounts(a.accounts);
    } catch (e) {
      setError(String(e));
      setAdding(false);
    } finally {
      setLoadingPool(false);
    }
  };

  const visibleEntries = useMemo(() => {
    return [...entries].sort((a, b) => {
      const cmp = a.accountName.localeCompare(b.accountName);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [entries, sortDir]);

  const pinnedIds = new Set(entries.map((e) => e.accountId));
  const matches = adding && search.trim()
    ? accounts.filter((a) => !pinnedIds.has(a.accountId) && a.accountName.toLowerCase().includes(search.trim().toLowerCase())).slice(0, 8)
    : [];

  const addEntry = async (a: UniverseAccount) => {
    try {
      const reason = reasonInput.trim() || null;
      await postJson('/must-move', { team: tab, accountId: a.accountId, accountName: a.accountName, reason });
      setEntries((es) => [...es.filter((e) => e.accountId !== a.accountId), { accountId: a.accountId, accountName: a.accountName, reason }]
        .sort((x, y) => x.accountName.localeCompare(y.accountName)));
      setSearch('');
      setReasonInput('');
      setPending(null);
    } catch (e) {
      setError(String(e));
    }
  };

  const pickAccount = (a: UniverseAccount) => {
    setPending(a);
    setSearch('');
  };
  const confirmAdd = () => {
    if (pending) void addEntry(pending);
  };

  const removeEntry = async (accountId: string) => {
    setRemoving(accountId);
    try {
      await json('/must-move', { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ team: tab, accountId }) });
      setEntries((es) => es.filter((e) => e.accountId !== accountId));
    } catch (e) {
      setError(String(e));
    } finally {
      setRemoving(null);
    }
  };

  const exportCsv = () => {
    if (!visibleEntries.length) return;
    const escape = (v: string): string => {
      const s = v ?? '';
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = [['Account', 'Account ID', 'Reason']];
    for (const e of visibleEntries) rows.push([e.accountName, e.accountId, e.reason ?? '']);
    const csv = rows.map((r) => r.map(escape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const dateIso = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `must-move-${tab}-${dateIso}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Card className="bc-card-clay" noPadding>
      <div className="bc-card-pad-tight">
        <div className="bc-row bc-spread">
          <h2 className="bc-h2">
            <button type="button" className="bc-gs-toggle" onClick={() => setOpen((o) => !o)}>
              <span className="bc-caret bc-caret-lg">{open ? '▾' : '▸'}</span>
              Global Must Move{loading ? '' : ` (${entries.length})`}
            </button>
          </h2>
          {open && (
            <div className="bc-row">
              <div className="bc-tabs">
                <button className={`bc-tab${tab === 'egs' ? ' bc-tab-on' : ''}`} onClick={() => setTab('egs')}>EGS</button>
                <button className={`bc-tab${tab === 'sgs' ? ' bc-tab-on' : ''}`} onClick={() => setTab('sgs')}>Strat</button>
              </div>
              <Button
                variant="ghost"
                disabled={loading || visibleEntries.length === 0}
                onClick={exportCsv}
                title={visibleEntries.length === 0 ? 'No entries to export' : `Export ${visibleEntries.length} entries as CSV`}
              >
                Export CSV
              </Button>
            </div>
          )}
        </div>
        {open && (
          <>
            <p className="bc-muted">
              Accounts listed here MUST be reassigned off their current GS on every carve — bypasses freezes and lock windows.
              A manual pin back to the current GS still wins (with a loud bypass warning).
            </p>
            {loading ? (
              <p className="bc-muted">Loading…</p>
            ) : entries.length === 0 ? (
              <EmptyState title="No must-move accounts" description="Add one below to force it off its current GS on every carve." />
            ) : (
              <div className="bc-table-scroll">
                <table className="bc-table bc-table-sortable bc-mustkeep-table">
                  <thead>
                    <tr>
                      <th onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}>
                        Account{sortDir === 'asc' ? ' ▲' : ' ▼'}
                      </th>
                      <th>Reason</th>
                      <th className="bc-mustkeep-action" aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {visibleEntries.map((e) => (
                      <tr key={e.accountId}>
                        <td>{e.accountName}</td>
                        <td className="bc-muted">{e.reason ?? '—'}</td>
                        <td className="bc-mustkeep-action">
                          <button
                            type="button"
                            className="bc-link bc-link-danger"
                            disabled={removing === e.accountId}
                            onClick={() => { void removeEntry(e.accountId); }}
                          >
                            {removing === e.accountId ? 'Removing…' : 'Remove'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="bc-mustkeep">
              {!adding ? (
                <Button variant="neutral" onClick={() => { void openAdd(); }}>+ Add account</Button>
              ) : loadingPool ? (
                <p className="bc-muted">Loading accounts…</p>
              ) : (
                <>
                  <div className="bc-row">
                    <Input placeholder="Search account by name…" value={search} onChange={(e) => setSearch(e.target.value)} />
                    <Input placeholder="Reason (optional)" value={reasonInput} onChange={(e) => setReasonInput(e.target.value)} />
                    <Button variant="ghost" onClick={() => { setAdding(false); setSearch(''); setReasonInput(''); setPending(null); }}>Cancel</Button>
                  </div>
                  {pending && (
                    <div className="bc-pin-chips">
                      <span className="bc-chip">
                        {pending.accountName}
                        <button type="button" onClick={() => setPending(null)} aria-label="Clear selected account">×</button>
                      </span>
                      <Button variant="primary" onClick={confirmAdd}>Add to Must-Move</Button>
                    </div>
                  )}
                  {!pending && matches.length > 0 && (
                    <div className="bc-pin-results">
                      {matches.map((a) => (
                        <button key={a.accountId} type="button" className="bc-pin-result" onClick={() => pickAccount(a)}>
                          <span>{a.accountName}</span>
                          <span className="bc-muted">{a.currentGs} · {money(a.arr)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            {error && <p className="bc-error">{error}</p>}
          </>
        )}
      </div>
    </Card>
  );
}

// ────────────────────────────── Manager Overrides (homepage) ──────────────────────────────

interface ManagerOverrideEntry {
  gsName: string;
  managerName: string;
  setByEmail: string | null;
  setAt: string;
}

/**
 * Homepage card for the global manager-override list (bookcarve_manager_override). Corrects the
 * SFDC-derived manager when Salesforce is stale — Rachel Wang → Suchita Donti is the canonical
 * case (SFDC still says Gabby Borenstein). Applied inside buildFreshUniverse so this list
 * automatically flows into the Must-Keep card's Manager column, the optimizer, LLM adjust, etc.
 * Any user with access can edit; every write invalidates the universe cache so the change is
 * visible on the next refresh.
 */
function ManagerOverridesCard({ json, postJson }: {
  json: <T,>(p: string, i?: RequestInit & { timeoutMs?: number }) => Promise<T>;
  postJson: <T,>(p: string, b: unknown, t?: number) => Promise<T>;
}) {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState<ManagerOverrideEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [gsName, setGsName] = useState('');
  const [managerName, setManagerName] = useState('');
  const [reps, setReps] = useState<Rep[]>([]);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchEntries = useCallback(() => {
    setLoading(true);
    setError(null);
    return json<{ entries: ManagerOverrideEntry[] }>('/manager-overrides')
      .then((d) => setEntries(d.entries))
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [json]);

  useEffect(() => {
    if (open) void fetchEntries();
  }, [open, fetchEntries]);

  // Roster union across EGS+SGS drives both dropdowns (any GS can be re-managed to any other
  // GS/manager in the org). Fails soft — dropdowns fall back to a free-text input.
  useEffect(() => {
    if (!open || reps.length > 0) return;
    let cancelled = false;
    Promise.all([
      json<{ reps: Rep[] }>('/universe?team=egs', { timeoutMs: 120_000 }).catch(() => ({ reps: [] as Rep[] })),
      json<{ reps: Rep[] }>('/universe?team=sgs', { timeoutMs: 120_000 }).catch(() => ({ reps: [] as Rep[] })),
    ]).then(([a, b]) => {
      if (cancelled) return;
      const byName = new Map<string, Rep>();
      for (const r of [...a.reps, ...b.reps]) if (!byName.has(r.name)) byName.set(r.name, r);
      setReps([...byName.values()].sort((x, y) => x.name.localeCompare(y.name)));
    });
    return () => { cancelled = true; };
  }, [open, reps.length, json]);

  const managerChoices = useMemo(() => {
    // A "manager" is anyone appearing as a manager on any rep OR anyone flagged isManager on the roster.
    const s = new Set<string>();
    for (const r of reps) {
      if (r.manager) s.add(r.manager);
      if (r.isManager) s.add(r.name);
    }
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [reps]);

  const overriddenByGs = useMemo(() => new Map(entries.map((e) => [e.gsName, e])), [entries]);

  const canAdd = gsName.trim() !== '' && managerName.trim() !== '' && gsName.trim() !== managerName.trim();

  const add = async () => {
    if (!canAdd) return;
    setSaving(true);
    setError(null);
    try {
      await postJson('/manager-overrides', { gsName: gsName.trim(), managerName: managerName.trim() });
      setGsName('');
      setManagerName('');
      setAdding(false);
      await fetchEntries();
    } catch (e) {
      setError(String(e));
    } finally {
      setSaving(false);
    }
  };

  const removeEntry = async (name: string) => {
    setRemoving(name);
    try {
      await json('/manager-overrides', { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ gsName: name }) });
      setEntries((es) => es.filter((e) => e.gsName !== name));
    } catch (e) {
      setError(String(e));
    } finally {
      setRemoving(null);
    }
  };

  return (
    <Card className="bc-card-clay" noPadding>
      <div className="bc-card-pad-tight">
        <div className="bc-row bc-spread">
          <h2 className="bc-h2">
            <button type="button" className="bc-gs-toggle" onClick={() => setOpen((o) => !o)}>
              <span className="bc-caret bc-caret-lg">{open ? '▾' : '▸'}</span>
              Manager Overrides{loading ? '' : ` (${entries.length})`}
            </button>
          </h2>
        </div>
        {open && (
          <>
            <p className="bc-muted">
              Correct a rep&apos;s manager when Salesforce is out of date. Applied to every carve on the
              next universe refresh — new carves pick it up immediately, existing carves after refresh.
            </p>
            {loading ? (
              <p className="bc-muted">Loading…</p>
            ) : entries.length === 0 ? (
              <EmptyState title="No manager overrides" description="Add one below if a rep's SFDC manager is wrong." />
            ) : (
              <div className="bc-table-scroll">
                <table className="bc-table bc-table-sortable bc-mustkeep-table">
                  <thead>
                    <tr>
                      <th>Rep</th>
                      <th>Manager (override)</th>
                      <th>Set by</th>
                      <th className="bc-mustkeep-action" aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e) => (
                      <tr key={e.gsName}>
                        <td>{e.gsName}</td>
                        <td>{e.managerName}</td>
                        <td className="bc-muted">{e.setByEmail ?? '—'}</td>
                        <td className="bc-mustkeep-action">
                          <button
                            type="button"
                            className="bc-link bc-link-danger"
                            disabled={removing === e.gsName}
                            onClick={() => { void removeEntry(e.gsName); }}
                          >
                            {removing === e.gsName ? 'Removing…' : 'Remove'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="bc-mustkeep">
              {!adding ? (
                <Button variant="neutral" onClick={() => setAdding(true)}>+ Add override</Button>
              ) : (
                <>
                  <div className="bc-row">
                    <select value={gsName} onChange={(e) => setGsName(e.target.value)} title="Rep to reassign">
                      <option value="">Rep…</option>
                      {reps.map((r) => (
                        <option key={r.name} value={r.name}>
                          {r.name}{overriddenByGs.has(r.name) ? ' (has override)' : ''}
                        </option>
                      ))}
                    </select>
                    <select value={managerName} onChange={(e) => setManagerName(e.target.value)} title="New manager">
                      <option value="">New manager…</option>
                      {managerChoices.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                    <Button variant="primary" onClick={() => { void add(); }} disabled={!canAdd || saving}>
                      {saving ? 'Saving…' : 'Save'}
                    </Button>
                    <Button variant="ghost" onClick={() => { setAdding(false); setGsName(''); setManagerName(''); }}>Cancel</Button>
                  </div>
                  {gsName && overriddenByGs.has(gsName) && (
                    <p className="bc-muted">
                      {gsName} is currently overridden to <strong>{overriddenByGs.get(gsName)?.managerName}</strong>. Saving replaces that.
                    </p>
                  )}
                </>
              )}
            </div>
            {error && <p className="bc-error">{error}</p>}
          </>
        )}
      </div>
    </Card>
  );
}

// ────────────────────────────── Rep Settings (homepage) ──────────────────────────────

interface RepSettingEntry {
  gsName: string;
  mode: 'freeze' | 'velocity_book' | 'velocity_only';
  notes: string | null;
  setByEmail: string | null;
  setAt: string;
}

const REP_SETTING_MODES: { value: RepSettingEntry['mode']; label: string; description: string }[] = [
  {
    value: 'freeze',
    label: 'Freeze',
    description: 'Rep keeps existing accounts. Nothing else moves in or out except explicit pins (must-keep or per-run).',
  },
  {
    value: 'velocity_book',
    label: 'Velocity book',
    description: 'Rep is injected as a synthetic new-owner book with size = pinned-account count. Freezes the book too so only pinned accounts land there. Use for reps outside the segment roster (e.g. Scale reps holding Velocity-segment accounts pinned into an ENT carve).',
  },
  {
    value: 'velocity_only',
    label: 'Pinned accounts only (empty the rest)',
    description: 'Rep keeps ONLY their pinned accounts — every other account on their book is donated back to the segment. Use for a rep who has left the segment but still holds a pinned book. Freeze would strand those other accounts, because a frozen book never donates.',
  },
];

/**
 * Homepage card for the persistent per-rep behavior overrides list (bookcarve_rep_setting).
 * Applied on EVERY carve run (via mergeLiveRepSettings in api.ts), so operators can add or
 * change a setting without hand-editing draft configs. Team-scoped (EGS/SGS). Every write
 * invalidates the universe cache so the change takes effect immediately.
 */
function RepSettingsCard({ json, postJson }: {
  json: <T,>(p: string, i?: RequestInit & { timeoutMs?: number }) => Promise<T>;
  postJson: <T,>(p: string, b: unknown, t?: number) => Promise<T>;
}) {
  const [open, setOpen] = useState(false);
  const [team, setTeam] = useState<'egs' | 'sgs'>('egs');
  const [entries, setEntries] = useState<RepSettingEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [gsName, setGsName] = useState('');
  const [mode, setMode] = useState<RepSettingEntry['mode']>('freeze');
  const [notes, setNotes] = useState('');
  const [reps, setReps] = useState<Rep[]>([]);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchEntries = useCallback(() => {
    setLoading(true);
    setError(null);
    return json<{ entries: RepSettingEntry[] }>(`/rep-settings?team=${team}`)
      .then((d) => setEntries(d.entries))
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [json, team]);

  useEffect(() => {
    if (open) void fetchEntries();
  }, [open, fetchEntries]);

  // Roster for the picker. Union across both teams because velocity_book by definition may
  // point at a rep who isn't in the current segment's SFDC roster (Maya = Scale rep pinned
  // into ENT). Fails soft — an admin can also free-text the rep name.
  useEffect(() => {
    if (!open || reps.length > 0) return;
    let cancelled = false;
    Promise.all([
      json<{ reps: Rep[] }>('/universe?team=egs', { timeoutMs: 120_000 }).catch(() => ({ reps: [] as Rep[] })),
      json<{ reps: Rep[] }>('/universe?team=sgs', { timeoutMs: 120_000 }).catch(() => ({ reps: [] as Rep[] })),
    ]).then(([a, b]) => {
      if (cancelled) return;
      const byName = new Map<string, Rep>();
      for (const r of [...a.reps, ...b.reps]) if (!byName.has(r.name)) byName.set(r.name, r);
      setReps([...byName.values()].sort((x, y) => x.name.localeCompare(y.name)));
    });
    return () => { cancelled = true; };
  }, [open, reps.length, json]);

  const canAdd = gsName.trim() !== '';

  const add = async () => {
    if (!canAdd) return;
    setSaving(true);
    setError(null);
    try {
      await postJson('/rep-settings', { team, gsName: gsName.trim(), mode, notes: notes.trim() || undefined });
      setGsName('');
      setNotes('');
      setMode('freeze');
      setAdding(false);
      await fetchEntries();
    } catch (e) {
      setError(String(e));
    } finally {
      setSaving(false);
    }
  };

  const removeEntry = async (name: string) => {
    setRemoving(name);
    try {
      await json('/rep-settings', { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ team, gsName: name }) });
      setEntries((es) => es.filter((e) => e.gsName !== name));
    } catch (e) {
      setError(String(e));
    } finally {
      setRemoving(null);
    }
  };

  return (
    <Card className="bc-card-clay" noPadding>
      <div className="bc-card-pad-tight">
        <div className="bc-row bc-spread">
          <h2 className="bc-h2">
            <button type="button" className="bc-gs-toggle" onClick={() => setOpen((o) => !o)}>
              <span className="bc-caret bc-caret-lg">{open ? '▾' : '▸'}</span>
              Rep Settings{loading ? '' : ` (${entries.length})`}
            </button>
          </h2>
          {open && (
            <div className="bc-row">
              <select value={team} onChange={(e) => setTeam(e.target.value as 'egs' | 'sgs')}>
                <option value="egs">Enterprise (EGS)</option>
                <option value="sgs">Strategic (SGS)</option>
              </select>
            </div>
          )}
        </div>
        {open && (
          <>
            <p className="bc-muted">
              Persistent per-rep behavior applied on every future carve. Use <strong>Freeze</strong> to lock a rep&apos;s book (they keep what they have, no in/out except explicit pins) and <strong>Velocity book</strong> to add a rep outside the segment roster as a pin destination.
            </p>
            {loading ? (
              <p className="bc-muted">Loading…</p>
            ) : entries.length === 0 ? (
              <EmptyState title="No rep settings" description="Add one below to freeze a rep or set up a velocity book." />
            ) : (
              <div className="bc-table-scroll">
                <table className="bc-table bc-table-sortable bc-mustkeep-table">
                  <thead>
                    <tr>
                      <th>Rep</th>
                      <th>Mode</th>
                      <th>Notes</th>
                      <th>Set by</th>
                      <th className="bc-mustkeep-action" aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e) => (
                      <tr key={e.gsName}>
                        <td>{e.gsName}</td>
                        <td>{REP_SETTING_MODES.find((m) => m.value === e.mode)?.label ?? e.mode}</td>
                        <td className="bc-muted">{e.notes ?? ''}</td>
                        <td className="bc-muted">{e.setByEmail ?? '—'}</td>
                        <td className="bc-mustkeep-action">
                          <button
                            type="button"
                            className="bc-link bc-link-danger"
                            disabled={removing === e.gsName}
                            onClick={() => { void removeEntry(e.gsName); }}
                          >
                            {removing === e.gsName ? 'Removing…' : 'Remove'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="bc-mustkeep">
              {!adding ? (
                <Button variant="neutral" onClick={() => setAdding(true)}>+ Add rep setting</Button>
              ) : (
                <>
                  <div className="bc-row">
                    <select value={gsName} onChange={(e) => setGsName(e.target.value)} title="Rep">
                      <option value="">Rep…</option>
                      {reps.map((r) => (
                        <option key={r.name} value={r.name}>{r.name}</option>
                      ))}
                    </select>
                    <select value={mode} onChange={(e) => setMode(e.target.value as RepSettingEntry['mode'])} title="Mode">
                      {REP_SETTING_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                    <input type="text" placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
                    <Button variant="primary" onClick={() => { void add(); }} disabled={!canAdd || saving}>
                      {saving ? 'Saving…' : 'Save'}
                    </Button>
                    <Button variant="ghost" onClick={() => { setAdding(false); setGsName(''); setMode('freeze'); setNotes(''); }}>Cancel</Button>
                  </div>
                  <p className="bc-muted">{REP_SETTING_MODES.find((m) => m.value === mode)?.description ?? ''}</p>
                </>
              )}
            </div>
            {error && <p className="bc-error">{error}</p>}
          </>
        )}
      </div>
    </Card>
  );
}

// ────────────────────────────── Account Exclusions (homepage) ──────────────────────────────

interface AccountExclusionEntry {
  accountId: string;
  accountName: string;
  reason: string;
  excludedByEmail: string | null;
  excludedAt: string;
}

/**
 * Homepage card for the global account-exclusion list (bookcarve_account_exclusion). Accounts
 * on this list are stripped from the universe regardless of contract status — used for churned
 * accounts SFDC hasn't updated (Compass) or any other data-quality carve-out. Replaces the
 * previously-hard-coded AppOmni VALUES clause in enrichmentSql (AppOmni is seeded on migration).
 * Every write invalidates the universe cache so the exclusion takes effect on the next refresh.
 */
function AccountExclusionsCard({ json, postJson }: {
  json: <T,>(p: string, i?: RequestInit & { timeoutMs?: number }) => Promise<T>;
  postJson: <T,>(p: string, b: unknown, t?: number) => Promise<T>;
}) {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState<AccountExclusionEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [accountId, setAccountId] = useState('');
  const [accountName, setAccountName] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Universe pool across BOTH segments — lets operators pick from a searchable list of accounts
  // that ARE currently in the universe (the common case for a "just churned" exclusion). Loaded
  // lazily on +Add so the homepage stays light; fails soft — the raw-ID inputs still work.
  const [pool, setPool] = useState<UniverseAccount[]>([]);
  const [search, setSearch] = useState('');

  const fetchEntries = useCallback(() => {
    setLoading(true);
    setError(null);
    return json<{ entries: AccountExclusionEntry[] }>('/account-exclusions')
      .then((d) => setEntries(d.entries))
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [json]);

  useEffect(() => {
    if (open) void fetchEntries();
  }, [open, fetchEntries]);

  const openAdd = async () => {
    setAdding(true);
    if (pool.length > 0) return;
    try {
      const [a, b] = await Promise.all([
        json<{ accounts: UniverseAccount[] }>('/universe/accounts?team=egs', { timeoutMs: 120_000 }).catch(() => ({ accounts: [] as UniverseAccount[] })),
        json<{ accounts: UniverseAccount[] }>('/universe/accounts?team=sgs', { timeoutMs: 120_000 }).catch(() => ({ accounts: [] as UniverseAccount[] })),
      ]);
      const seen = new Set<string>();
      const merged: UniverseAccount[] = [];
      for (const acc of [...a.accounts, ...b.accounts]) {
        if (seen.has(acc.accountId)) continue;
        seen.add(acc.accountId);
        merged.push(acc);
      }
      setPool(merged);
    } catch {
      /* fall back to raw-ID entry */
    }
  };

  const excludedIds = new Set(entries.map((e) => e.accountId));
  const matches = adding && search.trim()
    ? pool.filter((a) => !excludedIds.has(a.accountId) && a.accountName.toLowerCase().includes(search.trim().toLowerCase())).slice(0, 8)
    : [];

  const pickFromPool = (a: UniverseAccount) => {
    setAccountId(a.accountId);
    setAccountName(a.accountName);
    setSearch('');
  };

  const canAdd = /^[A-Za-z0-9]{15,18}$/.test(accountId.trim()) && accountName.trim() !== '' && reason.trim() !== '';

  const add = async () => {
    if (!canAdd) return;
    setSaving(true);
    setError(null);
    try {
      await postJson('/account-exclusions', { accountId: accountId.trim(), accountName: accountName.trim(), reason: reason.trim() });
      setAccountId('');
      setAccountName('');
      setReason('');
      setSearch('');
      setAdding(false);
      await fetchEntries();
    } catch (e) {
      setError(String(e));
    } finally {
      setSaving(false);
    }
  };

  const removeEntry = async (id: string) => {
    setRemoving(id);
    try {
      await json('/account-exclusions', { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ accountId: id }) });
      setEntries((es) => es.filter((e) => e.accountId !== id));
    } catch (e) {
      setError(String(e));
    } finally {
      setRemoving(null);
    }
  };

  return (
    <Card className="bc-card-clay" noPadding>
      <div className="bc-card-pad-tight">
        <div className="bc-row bc-spread">
          <h2 className="bc-h2">
            <button type="button" className="bc-gs-toggle" onClick={() => setOpen((o) => !o)}>
              <span className="bc-caret bc-caret-lg">{open ? '▾' : '▸'}</span>
              Excluded Accounts{loading ? '' : ` (${entries.length})`}
            </button>
          </h2>
        </div>
        {open && (
          <>
            <p className="bc-muted">
              Accounts stripped from the universe regardless of contract status — use for churned accounts
              SFDC hasn&apos;t updated (e.g. Compass), or any account that should never appear in Book Carve.
            </p>
            {loading ? (
              <p className="bc-muted">Loading…</p>
            ) : entries.length === 0 ? (
              <EmptyState title="No excluded accounts" description="Add one below to strip a stale-churn or off-limits account from every carve." />
            ) : (
              <div className="bc-table-scroll">
                <table className="bc-table bc-table-sortable bc-mustkeep-table">
                  <thead>
                    <tr>
                      <th>Account</th>
                      <th>Reason</th>
                      <th>Excluded by</th>
                      <th className="bc-mustkeep-action" aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e) => (
                      <tr key={e.accountId}>
                        <td>{e.accountName} <span className="bc-muted">({e.accountId})</span></td>
                        <td>{e.reason}</td>
                        <td className="bc-muted">{e.excludedByEmail ?? '—'}</td>
                        <td className="bc-mustkeep-action">
                          <button
                            type="button"
                            className="bc-link bc-link-danger"
                            disabled={removing === e.accountId}
                            onClick={() => { void removeEntry(e.accountId); }}
                          >
                            {removing === e.accountId ? 'Removing…' : 'Remove'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="bc-mustkeep">
              {!adding ? (
                <Button variant="neutral" onClick={() => { void openAdd(); }}>+ Add exclusion</Button>
              ) : (
                <>
                  <div className="bc-row">
                    <Input
                      placeholder="Search universe (or paste SFDC ID below)…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                    <Button variant="ghost" onClick={() => { setAdding(false); setAccountId(''); setAccountName(''); setReason(''); setSearch(''); }}>Cancel</Button>
                  </div>
                  {matches.length > 0 && (
                    <div className="bc-pin-results">
                      {matches.map((a) => (
                        <button key={a.accountId} type="button" className="bc-pin-result" onClick={() => pickFromPool(a)}>
                          <span>{a.accountName}</span>
                          <span className="bc-muted">{a.currentGs} · {money(a.arr)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="bc-row">
                    <Input placeholder="SFDC account ID (001…)" value={accountId} onChange={(e) => setAccountId(e.target.value)} />
                    <Input placeholder="Account name" value={accountName} onChange={(e) => setAccountName(e.target.value)} />
                  </div>
                  <div className="bc-row">
                    <Input placeholder="Reason (e.g. churned, test account, partner)" value={reason} onChange={(e) => setReason(e.target.value)} />
                    <Button variant="primary" onClick={() => { void add(); }} disabled={!canAdd || saving}>
                      {saving ? 'Saving…' : 'Save'}
                    </Button>
                  </div>
                </>
              )}
            </div>
            {error && <p className="bc-error">{error}</p>}
          </>
        )}
      </div>
    </Card>
  );
}

// ────────────────────────────── Segment Moves (homepage) ──────────────────────────────

interface AccountOverrideEntry {
  accountId: string;
  accountName: string | null;
  targetSegment: 'ENT' | 'STRAT' | null;
  region: string | null;
  createdByEmail: string | null;
  createdAt: string;
}

/**
 * Shared, carve-independent account overrides. A row can declare a segment transfer (the account
 * now belongs to ENT/STRAT — every run of the target segment pulls it in, every run of the other
 * segment drops it) and/or a manual region pin. Region pins are HARD constraints on the optimizer
 * (even with Region Lock off — the account only lands on a matching-region book or rep). Applied at
 * run time by every carve, so both segments always agree with no per-carve setup.
 */
function SegmentMovesCard({ json, postJson }: {
  json: <T,>(p: string, i?: RequestInit & { timeoutMs?: number }) => Promise<T>;
  postJson: <T,>(p: string, b: unknown, t?: number) => Promise<T>;
}) {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState<AccountOverrideEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [targetSegment, setTargetSegment] = useState('');
  const [region, setRegion] = useState('');
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Account names across BOTH universes (cosmetic — pasted IDs outside either show as raw IDs).
  const namePool = useRef<Map<string, string> | null>(null);

  const fetchEntries = useCallback(() => {
    setLoading(true);
    setError(null);
    return json<{ entries: AccountOverrideEntry[] }>('/account-overrides')
      .then((d) => setEntries(d.entries))
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [json]);

  useEffect(() => {
    if (open) void fetchEntries();
  }, [open, fetchEntries]);

  const loadNames = async (): Promise<Map<string, string>> => {
    if (namePool.current) return namePool.current;
    const m = new Map<string, string>();
    for (const team of ['egs', 'sgs'] as const) {
      try {
        const d = await json<{ accounts: UniverseAccount[] }>(`/universe/accounts?team=${team}`, { timeoutMs: 20000 });
        for (const a of d.accounts) m.set(a.accountId, a.accountName);
      } catch {
        /* names are cosmetic; fall back to raw IDs */
      }
    }
    namePool.current = m;
    return m;
  };

  const ids = extractAccountIds(pasteText);
  const canAdd = ids.length > 0 && (targetSegment !== '' || region !== '');
  const add = async () => {
    if (!canAdd) return;
    setSaving(true);
    setError(null);
    try {
      const names = await loadNames();
      await postJson('/account-overrides', {
        entries: ids.map((accountId) => ({
          accountId,
          accountName: names.get(accountId) ?? null,
          targetSegment: targetSegment || null,
          region: region || null,
        })),
      });
      setPasteText('');
      setTargetSegment('');
      setRegion('');
      setAdding(false);
      await fetchEntries();
    } catch (e) {
      setError(String(e));
    } finally {
      setSaving(false);
    }
  };

  const removeEntry = async (accountId: string) => {
    setRemoving(accountId);
    try {
      await json('/account-overrides', { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ accountId }) });
      setEntries((es) => es.filter((e) => e.accountId !== accountId));
    } catch (e) {
      setError(String(e));
    } finally {
      setRemoving(null);
    }
  };

  return (
    <Card className="bc-card-clay" noPadding>
      <div className="bc-card-pad-tight">
        <div className="bc-row bc-spread">
          <h2 className="bc-h2">
            <button type="button" className="bc-gs-toggle" onClick={() => setOpen((o) => !o)}>
              <span className="bc-caret bc-caret-lg">{open ? '▾' : '▸'}</span>
              Segment + Region Sorting{loading ? '' : entries.length ? ` (${entries.length})` : ''}
            </button>
          </h2>
        </div>
        {open && (
          <>
            <p className="bc-muted">
              Accounts moving between ENT and Strat, plus manual region pins. Every carve run applies this list
              automatically: the target segment's runs pull the account in, the other segment's runs drop it, and a
              region pin is binding — even with Region Lock off, the account only lands on a matching-region book
              or rep, superseding freezes, must-keep pins, holds, and the movement cap (each override is called
              out in the run's warnings).
            </p>
            {loading ? (
              <p className="bc-muted">Loading…</p>
            ) : entries.length === 0 ? (
              <EmptyState title="No segment or region pins" description="Paste account IDs below and pick the segment they're moving to and/or a region pin." />
            ) : (
              <div className="bc-table-scroll">
                <table className="bc-table bc-mustkeep-table">
                  <thead>
                    <tr>
                      <th>Account</th>
                      <th>Moves to</th>
                      <th>Region</th>
                      <th>Added</th>
                      <th className="bc-mustkeep-action" aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e) => (
                      <tr key={e.accountId}>
                        <td>{e.accountName ?? e.accountId}</td>
                        <td>{e.targetSegment === 'STRAT' ? 'Strat' : e.targetSegment === 'ENT' ? 'ENT' : '—'}</td>
                        <td>{e.region ?? '—'}</td>
                        <td className="bc-muted">{e.createdByEmail ? `${e.createdByEmail.split('@')[0]} · ` : ''}{timeAgo(e.createdAt)}</td>
                        <td className="bc-mustkeep-action">
                          <button
                            type="button"
                            className="bc-link bc-link-danger"
                            disabled={removing === e.accountId}
                            onClick={() => { void removeEntry(e.accountId); }}
                          >
                            {removing === e.accountId ? 'Removing…' : 'Remove'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="bc-mustkeep">
              {!adding ? (
                <Button variant="neutral" onClick={() => { setAdding(true); void loadNames(); }}>+ Add accounts</Button>
              ) : (
                <>
                  <Textarea
                    expandable={false}
                    rows={2}
                    placeholder="Paste account IDs (or Name + SFDC Account ID rows) — 18-char IDs are extracted automatically"
                    value={pasteText}
                    onChange={(e) => setPasteText(e.target.value)}
                  />
                  <div className="bc-row">
                    <select value={targetSegment} onChange={(e) => setTargetSegment(e.target.value)} title="Segment these accounts are moving to (leave blank for a region-only pin)">
                      <option value="">Moves to…</option>
                      <option value="ENT">ENT</option>
                      <option value="STRAT">Strat</option>
                    </select>
                    <select value={region} onChange={(e) => setRegion(e.target.value)} title="Region pin (optional). Binding on every carve, even with Region Lock off: the account only lands on a matching-region book or rep.">
                      <option value="">Region…</option>
                      {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <span className="bc-muted">{ids.length} account{ids.length === 1 ? '' : 's'} detected</span>
                    <Button variant="primary" disabled={!canAdd || saving} onClick={() => { void add(); }}>
                      {saving ? 'Adding…' : 'Add'}
                    </Button>
                    <Button variant="ghost" onClick={() => { setAdding(false); setPasteText(''); setTargetSegment(''); setRegion(''); }}>Cancel</Button>
                  </div>
                </>
              )}
            </div>
            {error && <p className="bc-error">{error}</p>}
          </>
        )}
      </div>
    </Card>
  );
}

// ────────────────────────────── Expected Handoffs (homepage) ──────────────────────────────

interface HandoffMonth {
  month: string;
  arrivals: number;
  repsOver: { name: string; count: number; over: number; donateQuarters: string[] }[];
  books: { name: string; fill: number; target: number; status: 'pending' | 'ramped'; receiveQuarters: string[] }[];
  handoffs: { from: string; to: string; quarter: string | null; count: number }[];
  totals: { overage: number; slots: number; handoffs: number; carry: number };
}
interface HandoffForecast {
  team: 'egs' | 'sgs';
  carveTitle?: string | null;
  bias?: number;
  note?: string;
  /** Near-ramp risk (homepage only): finalized books ramping within ~30 days that still hold
   * open expected-new-business seat placeholders with no real arrival tied. */
  alerts?: string[];
  /** Informational context (homepage only): reps holding accounts already carved away with a
   * future cutover date, whose live SFDC counts run high until the cutover passes. */
  notes?: string[];
  months: HandoffMonth[];
}

/**
 * Shared Expected Handoffs body (homepage card + carve page toggle): a 12-month totals table
 * (new logos / donatable overage / open slots / executed handoffs / carry) followed by per-month
 * snapshots — reps over target with the quarters they should donate from, books still able to
 * receive with the quarters they need, and the donor→book handoffs the simulation executed.
 */
function HandoffForecastBody({ fc, loading, error }: {
  fc: HandoffForecast | null | undefined;
  loading: boolean;
  error: string | null;
}) {
  if (loading) return <p className="bc-muted">Loading…</p>;
  if (error) return <p className="bc-error">{error}</p>;
  if (!fc) return null;
  if (fc.note) return <p className="bc-muted">{fc.note}</p>;
  const alerts = fc.alerts ?? [];
  const notes = fc.notes ?? [];
  const active = fc.months.filter((m) => m.repsOver.length > 0 || m.handoffs.length > 0);
  if (!active.length && !alerts.length && !notes.length) {
    return <EmptyState title="No handoffs projected" description="No existing rep goes over their target inside the next 12 months on the current schedule." />;
  }
  return (
    <>
      {alerts.map((a, i) => (
        <p key={i} className="bc-error">{a}</p>
      ))}
      {notes.map((n, i) => (
        <p key={`n${i}`} className="bc-muted">{n}</p>
      ))}
      <div className="bc-table-scroll">
        <table className="bc-table bc-handoff-table">
          <thead>
            <tr>
              <th>Month</th>
              <th title="Forecast new accounts arriving this month (segment-wide)">New logos</th>
              <th title="Accounts held above target by supplying reps — the donatable pool">Overage</th>
              <th title="Open capacity across new books (pending: to at-ramp target; ramped: to full target)">Open slots</th>
              <th title="Handoffs the simulation executed this month">Handoffs</th>
              <th title="Overage left holding after handoffs — carries into next month">Carry</th>
            </tr>
          </thead>
          <tbody>
            {fc.months.map((m) => (
              <tr key={m.month} className={m.totals.handoffs > 0 ? 'bc-handoff-active' : ''}>
                <td>{monthKeyLabel(m.month)}</td>
                <td className="bc-muted">{m.arrivals}</td>
                <td>{m.totals.overage}</td>
                <td>{m.totals.slots}</td>
                <td>{m.totals.handoffs > 0 ? <strong>{m.totals.handoffs}</strong> : <span className="bc-muted">0</span>}</td>
                <td>{m.totals.carry > 0 ? m.totals.carry : <span className="bc-muted">0</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {active.map((m) => (
        <div key={m.month} className="bc-handoff-month">
          <div className="bc-label">{monthKeyLabel(m.month)}</div>
          {m.repsOver.length > 0 && (
            <div className="bc-handoff-group">
              <span className="bc-muted">Reps over target:</span>
              <ul>
                {m.repsOver.map((r) => (
                  <li key={r.name}>
                    {r.name} ({r.count} <strong>+{r.over}</strong>)
                    {r.donateQuarters.length > 0 && <span className="bc-muted"> — ideally donates from {r.donateQuarters.join(' or ')}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {m.books.length > 0 && (
            <div className="bc-handoff-group">
              <span className="bc-muted">Books eligible to receive:</span>
              <ul>
                {m.books.map((b) => (
                  <li key={b.name}>
                    {b.name}: {b.fill}/{b.target} <span className="bc-muted">({b.status}{b.receiveQuarters.length ? ` — ideally receives ${b.receiveQuarters.join(' or ')}` : ''})</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {m.handoffs.length > 0 && (
            <div className="bc-handoff-group">
              <span className="bc-muted">Handoffs executed:</span>
              <ul>
                {m.handoffs.map((h, i) => (
                  <li key={i}>
                    {h.from} → <strong>{h.to}</strong>{h.count > 1 ? ` ×${h.count}` : ''}{h.quarter && <span className="bc-muted"> ({h.quarter})</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ))}
    </>
  );
}

/**
 * Collapsed-by-default monthly ops plan of overflow handoffs on the homepage, simulated from the
 * active finalized carve plus the Forecasting schedule. See HandoffForecastBody for the layout.
 */
function ExpectedHandoffsCard({ json }: {
  json: <T,>(p: string, i?: RequestInit & { timeoutMs?: number }) => Promise<T>;
}) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'egs' | 'sgs'>('egs');
  const [data, setData] = useState<Partial<Record<'egs' | 'sgs', HandoffForecast>>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || data[tab]) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    json<HandoffForecast>(`/handoff-forecast?team=${tab}`, { timeoutMs: 30000 })
      .then((d) => { if (!cancelled) setData((p) => ({ ...p, [tab]: d })); })
      .catch((e) => { if (!cancelled) setError(String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, tab, data, json]);

  return (
    <Card className="bc-card-clay" noPadding>
      <div className="bc-card-pad-tight">
        <div className="bc-row bc-spread">
          <h2 className="bc-h2">
            <button type="button" className="bc-gs-toggle" onClick={() => setOpen((o) => !o)}>
              <span className="bc-caret bc-caret-lg">{open ? '▾' : '▸'}</span>
              Expected Handoffs
            </button>
          </h2>
          {open && (
            <div className="bc-tabs">
              <button className={`bc-tab${tab === 'egs' ? ' bc-tab-on' : ''}`} onClick={() => setTab('egs')}>EGS</button>
              <button className={`bc-tab${tab === 'sgs' ? ' bc-tab-on' : ''}`} onClick={() => setTab('sgs')}>Strat</button>
            </div>
          )}
        </div>
        {open && (
          <>
            <p className="bc-muted">
              Once an existing rep is above the fully-ramped target, each new logo they catch means one tenured
              account handed off to a new book — chosen from their heaviest renewal quarter, routed to the book
              that needs that quarter most. Projected from the active finalized carve + the Forecasting schedule.
            </p>
            <HandoffForecastBody fc={data[tab]} loading={loading} error={error} />
          </>
        )}
      </div>
    </Card>
  );
}

/**
 * Carve-page Expected Handoffs toggle, shown above the Book Summary once a run exists: the same
 * monthly ops view as the homepage card, but simulated on THIS carve's displayed run — so the
 * handoff plan can be sanity-checked before finalizing.
 */
function CarveHandoffToggle({ carveId, runId, json }: {
  carveId: string;
  runId: string | null;
  json: <T,>(p: string, i?: RequestInit & { timeoutMs?: number }) => Promise<T>;
}) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<Record<string, HandoffForecast>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const key = runId ?? 'latest';

  useEffect(() => {
    if (!open || data[key]) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    json<HandoffForecast>(`/carves/${carveId}/handoff-forecast${runId ? `?runId=${runId}` : ''}`, { timeoutMs: 30000 })
      .then((d) => { if (!cancelled) setData((p) => ({ ...p, [key]: d })); })
      .catch((e) => { if (!cancelled) setError(String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, key, carveId, runId, data, json]);

  return (
    <Card className="bc-card-clay" noPadding>
      <div className="bc-card-pad-tight">
        <h2 className="bc-h2">
          <button type="button" className="bc-gs-toggle" onClick={() => setOpen((o) => !o)}>
            <span className="bc-caret bc-caret-lg">{open ? '▾' : '▸'}</span>
            Expected Handoffs
          </button>
        </h2>
        {open && (
          <>
            <p className="bc-muted">
              Monthly ops plan for this run: which existing reps go over the fully-ramped target, the tenured
              accounts they hand off (heaviest renewal quarter first), and the new books that receive them —
              using the team's Forecasting schedule.
            </p>
            <HandoffForecastBody fc={data[key]} loading={loading} error={error} />
          </>
        )}
      </div>
    </Card>
  );
}

// ────────────────────────────── Projection settings (homepage) ──────────────────────────────

/**
 * Homepage editor for the team-scoped projection settings: the per-calendar-month, per-region
 * schedule of expected NEW accounts for the whole segment (Total row spreads evenly across the
 * regions; region rows are individually editable), the new-book bias weighting, and the
 * fully-ramped Target Accounts that seeds new-carve settings. Settings are owned by Home (fetched
 * once for both teams) so the Finalized Books fill charts update live on save.
 */
const BIAS_TOOLTIP =
  'Ramped below-target books get this many shares of each month\'s new logos vs 1 for a full book (so not a true round-robin).';
const TARGET_TOOLTIP =
  'Fully-ramped book size. Seeds the Target accounts / rep default when starting a new carve, and reps above it hand off one tenured account per new logo they catch.';

const ZERO_AMOUNTS: RegionAmounts = { 'US-W': 0, 'US-E': 0, EMEA: 0 };

function ProjectionSettingsCard({ postJson, settings, onSaved, rampedNewBooks }: {
  postJson: <T,>(p: string, b: unknown, t?: number) => Promise<T>;
  settings: Partial<Record<'egs' | 'sgs', ProjectionSettings>>;
  onSaved: (team: 'egs' | 'sgs', s: ProjectionSettings) => void;
  /** New books already past their ramp date per team; joins repCount in the per-rep readout denominator. */
  rampedNewBooks: Record<'egs' | 'sgs', number>;
}) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'egs' | 'sgs'>('egs');
  const [draft, setDraft] = useState<ProjectionSettings | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-seed the draft from the saved settings when switching tabs (or when the fetch lands and the
  // editor hasn't been touched). An empty schedule starts at the current month.
  const saved = settings[tab];
  useEffect(() => {
    if (dirty) return;
    setDraft(
      saved && saved.months.length > 0
        ? { months: saved.months.map((m) => ({ month: m.month, amounts: { ...m.amounts } })), newBookBias: saved.newBookBias, targetAccounts: saved.targetAccounts ?? null }
        : { months: [{ month: monthKeyUTC(Date.now()), amounts: { ...ZERO_AMOUNTS } }], newBookBias: saved?.newBookBias ?? 2, targetAccounts: saved?.targetAccounts ?? null },
    );
    setError(null);
  }, [tab, saved, dirty]);
  const switchTab = (t: 'egs' | 'sgs') => {
    setTab(t);
    setDirty(false);
  };

  const edit = (fn: (d: ProjectionSettings) => ProjectionSettings) => {
    setDraft((d) => (d ? fn(d) : d));
    setDirty(true);
  };
  // Editing a region updates just that cell (the Total re-derives as the sum); editing the Total
  // re-spreads it evenly across the three regions.
  const setRegionAmount = (i: number, region: ForecastRegion, amount: number) =>
    edit((d) => ({ ...d, months: d.months.map((m, idx) => (idx === i ? { ...m, amounts: { ...m.amounts, [region]: amount } } : m)) }));
  const setMonthTotal = (i: number, total: number) =>
    edit((d) => ({ ...d, months: d.months.map((m, idx) => (idx === i ? { ...m, amounts: spreadTotal(total) } : m)) }));
  const addMonth = () =>
    edit((d) => {
      const last = d.months[d.months.length - 1];
      return { ...d, months: [...d.months, { month: nextMonthKey(last.month), amounts: { ...last.amounts } }] };
    });
  const removeMonth = () => edit((d) => (d.months.length > 1 ? { ...d, months: d.months.slice(0, -1) } : d));

  // Derived per-rep view of finance's total: current month's total x 3 / (existing reps + ramped
  // new books). repCount comes from the segment's latest carve snapshot, so it's "—" pre-carve.
  const repCount = saved?.repCount ?? null;
  const perRepDenom = (repCount ?? 0) + rampedNewBooks[tab];
  const perRepQuarter = draft && repCount !== null && perRepDenom > 0
    ? (scheduleTotalAt(draft.months, Date.now()) * 3) / perRepDenom
    : null;

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    try {
      await postJson('/projection-settings', {
        team: tab,
        months: draft.months,
        newBookBias: draft.newBookBias,
        targetAccounts: draft.targetAccounts ?? null,
      });
      onSaved(tab, {
        months: draft.months.map((m) => ({ month: m.month, amounts: { ...m.amounts } })),
        newBookBias: draft.newBookBias,
        targetAccounts: draft.targetAccounts ?? null,
      });
      setDirty(false);
    } catch (e) {
      setError(String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bc-card-clay" noPadding>
      <div className="bc-card-pad-tight">
        <div className="bc-row bc-spread">
          <h2 className="bc-h2">
            <button type="button" className="bc-gs-toggle" onClick={() => setOpen((o) => !o)}>
              <span className="bc-caret bc-caret-lg">{open ? '▾' : '▸'}</span>
              Forecasting
            </button>
          </h2>
          {open && (
            <div className="bc-tabs">
              <button className={`bc-tab${tab === 'egs' ? ' bc-tab-on' : ''}`} onClick={() => switchTab('egs')}>EGS</button>
              <button className={`bc-tab${tab === 'sgs' ? ' bc-tab-on' : ''}`} onClick={() => switchTab('sgs')}>Strat</button>
            </div>
          )}
        </div>
        {open && draft && (
          <>
            <p className="bc-muted">
              Expected new accounts per month and region for the whole {tab === 'sgs' ? 'Strat' : 'EGS'} segment
              (finance's forecast). Edit the Total row to spread evenly across regions, or edit a region directly.
              Fill charts and full-book estimates split each month across active books and never project more than
              this forecast.
            </p>
            <div className="bc-row bc-proj-months-head">
              <span className="bc-field-label">Monthly new accounts</span>
              <div className="bc-proj-month-actions">
                <button type="button" className="bc-proj-month-btn" onClick={addMonth} title="Add the next calendar month">+</button>
                <button type="button" className="bc-proj-month-btn" onClick={removeMonth} disabled={draft.months.length <= 1} title="Remove the last month">−</button>
              </div>
            </div>
            <div className="bc-proj-grid" style={{ gridTemplateColumns: `72px repeat(${draft.months.length}, minmax(64px, 88px))` }}>
              <span className="bc-proj-grid-corner" />
              {draft.months.map((m) => (
                <span key={`h-${m.month}`} className="bc-field-label bc-proj-grid-month">{monthKeyLabel(m.month)}</span>
              ))}
              <span className="bc-field-label bc-proj-grid-rowlabel">Total</span>
              {draft.months.map((m, i) => (
                <input
                  key={`t-${m.month}`}
                  className="bc-num bc-proj-total"
                  type="number"
                  min={0}
                  value={totalAmounts(m.amounts)}
                  onChange={(e) => setMonthTotal(i, Math.max(0, Number(e.target.value) || 0))}
                  title={`Segment total for ${monthKeyLabel(m.month)} — editing spreads it evenly across US-W / US-E / EMEA. The last month carries forward past the end of the schedule.`}
                />
              ))}
              {FORECAST_REGIONS.map((region) => (
                <Fragment key={region}>
                  <span className="bc-field-label bc-proj-grid-rowlabel">{region}</span>
                  {draft.months.map((m, i) => (
                    <input
                      key={`${region}-${m.month}`}
                      className="bc-num"
                      type="number"
                      min={0}
                      value={m.amounts[region]}
                      onChange={(e) => setRegionAmount(i, region, Math.max(0, Number(e.target.value) || 0))}
                      title={`Expected new ${region} accounts in ${monthKeyLabel(m.month)}.`}
                    />
                  ))}
                </Fragment>
              ))}
            </div>
            <div className="bc-row bc-proj-foot">
              <label className="bc-field" title={BIAS_TOOLTIP}>
                <span className="bc-field-label">New-Hire Fill Bias</span>
                <input
                  className="bc-num"
                  type="number"
                  min={0.5}
                  step={0.5}
                  value={draft.newBookBias}
                  onChange={(e) => edit((d) => ({ ...d, newBookBias: Math.max(0.5, Number(e.target.value) || 0.5) }))}
                  title={BIAS_TOOLTIP}
                />
                <span className="bc-muted bc-proj-bias-def" title="Current month's total x 3, split across existing reps + ramped new books.">
                  {perRepQuarter !== null
                    ? `~${perRepQuarter.toFixed(1)} accts/rep/quarter`
                    : '— accts/rep/quarter (needs a carve)'}
                </span>
              </label>
              <label className="bc-field" title={TARGET_TOOLTIP}>
                <span className="bc-field-label">Target Accounts</span>
                <input
                  className="bc-num"
                  type="number"
                  min={1}
                  value={draft.targetAccounts ?? ''}
                  placeholder="—"
                  onChange={(e) => {
                    const v = Math.round(Number(e.target.value));
                    edit((d) => ({ ...d, targetAccounts: Number.isFinite(v) && v > 0 ? v : null }));
                  }}
                />
              </label>
              <Button variant="primary" className="bc-proj-save" disabled={saving || !dirty} onClick={() => { void save(); }}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
            {error && <p className="bc-error">{error}</p>}
          </>
        )}
      </div>
    </Card>
  );
}

// ────────────────────────────── New carve setup ──────────────────────────────

const REP_ACTIONS: { value: RepAction; label: string }[] = [
  { value: 'rebalance', label: 'Rebalance' },
  { value: 'keep', label: 'Keep Book (freeze)' },
  { value: 'keepsilent', label: 'Keep Book (freeze, no handoffs)' },
  { value: 'empty', label: 'Empty Book (reassign all)' },
  { value: 'nonew', label: 'No New Accounts' },
];

/**
 * Scope-aware action options. In New-Hire scope existing reps are never rebalanced against each
 * other — they only donate — so "Rebalance" reads as "Donor" and "No New Accounts" is moot (no rep
 * receives), so it's hidden.
 */
function repActionsForScope(scope: CarveConfig['scope']): { value: RepAction; label: string }[] {
  if (scope === 'newhire') {
    return [
      { value: 'rebalance', label: 'Donor (default)' },
      { value: 'keep', label: 'Keep Book (no donate)' },
      { value: 'keepsilent', label: 'Keep Book (no donate, no handoffs)' },
      { value: 'empty', label: 'Empty Book (donate all)' },
    ];
  }
  return REP_ACTIONS;
}

/** Extract 18-char Salesforce account IDs from pasted text (handles Name<tab>ID tables). */
function extractAccountIds(text: string): string[] {
  const matches = text.match(/001[A-Za-z0-9]{15}/g) ?? [];
  return Array.from(new Set(matches));
}

const SCOPE_OPTIONS: { value: CarveConfig['scope']; label: string }[] = [
  { value: 'newhire', label: 'New hire' },
  { value: 'rebalance', label: 'Rebalance' },
  { value: 'combined', label: 'Combined' },
];
const LOCK_DEFAULTS: Record<'onboardingWithinDays' | 'renewingWithinDays' | 'ownedRecentlyDays', number> = {
  onboardingWithinDays: 90,
  renewingWithinDays: 90,
  ownedRecentlyDays: 180,
};

/** Interactive view of the v2 base rules — edits flow into the carve config on create. */
/** Compact editor for the configurable expansion-score model (weights, bands, query-backed fields). */
// Built-in expansion components — never deletable, and carry an example query.
// Built-in expansion components grouped for the editor layout (variables 2-per-row, then bonuses, penalties).
const EXPANSION_VARIABLE_KEYS = ['seatHeadroom', 'funding', 'sentiment', 'pacing', 'execEngagement', 'empGrowthYoy'];
const EXPANSION_BONUS_KEYS = ['amendmentBonus', 'fundingEvent'];
const EXPANSION_PENALTY_KEYS = ['peBacked', 'recentCw'];
const STOCK_EXPANSION_KEYS = new Set([
  ...EXPANSION_VARIABLE_KEYS,
  ...EXPANSION_BONUS_KEYS,
  ...EXPANSION_PENALTY_KEYS,
]);

// Standalone read-only example queries (return account_id + numeric value) showing how the
// built-in scores are computed. Percentile-rank components end in PERCENT_RANK(); bucket/flag
// components return a fixed value per account.
const EXAMPLE_SQL: Record<string, string> = {
  seatHeadroom: `WITH aw AS (
  SELECT w.account_c::string AS account_id, w.workspace_id_c::number AS workspace_id
  FROM fivetran.salesforce.workspace_c w
  WHERE w.is_deleted = FALSE AND w.account_c IS NOT NULL AND w.workspace_id_c IS NOT NULL
),
active_l90 AS (
  SELECT aw.account_id, COUNT(DISTINCT haw.attributed_user_id) AS active_users
  FROM aw JOIN dbt.prod.hourly_action_worker haw ON haw.workspace_id = aw.workspace_id
  WHERE haw.hour_invoked_utc >= DATEADD('day', -90, CURRENT_TIMESTAMP())
    AND haw.attributed_user_id NOT IN ('no user id','unknown') AND haw.credit_cost > 0
  GROUP BY aw.account_id
),
ratios AS (
  SELECT a.id::string AS account_id,
    CASE WHEN a.number_of_employees IS NULL OR COALESCE(al.active_users,0)=0 THEN NULL
      ELSE a.number_of_employees::float / al.active_users END AS fte_per_active
  FROM fivetran.salesforce.account a
  LEFT JOIN active_l90 al ON al.account_id = a.id::string
)
SELECT account_id, PERCENT_RANK() OVER (ORDER BY fte_per_active NULLS FIRST) * 100 AS value
FROM ratios;`,
  funding: `SELECT a.id::string AS account_id,
  PERCENT_RANK() OVER (ORDER BY a.total_funding_amount_c NULLS FIRST) * 100 AS value
FROM fivetran.salesforce.account a
WHERE a.is_deleted = FALSE;`,
  execEngagement: `WITH exec_touch AS (
  SELECT account_id, COUNT(DISTINCT dedup_key) AS exec_touchpoints FROM (
    SELECT e.account_id, COALESCE(e.gong_gong_activity_id_c, 'evt_' || e.id) AS dedup_key
    FROM fivetran.salesforce.event e
    JOIN fivetran.salesforce.contact c ON c.id = e.who_id AND c.is_deleted = FALSE
    WHERE e._fivetran_deleted = FALSE AND e.activity_date >= DATEADD('day', -30, CURRENT_DATE)
      AND c.job_level_c IN ('Director','Executive','Vice President','C-Suite','CEO','CFO','COO')
    UNION ALL
    SELECT t.account_id, COALESCE(t.gong_gong_activity_id_c, 'tsk_' || t.id) AS dedup_key
    FROM fivetran.salesforce.task t
    JOIN fivetran.salesforce.contact c ON c.id = t.who_id AND c.is_deleted = FALSE
    WHERE t._fivetran_deleted = FALSE AND t.activity_date >= DATEADD('day', -30, CURRENT_DATE)
      AND c.job_level_c IN ('Director','Executive','Vice President','C-Suite','CEO','CFO','COO')
  ) combined GROUP BY account_id
)
SELECT account_id, PERCENT_RANK() OVER (ORDER BY exec_touchpoints ASC) * 50 AS value
FROM exec_touch;`,
  sentiment: `-- Latest overall sentiment per account → 0 / 25 / 50
SELECT account_id::string AS account_id,
  CASE UPPER(overall_sentiment)
    WHEN 'GREEN' THEN 50 WHEN 'YELLOW' THEN 25 WHEN 'RED' THEN 0 ELSE 0 END AS value
FROM clay.prod.egs_account_sentiment
QUALIFY ROW_NUMBER() OVER (PARTITION BY account_id ORDER BY update_date DESC NULLS LAST) = 1;`,
  pacing: `-- Implied contract pacing bucket → 0 / 25 / 50 / 100
SELECT account_id, CASE
    WHEN implied_pacing IS NULL THEN 0
    WHEN implied_pacing < 0.5 THEN 0
    WHEN implied_pacing < 0.75 THEN 25
    WHEN implied_pacing < 1.0 THEN 50
    ELSE 100 END AS value
FROM (
  SELECT cd.account_id::string AS account_id, cd.implied_pacing_from_aw AS implied_pacing,
    ROW_NUMBER() OVER (PARTITION BY cd.account_id ORDER BY cd.day DESC, cd.contract_start_date DESC) AS rn
  FROM dbt.prod.contracts_daily cd WHERE cd.plan_type = 'enterprise'
) WHERE rn = 1;`,
  empGrowthYoy: `-- Employee headcount YoY % change, percentile-ranked
WITH hist AS (
  SELECT account_id::string AS account_id, created_date, TRY_TO_NUMBER(new_value) AS emp_val
  FROM fivetran.salesforce.account_history
  WHERE is_deleted = FALSE AND field = 'NumberOfEmployees' AND TRY_TO_NUMBER(new_value) IS NOT NULL
),
y1 AS (
  SELECT account_id, emp_val AS emp_1y_ago FROM hist
  WHERE created_date <= DATEADD('day', -365, CURRENT_DATE)
  QUALIFY ROW_NUMBER() OVER (PARTITION BY account_id ORDER BY created_date DESC) = 1
)
SELECT a.id::string AS account_id,
  PERCENT_RANK() OVER (ORDER BY
    CASE WHEN y1.emp_1y_ago IS NULL OR y1.emp_1y_ago = 0 THEN NULL
      ELSE (a.number_of_employees - y1.emp_1y_ago)::float / y1.emp_1y_ago END NULLS FIRST) * 100 AS value
FROM fivetran.salesforce.account a LEFT JOIN y1 ON y1.account_id = a.id::string;`,
  peBacked: `-- PE-backed penalty flag (1 = subtract 10 via weight -10)
SELECT a.id::string AS account_id,
  IFF(a.last_funding_type_c = 'Private Equity', 1, 0) AS value
FROM fivetran.salesforce.account a WHERE a.is_deleted = FALSE;`,
  recentCw: `-- Recent Closed Won (last 5 months) penalty flag (1 = subtract 10 via weight -10)
SELECT a.id::string AS account_id,
  IFF(MAX(o.close_date) >= DATEADD('month', -5, CURRENT_DATE), 1, 0) AS value
FROM fivetran.salesforce.account a
LEFT JOIN fivetran.salesforce.opportunity o
  ON o.account_id = a.id AND o._fivetran_deleted = FALSE AND o.is_won = TRUE
WHERE a.is_deleted = FALSE GROUP BY a.id;`,
  amendmentBonus: `-- Open GS-owned amendment with a live quote → bonus flag (1 = add 10 via weight +10)
SELECT DISTINCT o.account_id::string AS account_id, 1 AS value
FROM fivetran.salesforce.opportunity o
WHERE o._fivetran_deleted = FALSE AND o.is_closed = FALSE
  AND o.monetize_lifecycle_type_c = 'Amendment' AND o.close_date >= CURRENT_DATE()
  AND EXISTS (
    SELECT 1 FROM fivetran.salesforce.monetize_quote_c q
    WHERE q.monetize_opportunity_c = o.id AND q._fivetran_deleted = FALSE AND q.monetize_status_c <> 'CANCELED'
  );`,
  fundingEvent: `-- IPO in last 180d OR any new funding round in last 90d → bonus flag (1 = add 10 via weight +10)
SELECT a.id::string AS account_id,
  IFF(
    (a.last_funding_type_c IN ('IPO','Post Ipo Equity') AND a.last_funding_date_c >= DATEADD('day', -180, CURRENT_DATE))
    OR a.last_funding_date_c >= DATEADD('day', -90, CURRENT_DATE), 1, 0) AS value
FROM fivetran.salesforce.account a WHERE a.is_deleted = FALSE;`,
};

function ExpansionModelEditor({
  model,
  setModel,
}: {
  model: ExpansionModel;
  setModel: (updater: (m: ExpansionModel) => ExpansionModel) => void;
}) {
  const patchComp = (i: number, patch: Partial<ExpansionComponent>) =>
    setModel((m) => ({ ...m, components: m.components.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) }));
  const setBands = (i: number, bands: ExpansionComponent['bands']) => patchComp(i, { bands });
  const removeComp = (i: number) => setModel((m) => ({ ...m, components: m.components.filter((_, idx) => idx !== i) }));
  const addQueryComp = () =>
    setModel((m) => ({
      ...m,
      components: [
        ...m.components,
        { key: `custom_${m.components.length + 1}`, label: 'New scored field', weight: 1, enabled: true, source: 'query', sql: '', bands: [] },
      ],
    }));

  const renderComp = (c: ExpansionComponent, i: number) => (
    <div className="bc-exp-comp" key={`${c.key}-${i}`}>
      <div className="bc-rule-row">
        <label className="bc-rule-check">
          <input type="checkbox" checked={c.enabled} onChange={(e) => patchComp(i, { enabled: e.target.checked })} />
          {STOCK_EXPANSION_KEYS.has(c.key) ? (
            <span className="bc-exp-name" title={c.label}>{c.label}</span>
          ) : (
            <input
              className="bc-exp-label"
              value={c.label}
              onChange={(e) => patchComp(i, { label: e.target.value })}
              placeholder="Label"
            />
          )}
        </label>
        <input
          className="bc-num"
          type="number"
          step={0.5}
          value={c.weight}
          title="weight"
          onChange={(e) => patchComp(i, { weight: Number(e.target.value) || 0 })}
        />
        {!STOCK_EXPANSION_KEYS.has(c.key) && (
          <button type="button" className="bc-link-danger" onClick={() => removeComp(i)}>Delete</button>
        )}
      </div>
      {c.source === 'query' && (
        <textarea
          className="bc-exp-sql"
          value={c.sql ?? ''}
          placeholder="SELECT account_id, value FROM dbt.prod.… -- read-only; returns account_id + numeric value"
          onChange={(e) => patchComp(i, { sql: e.target.value })}
        />
      )}
      <BandEditor bands={c.bands} onChange={(b) => setBands(i, b)} />
      {EXAMPLE_SQL[c.key] && (
        <ul className="bc-exp-sample">
          <li>
            <details>
              <summary>Sample query</summary>
              <textarea className="bc-exp-sql" readOnly value={EXAMPLE_SQL[c.key]} />
            </details>
          </li>
        </ul>
      )}
    </div>
  );

  // Keep original indices (patchComp/removeComp address by index) while grouping for layout.
  const indexed = model.components.map((c, i) => [c, i] as const);
  const pick = (keys: string[]) => indexed.filter(([c]) => keys.includes(c.key));
  const variables = pick(EXPANSION_VARIABLE_KEYS);
  const bonuses = pick(EXPANSION_BONUS_KEYS);
  const penalties = pick(EXPANSION_PENALTY_KEYS);
  const custom = indexed.filter(([c]) => !STOCK_EXPANSION_KEYS.has(c.key));

  return (
    <div className="bc-exp-editor">
      <p className="bc-muted">
        Expansion is recomputed as a weighted, banded sum of the components below, then percentile-ranked. Six built-in
        variables (seat headroom, funding, sentiment, contract pacing, exec engagement, employee growth) plus two bonuses
        and two penalties; add your own query-backed fields below (read-only, materialized into this carve's snapshot).
      </p>
      {model.enabled && (
        <div className="bc-exp-groups">
          {variables.length > 0 && (
            <div className="bc-exp-group">
              <div className="bc-exp-grouphead">Expansion variables</div>
              <div className="bc-exp-grid">{variables.map(([c, i]) => renderComp(c, i))}</div>
            </div>
          )}
          {bonuses.length > 0 && (
            <div className="bc-exp-group">
              <div className="bc-exp-grouphead">Bonuses</div>
              <div className="bc-exp-grid">{bonuses.map(([c, i]) => renderComp(c, i))}</div>
            </div>
          )}
          {penalties.length > 0 && (
            <div className="bc-exp-group">
              <div className="bc-exp-grouphead">Penalties</div>
              <div className="bc-exp-grid">{penalties.map(([c, i]) => renderComp(c, i))}</div>
            </div>
          )}
          <div className="bc-exp-group">
            <div className="bc-exp-grouphead">Custom fields</div>
            {custom.length > 0 && (
              <div className="bc-exp-grid bc-exp-grid-1">{custom.map(([c, i]) => renderComp(c, i))}</div>
            )}
            <button type="button" className="bc-link" onClick={addQueryComp}>+ Add scored field (SQL)</button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Inline editor for a component's scoring bands ({ upTo, score }, ascending; blank upTo = catch-all). */
function BandEditor({ bands, onChange }: { bands: ExpansionComponent['bands']; onChange: (b: ExpansionComponent['bands']) => void }) {
  const setBand = (i: number, patch: Partial<ExpansionComponent['bands'][number]>) =>
    onChange(bands.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  return (
    <div className="bc-bands">
      <span className="bc-muted bc-bands-label">Bands:{bands.length ? '' : ' None'}</span>
      {bands.map((b, i) => (
        <span className="bc-band" key={i}>
          ≤
          <input
            className="bc-num bc-band-num"
            type="number"
            value={b.upTo ?? ''}
            placeholder="∞"
            onChange={(e) => setBand(i, { upTo: e.target.value === '' ? null : Number(e.target.value) })}
          />
          →
          <input
            className="bc-num bc-band-num"
            type="number"
            value={b.score}
            onChange={(e) => setBand(i, { score: Number(e.target.value) || 0 })}
          />
          <button type="button" className="bc-link-danger" onClick={() => onChange(bands.filter((_, idx) => idx !== i))}>×</button>
        </span>
      ))}
      <button type="button" className="bc-band-add" onClick={() => onChange([...bands, { upTo: null, score: 100 }])}>+ Band</button>
    </div>
  );
}

/** Small info marker that surfaces help text in a styled bubble on hover. */
function Tip({ text, icon = '?' }: { text: string; icon?: string }) {
  return (
    <span className="bc-tip" aria-label={text}>
      {icon}
      <span className="bc-tip-bubble" role="tooltip">{text}</span>
    </span>
  );
}

function RulesEditor({
  scope, setScope, cutoverDate, setCutoverDate, locks, setLocks, capEnabled, setCapEnabled, capPct, setCapPct,
  geoLock, setGeoLock, balance, setBalance,
  existingTargetCount, setExistingTargetCount, existingTargetArr, setExistingTargetArr,
  protectRedHealth, setProtectRedHealth,
  team,
}: {
  scope: CarveConfig['scope'];
  setScope: (s: CarveConfig['scope']) => void;
  cutoverDate: string;
  setCutoverDate: (v: string) => void;
  locks: LockConfig;
  setLocks: (updater: (l: LockConfig) => LockConfig) => void;
  capEnabled: boolean;
  setCapEnabled: (b: boolean) => void;
  capPct: number;
  setCapPct: (n: number) => void;
  geoLock: boolean;
  setGeoLock: (b: boolean) => void;
  balance: BalanceDims;
  setBalance: (updater: (b: BalanceDims) => BalanceDims) => void;
  existingTargetCount: number;
  setExistingTargetCount: (n: number) => void;
  existingTargetArr: number;
  setExistingTargetArr: (n: number) => void;
  protectRedHealth: boolean;
  setProtectRedHealth: (b: boolean) => void;
  team: 'egs' | 'sgs';
}) {
  const teamLabel = team.toUpperCase();
  const targetsDisabled = scope === 'newhire';
  const setDim = (key: BalanceKey, patch: Partial<BalanceDim>) =>
    setBalance((b) => ({ ...b, [key]: { ...b[key], ...patch } }));
  const setLockNum = (key: 'onboardingWithinDays' | 'renewingWithinDays' | 'ownedRecentlyDays', enabled: boolean, value?: number) =>
    setLocks((l) => ({ ...l, [key]: enabled ? (value ?? (l[key] === false ? LOCK_DEFAULTS[key] : l[key])) : false }));

  // Expansion is a balance dimension only: this toggles whether expansion is weighted in the carve.
  // The scoring model itself is fixed (v4, precomputed) and shown read-only under "3. Expansion Scoring".
  const setExpansionEnabled = (on: boolean) => setDim('expansion', { on });
  const [showRubric, setShowRubric] = useState(false);

  // Scope-aware helper text: the same slider influences different things in New-Hire vs Rebalance.
  const newHire = scope === 'newhire';
  const scopeHelp = (key: BalanceKey): string => {
    if (!balance[key].on && key === 'expansion') return 'off — not weighted';
    if (newHire) {
      switch (key) {
        case 'accounts':
          return 'New-Hire: how evenly existing reps give up accounts (donations).';
        case 'arr':
          return 'New-Hire: which books donate first (higher-ARR books) + ARR mix across the new books.';
        case 'renewal':
          return 'New-Hire: renewal-quarter spread across the new books.';
        case 'expansion':
          return 'New-Hire: expansion potential spread evenly across the new books.';
      }
    }
    switch (key) {
      case 'accounts':
        return 'Rebalance: each existing book\u2019s deviation from its account target.';
      case 'arr':
        return 'Rebalance: each existing book\u2019s deviation from its ARR target.';
      case 'renewal':
        return 'Rebalance: renewal-quarter spread across books.';
      case 'expansion':
        return 'Rebalance: expansion potential spread evenly across books.';
    }
    return '';
  };

  // Live values for the dynamic Base-Rule tooltips.
  const onbVal = locks.onboardingWithinDays === false ? LOCK_DEFAULTS.onboardingWithinDays : locks.onboardingWithinDays;
  const renVal = locks.renewingWithinDays === false ? LOCK_DEFAULTS.renewingWithinDays : locks.renewingWithinDays;
  const ownTopVal = locks.ownedRecentlyDays === false ? LOCK_DEFAULTS.ownedRecentlyDays : locks.ownedRecentlyDays;
  const ownFloorVal = locks.ownedRecentlyFloorDays ?? 90;

  /** Enable + 0-100 importance control shared by all four balance dimensions. */
  const importanceCtl = (key: BalanceKey) => {
    const dim = balance[key];
    return (
      <>
        <input
          className="bc-num"
          type="number"
          min={0}
          max={100}
          value={dim.importance}
          disabled={!dim.on}
          onChange={(e) => setDim(key, { importance: Math.min(100, Math.max(0, Number(e.target.value) || 0)) })}
        />
      </>
    );
  };

  return (
    <div className="bc-rules">
      <div className="bc-rule-block">
        <div className="bc-label">Scope</div>
        <div className="bc-scope-line">
          <select className="bc-scope-select" value={scope} onChange={(e) => setScope(e.target.value as CarveConfig['scope'])}>
            {SCOPE_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <span className="bc-muted">
            New hire (default): seed new books only — existing reps donate but are never rebalanced against each other (no EGS-to-EGS swaps).
          </span>
        </div>
        {scope !== 'newhire' && (
          <div className="bc-cutover-row">
            <span className="bc-field-label">Cutover Date</span>
            <input className="bc-date" type="date" value={cutoverDate} onChange={(e) => setCutoverDate(e.target.value)} />
            <Tip text="Assignment date sent to Clay for accounts moving between current reps when this carve is finalized. New books still use their ramp date." />
          </div>
        )}
      </div>

      <div className="bc-rule-block">
        <div className="bc-label">1. Base Rules</div>
        <div className="bc-base-list">
          {/* Onboarding */}
          <div className="bc-base-row">
            <label className="bc-rule-check">
              <input type="checkbox" checked={locks.onboardingWithinDays !== false} onChange={(e) => setLockNum('onboardingWithinDays', e.target.checked)} />
              <span>New Customer Hold</span>
            </label>
            <input
              className="bc-num"
              type="number"
              min={0}
              value={locks.onboardingWithinDays === false ? LOCK_DEFAULTS.onboardingWithinDays : locks.onboardingWithinDays}
              disabled={locks.onboardingWithinDays === false}
              onChange={(e) => setLockNum('onboardingWithinDays', true, Math.max(0, Number(e.target.value) || 0))}
            />
            <span className="bc-muted bc-rule-help">days</span>
            <Tip text={`Onboarding Rule: won't reassign accounts that started on Enterprise within ${onbVal} days of carve/ramp date.`} />
          </div>

          {/* Renewing */}
          <div className="bc-base-row">
            <label className="bc-rule-check">
              <input type="checkbox" checked={locks.renewingWithinDays !== false} onChange={(e) => setLockNum('renewingWithinDays', e.target.checked)} />
              <span>Renewal Hold</span>
            </label>
            <input
              className="bc-num"
              type="number"
              min={0}
              value={locks.renewingWithinDays === false ? LOCK_DEFAULTS.renewingWithinDays : locks.renewingWithinDays}
              disabled={locks.renewingWithinDays === false}
              onChange={(e) => setLockNum('renewingWithinDays', true, Math.max(0, Number(e.target.value) || 0))}
            />
            <span className="bc-muted bc-rule-help">days</span>
            <Tip text={`Renewals: won't reassign accounts renewing in ${renVal} days from carve/ramp date.`} />
          </div>

          {/* Re-owned recently: top + floor + auto-relax */}
          <div className="bc-base-row">
            <label className="bc-rule-check">
              <input type="checkbox" checked={locks.ownedRecentlyDays !== false} onChange={(e) => setLockNum('ownedRecentlyDays', e.target.checked)} />
              <span>Recently Assigned Hold</span>
            </label>
            <input
              className="bc-num"
              type="number"
              min={0}
              value={locks.ownedRecentlyDays === false ? LOCK_DEFAULTS.ownedRecentlyDays : locks.ownedRecentlyDays}
              disabled={locks.ownedRecentlyDays === false}
              onChange={(e) => setLockNum('ownedRecentlyDays', true, Math.max(0, Number(e.target.value) || 0))}
            />
            <span className="bc-muted bc-rule-help">to</span>
            <input
              className="bc-num"
              type="number"
              min={0}
              value={locks.ownedRecentlyFloorDays ?? 90}
              disabled={locks.ownedRecentlyDays === false}
              onChange={(e) => setLocks((l) => ({ ...l, ownedRecentlyFloorDays: Math.max(0, Number(e.target.value) || 0) }))}
            />
            <span className="bc-muted bc-rule-help">days</span>
            <Tip text={`Do not carve accounts that were assigned within ${ownTopVal} days. If books can't be filled to target, this hold auto-relaxes from ${ownTopVal} down to ${ownFloorVal} days.`} />
          </div>

          {/* Cap Donor Loss */}
          <div className="bc-base-row">
            <label className="bc-rule-check">
              <input type="checkbox" checked={capEnabled} onChange={(e) => setCapEnabled(e.target.checked)} />
              <span>Cap Donor Loss</span>
            </label>
            <input
              className="bc-num"
              type="number"
              min={0}
              max={100}
              value={capPct}
              disabled={!capEnabled}
              onChange={(e) => setCapPct(Math.min(100, Math.max(0, Number(e.target.value) || 0)))}
            />
            <span className="bc-muted bc-rule-help">% of pre-book</span>
            <Tip text="Donating GSs can lose at most [25]% of their current books." />
          </div>

          {/* Geo Lock */}
          <div className="bc-base-row">
            <label className="bc-rule-check">
              <input type="checkbox" checked={geoLock} onChange={(e) => setGeoLock(e.target.checked)} />
              <span>Geo Lock</span>
            </label>
            <Tip text="Hard rule: each new book / regioned rep only takes accounts whose recommended region (US-W / US-E / EMEA) matches." />
          </div>

          {/* Protect Red-health accounts */}
          <div className="bc-base-row">
            <label className="bc-rule-check">
              <input type="checkbox" checked={protectRedHealth} onChange={(e) => setProtectRedHealth(e.target.checked)} />
              <span>Protect Red-health accounts</span>
            </label>
            <Tip text="Strongly discourages reassigning accounts whose SFDC health is Red. They only move if there's no other way to fill the new books." />
          </div>
        </div>
        <p className="bc-muted">Pinned / frozen accounts are also immovable.</p>
      </div>

      <div className="bc-rule-block">
        <div className="bc-label bc-rule-check">
          2. Carve Priorities
          <Tip text="New-hire carves place accounts in three steps: (1) Which rep donates next — set by Account Balance (even account counts) and ARR Balance (higher-ARR books give first); (2) Which of that rep's accounts moves — protects recently-assigned and Red-health accounts; (3) Which new book receives it — spreads ARR, renewal quarters, and expansion evenly across the new books. In Rebalance/Combined scope these same priorities balance the existing books toward their targets." />
        </div>
        <p className="bc-muted">
          How donor accounts are balanced across books. Importance is a 0-100 relative weight (equal numbers = equal emphasis).
          {' '}ARR outliers are capped at the 95th percentile for balance math, so one whale doesn&apos;t distort the targets.
          {targetsDisabled ? '' : ` Targets steer each existing ${teamLabel} book toward a fully-ramped size while respecting Cap Donor Loss.`}
        </p>

        {/* Accounts: importance + existing-GS target count */}
        <div className="bc-balance-row">
          <label className="bc-rule-check">
            <input type="checkbox" checked={balance.accounts.on} onChange={(e) => setDim('accounts', { on: e.target.checked })} />
            <span className="bc-balance-title">Account Balance</span>
            <Tip text={`How many accounts do we want existing GSs (fully ramped) to have?\n\n${scopeHelp('accounts')}`} />
          </label>
          {importanceCtl('accounts')}
          {!targetsDisabled && (
            <div className="bc-balance-target">
              <input
                className="bc-num bc-num-arr"
                type="number"
                min={0}
                value={existingTargetCount}
                title="Target accounts for a fully-ramped existing GS"
                onChange={(e) => setExistingTargetCount(Math.max(0, Number(e.target.value) || 0))}
              />
              <span className="bc-muted bc-rule-help">target accts (existing GSs)</span>
            </div>
          )}
        </div>

        {/* ARR: importance + existing-GS target ARR */}
        <div className="bc-balance-row">
          <label className="bc-rule-check">
            <input type="checkbox" checked={balance.arr.on} onChange={(e) => setDim('arr', { on: e.target.checked })} />
            <span className="bc-balance-title">ARR Balance</span>
            <Tip text={`How much ARR under management do we want existing GSs (fully ramped) to have?\n\n${scopeHelp('arr')}`} />
          </label>
          {importanceCtl('arr')}
          {!targetsDisabled && (
            <div className="bc-balance-target">
              <input
                className="bc-num bc-num-arr"
                title="Target ARR for a fully-ramped existing GS"
                {...arrInputProps(existingTargetArr, setExistingTargetArr)}
              />
              <span className="bc-muted bc-rule-help">target ARR (existing GSs)</span>
            </div>
          )}
        </div>

        {/* Renewal-quarter spread */}
        <div className="bc-balance-row">
          <label className="bc-rule-check">
            <input type="checkbox" checked={balance.renewal.on} onChange={(e) => setDim('renewal', { on: e.target.checked })} />
            <span className="bc-balance-title">Renewal Quarter Balance</span>
            <Tip text={`When enabled, the model will attempt to balance renewals evenly.\n\n${scopeHelp('renewal')}`} />
          </label>
          {importanceCtl('renewal')}
        </div>
        {/* Expansion score */}
        <div className="bc-balance-row">
          <label className="bc-rule-check">
            <input type="checkbox" checked={balance.expansion.on} onChange={(e) => setExpansionEnabled(e.target.checked)} />
            <span className="bc-balance-title">Expansion Score Balance</span>
            <Tip text={`How much expansion potential is balanced across books. On = expansion is spread evenly (new and existing books each get a fair, roughly-average share).\n\n${scopeHelp('expansion')}`} />
          </label>
          {importanceCtl('expansion')}
        </div>
      </div>

      <div className="bc-rule-block">
        <div className="bc-label bc-rule-check bc-section-toggle">
          <span>3. Expansion Scoring</span>
          <input
            type="checkbox"
            checked={balance.expansion.on}
            onChange={(e) => setExpansionEnabled(e.target.checked)}
            title="Weight expansion potential in the carve (same toggle as Expansion Score Balance above)"
          />
          <Tip text={'Fixed Expansion Score v4 model, precomputed in Snowflake (read-only).\n\nWeighted base components (seat headroom, HC growth, funding/scale, pacing, exec engagement) + a health modifier, then penalties (PE-backed, multi-year, contraction, saturation, recent renewals) and bonuses (amendments, funding events, team penetration).'} />
          <button type="button" className="bc-link bc-rubric-toggle" onClick={() => setShowRubric((v) => !v)}>
            {showRubric ? 'Hide model' : 'View model'}
          </button>
        </div>
        {showRubric && <ExpansionRubric />}
      </div>
    </div>
  );
}

/** Read-only, full-width two-column view of the Expansion Score v4 rubric (no inputs). */
function ExpansionRubric() {
  const Row = ({ item }: { item: RubricItem }) => (
    <div className="bc-rubric-row">
      <span className="bc-rubric-weight">{item.weight}</span>
      <span className="bc-rubric-body">
        <span className="bc-rubric-label">{item.label}</span>
        <span className="bc-rubric-detail">{item.detail}</span>
      </span>
    </div>
  );
  return (
    <div className="bc-rubric">
      <p className="bc-muted">
        Expansion is scored by a fixed v4 model (precomputed in Snowflake). Weights below are relative; the base
        denominator shrinks when a component is excluded (e.g. pacing during the 90-day grace period).
      </p>
      <div className="bc-rubric-cols">
        <div className="bc-rubric-col">
          <div className="bc-rubric-head">Base scoring (weighted average)</div>
          {EXPANSION_RUBRIC.base.map((it) => (
            <Row key={it.label} item={it} />
          ))}
          <div className="bc-rubric-head">Health modifier</div>
          {EXPANSION_RUBRIC.modifier.map((it) => (
            <Row key={it.label} item={it} />
          ))}
        </div>
        <div className="bc-rubric-col">
          <div className="bc-rubric-head">Penalties</div>
          {EXPANSION_RUBRIC.penalties.map((it) => (
            <Row key={it.label} item={it} />
          ))}
          <div className="bc-rubric-head">Bonuses</div>
          {EXPANSION_RUBRIC.bonuses.map((it) => (
            <Row key={it.label} item={it} />
          ))}
        </div>
      </div>
      <p className="bc-muted bc-rubric-note">{EXPANSION_RUBRIC.note}</p>
    </div>
  );
}

// ────────────────────────────── Reusable carve config editor ──────────────────────────────

/** The config object the editor produces (consumed by POST /carves and /carves/:id/run). */
interface CarvePatch {
  scope: CarveConfig['scope'];
  /** Rebalance/Combined: assignment date sent to Clay for moves between current reps ('' = unset). */
  cutoverDate?: string;
  locks: LockConfig;
  movementCapPct: number;
  expansionModel: ExpansionModel;
  weights: {
    accounts: number;
    newOwnerArrBalanceK: number;
    newOwnerQuarterSpread: number;
    newOwnerExpansionK: number;
    /** Rebalance/Combined objective terms (normalizeConfig reads these; see config.ts). */
    arrSpreadK: number;
    expansionSpread: number;
    q2: number;
    q3: number;
    q4: number;
  };
  enforceRegion: boolean;
  frontLoadNewBooks: boolean;
  existingTargetCount: number;
  existingTargetArr: number;
  arrBalanceWinsorPct: number;
  protectRedHealth: boolean;
  includeDummyAccounts: boolean;
  renewalBallast: number;
  estNewBizPerQuarter?: number;
  newOwners: { name: string; targetCount: number; targetArr?: number; region?: string; startDate?: string; rampDate?: string; expectedAccounts?: number; seatMonths?: string[]; assigneeName?: string; assigneeSfdcUserId?: string }[];
  pinnedAssignments: { accountId: string; gs: string }[];
  perRepOverrides: Record<string, RepOverride>;
  repRegions: Record<string, string>;
  repManagers: Record<string, string>;
  excludedReps: string[];
  excludedAccountIds: string[];
}

/** Full editor state — kept in one bag so it can be seeded from a config and serialized for a build. */
interface EditorState {
  scope: CarveConfig['scope'];
  /** Rebalance/Combined: assignment date sent to Clay for moves between current reps ('' = unset). */
  cutoverDate: string;
  locks: LockConfig;
  capEnabled: boolean;
  capPct: number;
  expansionModel: ExpansionModel;
  balance: BalanceDims;
  geoLock: boolean;
  frontLoad: boolean;
  existingTargetCount: number;
  existingTargetArr: number;
  winsorOn: boolean;
  winsorPct: number;
  protectRedHealth: boolean;
  includeDummyAccounts: boolean;
  /** Minimum REAL accounts per new book before the general fill (seats leave this much room). */
  renewalBallast: number;
  /** Override for the Full Book estimate rate (accounts/rep/quarter). undefined = use historical stat. */
  estNewBizPerQuarter?: number;
  books: NewBook[];
  actions: Record<string, RepAction>;
  regions: Record<string, string>;
  /** Explicit rep/new-book manager overrides (display-only grouping). */
  managers: Record<string, string>;
  repTargets: Record<string, { count?: number; arr?: number }>;
  extraPins: { accountId: string; accountName: string; gs: string; global?: boolean }[];
  includeManagers: boolean;
  /** Pasted/search-picked account IDs removed from this carve entirely (exclusion beats pins). */
  excludedText: string;
}

const RYG_DOT = (v?: string | null): ReactNode => {
  const k = (v ?? '').toLowerCase();
  const cls = k.includes('green') ? 'bc-dot-green' : k.includes('yellow') ? 'bc-dot-yellow' : k.includes('red') ? 'bc-dot-red' : 'bc-dot-none';
  return <span className={`bc-dot ${cls}`} title={v ?? '—'} />;
};

/** Seed editor state from a config (used on create from base logic, and on the carve page from setup/current config). */
function seedEditorState(cfg: CarveConfig | null, team: 'egs' | 'sgs', mode: 'create' | 'edit', allAccounts: UniverseAccount[], mustKeep: MustKeepEntry[], reps: Rep[]): EditorState {
  const seg = SEGMENT_TARGETS[team];
  const dim = (key: BalanceKey, coef: number | undefined): BalanceDim =>
    coef == null ? { on: true, importance: IMPORTANCE_DEFAULTS[key] } : { on: coef > 0, importance: coef > 0 ? coefToImportance(key, coef) : IMPORTANCE_DEFAULTS[key] };
  if (!cfg) {
    return {
      scope: 'newhire',
      cutoverDate: '',
      locks: { onboardingWithinDays: 90, renewingWithinDays: 90, ownedRecentlyDays: 180, ownedRecentlyFloorDays: 90, autoRelaxOwnedRecently: true },
      capEnabled: true,
      capPct: 25,
      expansionModel: DEFAULT_EXPANSION_MODEL,
      balance: {
        accounts: { on: true, importance: IMPORTANCE_DEFAULTS.accounts },
        arr: { on: true, importance: IMPORTANCE_DEFAULTS.arr },
        renewal: { on: true, importance: IMPORTANCE_DEFAULTS.renewal },
        expansion: { on: true, importance: IMPORTANCE_DEFAULTS.expansion },
      },
      geoLock: team === 'sgs',
      // SoT Criteria 7 default-on: ramping reps get renewals from the 2 quarters before ramp.
      frontLoad: true,
      existingTargetCount: seg.count,
      existingTargetArr: seg.arr,
      winsorOn: true,
      winsorPct: 95,
      protectRedHealth: true,
      includeDummyAccounts: true,
      renewalBallast: 2,
      estNewBizPerQuarter: undefined,
      books: [{ name: '', targetCount: seg.startCount, pinnedText: '' }],
      actions: {},
      regions: {},
      managers: {},
      repTargets: {},
      extraPins: [],
      includeManagers: false,
      excludedText: '',
    };
  }
  const w = cfg.weights;
  const newBookNames = new Set(cfg.newOwners.map((o) => o.name));
  const nameById = new Map(allAccounts.map((a) => [a.accountId, a.accountName]));
  const mustKeepIds = new Set(mustKeep.map((e) => e.accountId));
  // Which managers this saved config has "not owning accounts". Carves saved from 2026-08-04 mark
  // them donateAll; older ones listed them in excludedReps, so both shapes are read here or an old
  // carve would reopen with the toggle off and put managers back on the books when re-saved.
  const drainedManagers = new Set(
    reps
      .filter((r) => r.isManager)
      .map((r) => r.name)
      .filter((name) => cfg.perRepOverrides?.[name]?.donateAll === true || (cfg.excludedReps ?? []).includes(name)),
  );
  // New-book pins → reconstructed into each book's pasted text; existing-rep pins → extraPins chips.
  const books: NewBook[] =
    mode === 'edit' && cfg.newOwners.length > 0
      ? cfg.newOwners.map((o) => {
          const ids = (cfg.pinnedAssignments ?? []).filter((p) => p.gs === o.name).map((p) => p.accountId);
          return {
            name: o.name,
            targetCount: o.targetCount,
            targetArr: o.targetArr,
            region: o.region,
            manager: cfg.repManagers?.[o.name],
            startDate: o.startDate,
            // Hidden manual ramp override (homepage "release") — preserved through editor saves.
            rampDate: o.rampDate,
            assigneeName: o.assigneeName,
            assigneeSfdcUserId: o.assigneeSfdcUserId,
            pinnedText: ids.join('\n'),
          };
        })
      : [{ name: '', targetCount: SEGMENT_TARGETS[team].startCount, pinnedText: '' }];
  const extraPins = (cfg.pinnedAssignments ?? [])
    .filter((p) => !newBookNames.has(p.gs))
    .map((p) => ({ accountId: p.accountId, accountName: nameById.get(p.accountId) ?? p.accountId, gs: p.gs, global: mustKeepIds.has(p.accountId) }));
  const actions: Record<string, RepAction> = {};
  const repTargets: Record<string, { count?: number; arr?: number }> = {};
  for (const [name, ov] of Object.entries(cfg.perRepOverrides ?? {})) {
    if (ov?.frozen) actions[name] = ov.noHandoffs ? 'keepsilent' : 'keep';
    // A drained manager's donateAll comes from the manager toggle, not an Empty Book action.
    // Surfacing it as 'empty' would leave the book draining even after the toggle is unchecked.
    else if (ov?.donateAll && !drainedManagers.has(name)) actions[name] = 'empty';
    else if (ov?.maxAdded === 0) actions[name] = 'nonew';
    if (ov?.targetCount != null) repTargets[name] = { ...repTargets[name], count: ov.targetCount };
    if (ov?.targetArr != null) repTargets[name] = { ...repTargets[name], arr: ov.targetArr };
  }
  return {
    scope: cfg.scope ?? 'newhire',
    cutoverDate: cfg.cutoverDate ?? '',
    // autoRelax is always-on now (the UI toggle was removed); normalize old configs that had it off.
    locks: { ...cfg.locks, ownedRecentlyFloorDays: cfg.locks.ownedRecentlyFloorDays ?? 90, autoRelaxOwnedRecently: true },
    capEnabled: (cfg.movementCapPct ?? 1) < 1,
    capPct: Math.round((cfg.movementCapPct ?? 0.25) * 100),
    expansionModel: cfg.expansionModel ?? DEFAULT_EXPANSION_MODEL,
    balance: { accounts: dim('accounts', w?.accounts), arr: dim('arr', w?.newOwnerArrBalanceK), renewal: dim('renewal', w?.newOwnerQuarterSpread), expansion: dim('expansion', w?.newOwnerExpansionK) },
    geoLock: cfg.enforceRegion ?? team === 'sgs',
    frontLoad: cfg.frontLoadNewBooks ?? true,
    existingTargetCount: cfg.existingTargetCount ?? seg.count,
    existingTargetArr: cfg.existingTargetArr ?? seg.arr,
    winsorOn: (cfg.arrBalanceWinsorPct ?? 95) < 100,
    winsorPct: cfg.arrBalanceWinsorPct ?? 95,
    protectRedHealth: cfg.protectRedHealth !== false,
    includeDummyAccounts: cfg.includeDummyAccounts !== false,
    renewalBallast: cfg.renewalBallast ?? 2,
    estNewBizPerQuarter: cfg.estNewBizPerQuarter,
    books,
    actions,
    regions: { ...(cfg.repRegions ?? {}) },
    managers: { ...(cfg.repManagers ?? {}) },
    repTargets,
    extraPins,
    // On create, managers never own accounts. On edit, reflect what was saved.
    includeManagers: mode === 'edit' ? drainedManagers.size === 0 : false,
    excludedText: (cfg.excludedAccountIds ?? []).join('\n'),
  };
}

/** Pure: turn editor state + reps into the config patch posted to the API. */
function buildCarveConfig(s: EditorState, reps: Rep[], team: 'egs' | 'sgs'): CarvePatch {
  const perAcct = team === 'sgs' ? 210000 : 80000;
  const perAcctTarget = s.existingTargetCount > 0 ? s.existingTargetArr / s.existingTargetCount : perAcct;
  const owners = s.books
    .filter((b) => b.name.trim())
    .map((b) => {
      const ids = extractAccountIds(b.pinnedText);
      // Pins seed the book; the user's target fills the rest. Target wins unless fewer than pinned.
      const targetCount = Math.max(b.targetCount || 0, ids.length);
      return { name: b.name.trim(), targetCount, targetArr: b.targetArr ?? Math.round(targetCount * perAcctTarget), ids, region: b.region, startDate: b.startDate, rampDate: b.rampDate, assigneeName: b.assigneeName?.trim(), assigneeSfdcUserId: b.assigneeSfdcUserId?.trim() };
    });
  // Precedence: an explicit paste into a book WINS over the ambient pins (global must-keep chips /
  // rep pins) — chips apply first, book pastes overwrite. Duplicates across two books keep the later
  // book; the editor flags both cases visibly so nothing is rebound silently.
  const pinMap = new Map<string, string>();
  for (const p of s.extraPins) pinMap.set(p.accountId, p.gs);
  for (const o of owners) for (const id of o.ids) pinMap.set(id, o.name);
  const pinnedAssignments = [...pinMap.entries()].map(([accountId, gs]) => ({ accountId, gs }));
  const perRepOverrides: Record<string, RepOverride> = {};
  const ov = (name: string): RepOverride => (perRepOverrides[name] ??= {});
  for (const [name, a] of Object.entries(s.actions)) {
    if (a === 'keep') ov(name).frozen = true;
    else if (a === 'keepsilent') { ov(name).frozen = true; ov(name).noHandoffs = true; }
    else if (a === 'empty') ov(name).donateAll = true;
    else if (a === 'nonew' && s.scope !== 'newhire') ov(name).maxAdded = 0; // moot in New-Hire scope
  }
  for (const [name, t] of Object.entries(s.repTargets)) {
    if (t.count != null && t.count !== s.existingTargetCount) ov(name).targetCount = t.count;
    if (t.arr != null && t.arr !== s.existingTargetArr) ov(name).targetArr = t.arr;
  }
  // Region map is for DISPLAY (Region Balance / badges) and is always populated; hard enforcement is
  // separately gated by enforceRegion (= s.geoLock). Seed inferred per-rep regions (manager/timezone),
  // then let explicit setup picks and per-book regions override.
  const repRegions: Record<string, string> = {};
  for (const r of reps) if (r.region) repRegions[r.name] = r.region;
  for (const [name, region] of Object.entries(s.regions)) if (region) repRegions[name] = region;
  for (const b of s.books) if (b.name.trim() && b.region) repRegions[b.name.trim()] = b.region;
  // Manager assignments (display-only): explicit overrides + per-new-book manager.
  const repManagers: Record<string, string> = {};
  for (const [name, mn] of Object.entries(s.managers)) if (mn) repManagers[name] = mn;
  for (const b of s.books) if (b.name.trim() && b.manager?.trim()) repManagers[b.name.trim()] = b.manager.trim();
  const coef = (key: BalanceKey, dim: BalanceDim) => (dim.on ? importanceToCoef(key, dim.importance) : 0);
  const renewalQ = s.balance.renewal.on ? (s.balance.renewal.importance / IMPORTANCE_DEFAULT) * BASE_QUARTER_COEF : 0;
  // Managers don't own accounts (operator direction 2026-08-04) — and the accounts they DO own
  // redistribute to their own team. Marking each detected manager donateAll is what buys that:
  // the engine's drain empties their book into the reps who report to them, and a donateAll rep is
  // excluded from the recipient pool so nothing routes back onto a manager.
  //
  // Deliberately NOT excludedReps, which is what this used to write. That list removes the accounts
  // from the run universe entirely rather than redistributing them — on the two live 2026-08 carves
  // it silently deleted 9 accounts worth ~$4.68M ARR (Anthropic at $3.62M vanished from the STRAT
  // carve); the only survivors were the ones some other rule happened to pin. An explicit Keep Book
  // action on a manager still wins, since the engine's freeze outranks the drain.
  if (!s.includeManagers) for (const r of reps) if (r.isManager) ov(r.name).donateAll = true;
  return {
    scope: s.scope,
    // '' clears a previously-set date (normalizeConfig drops empty/invalid values); moot in New-Hire scope.
    cutoverDate: s.scope !== 'newhire' ? s.cutoverDate : '',
    locks: s.locks,
    movementCapPct: s.capEnabled ? s.capPct / 100 : 1,
    expansionModel: s.expansionModel,
    weights: {
      accounts: coef('accounts', s.balance.accounts),
      newOwnerArrBalanceK: coef('arr', s.balance.arr),
      newOwnerQuarterSpread: coef('renewal', s.balance.renewal),
      newOwnerExpansionK: coef('expansion', s.balance.expansion),
      // Rebalance/Combined objective: keep the ARR-spread and expansion-spread terms responsive to their
      // sliders too (their own base coefs so the objective stays calibrated). expansionSpread was 0/off.
      arrSpreadK: s.balance.arr.on ? (s.balance.arr.importance / IMPORTANCE_DEFAULT) * 2 : 0,
      expansionSpread: coef('expansion', s.balance.expansion),
      q2: renewalQ,
      q3: renewalQ,
      q4: renewalQ,
    },
    enforceRegion: s.geoLock,
    // SoT Criteria 7: front-loading is default-on for New-Hire scope so a ramping rep's
    // initial book pulls renewals from the 2 quarters BEFORE their ramp date (a Q1 starter
    // gets Q3/Q4 renewals). Rebalance/Combined don't front-load — they're managing an
    // existing rep's book that already has a renewal cadence. The editor's `frontLoad` field
    // seeds from the config on edit, so this respects operator overrides across sessions.
    frontLoadNewBooks: s.scope === 'newhire' ? s.frontLoad : false,
    existingTargetCount: s.existingTargetCount,
    existingTargetArr: s.existingTargetArr,
    arrBalanceWinsorPct: s.winsorOn ? s.winsorPct : 100,
    protectRedHealth: s.protectRedHealth,
    includeDummyAccounts: s.includeDummyAccounts,
    renewalBallast: s.renewalBallast,
    ...(s.estNewBizPerQuarter != null ? { estNewBizPerQuarter: s.estNewBizPerQuarter } : {}),
    newOwners: owners.map(({ name, targetCount, targetArr, region, startDate, rampDate, assigneeName, assigneeSfdcUserId }) => ({
      name,
      targetCount,
      ...(targetArr ? { targetArr } : {}),
      // Region persists regardless of Region Lock: it feeds display, forecasting, and the hard
      // gate for Segment + Region Sorting pins (which bind even with the lock off).
      ...(region ? { region } : {}),
      ...(startDate ? { startDate } : {}),
      // Manual ramp override (homepage "release") rides along so editor saves don't clear it.
      ...(rampDate ? { rampDate } : {}),
      // SoT Criteria 7: reserve ~50% of a new-hire book for expected new business (leaves room
      // for the New Sale router to land there — see api.ts synthesizeDummies). This is a FLOOR:
      // pass-2 seat reconciliation can grow it via shortfall conversion when forecast supply
      // has extra headroom, and will shrink it (with a warning) when supply can't back all the
      // reserved seats — the freed slots return to real-account carving. Only set for New-Hire
      // scope; Rebalance/Combined already respect the configured targetCount without reservation.
      ...(s.scope === 'newhire' ? { expectedAccounts: Math.max(1, Math.round(targetCount / 2)) } : {}),
      ...(assigneeName ? { assigneeName } : {}),
      ...(assigneeSfdcUserId ? { assigneeSfdcUserId } : {}),
    })),
    pinnedAssignments,
    perRepOverrides,
    repRegions,
    repManagers,
    // Always empty from this editor: the manager case moved to donateAll (above), and excludedReps
    // is now only ever populated outside the setup UI, for genuinely excluding a rep.
    excludedReps: [],
    excludedAccountIds: extractAccountIds(s.excludedText),
  };
}

/** Books that block a run under Region Lock (mirrors the server-side guard in POST /carves/:id/run). */
function regionLockBlockers(patch: Pick<CarvePatch, 'enforceRegion' | 'scope' | 'newOwners'> | null): string[] {
  if (!patch?.enforceRegion || patch.scope === 'rebalance') return [];
  return patch.newOwners.filter((o) => !(o.region ?? '').trim()).map((o) => o.name);
}

/**
 * Per-book pin editor: search the team universe by account name to add pins, paste an ID list in bulk,
 * and see the pinned accounts rendered by name (with remove). All state lives in the book's pinnedText,
 * so nothing downstream changes — extractAccountIds parses the IDs regardless of surrounding text.
 */
function BookPinEditor({ value, onChange, accounts, nameById }: {
  value: string;
  onChange: (next: string) => void;
  accounts: UniverseAccount[];
  nameById: Map<string, string>;
}) {
  const [q, setQ] = useState('');
  const ids = extractAccountIds(value);
  const idSet = new Set(ids);
  const query = q.trim().toLowerCase();
  const matches = query
    ? accounts.filter((a) => !idSet.has(a.accountId) && a.accountName.toLowerCase().includes(query)).slice(0, 8)
    : [];
  const add = (a: UniverseAccount) => {
    if (idSet.has(a.accountId)) return;
    onChange(`${value ? `${value}\n` : ''}${a.accountName}\t${a.accountId}`);
    setQ('');
  };
  const remove = (id: string) => {
    onChange(
      ids
        .filter((x) => x !== id)
        .map((x) => { const nm = nameById.get(x); return nm ? `${nm}\t${x}` : x; })
        .join('\n'),
    );
  };
  return (
    <div className="bc-book-pins">
      <div className="bc-book-pin-search">
        <Input placeholder="Search account by name to add…" value={q} onChange={(e) => setQ(e.target.value)} />
        {matches.length > 0 && (
          <div className="bc-pin-results">
            {matches.map((a) => (
              <button key={a.accountId} type="button" className="bc-pin-result" onClick={() => add(a)}>
                <span>{a.accountName}</span>
                <span className="bc-muted">{a.currentGs} · {money(a.arr)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <Textarea expandable={false} rows={2} placeholder="Or paste a pre-carved account list (Name + SFDC Account ID) to pin this exact book" value={value} onChange={(e) => onChange(e.target.value)} />
      {ids.length > 0 && (
        <>
          <div className="bc-pin-chips">
            {ids.map((id) => (
              <span key={id} className="bc-chip">
                {nameById.get(id) ?? id}
                <button type="button" onClick={() => remove(id)} aria-label="Remove pinned account">×</button>
              </span>
            ))}
          </div>
          <span className="bc-muted bc-book-pin-count">{ids.length} pinned to this book</span>
        </>
      )}
    </div>
  );
}

/** Per-rep account accordion body: detailed owned-account table with health + sentiment. */
function RepAccountList({ accounts }: { accounts: UniverseAccount[] }) {
  if (accounts.length === 0) return <p className="bc-muted bc-sub-empty">No accounts for this rep.</p>;
  const sorted = [...accounts].sort((a, b) => b.arr - a.arr);
  return (
    <div className="bc-table-scroll bc-rep-accts">
      <table className="bc-table bc-sub-table">
        <thead>
          <tr>
            <th>Account</th>
            <th>ARR</th>
            <th>Renewal</th>
            <th title="Expansion percentile">Exp %</th>
            <th title="Days with current owner">Days</th>
            <th>Health</th>
            <th>Sentiment</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((a) => (
            <tr key={a.accountId}>
              <td>{a.accountName}</td>
              <td>{money(a.arr)}</td>
              <td>{a.renewalQuarter ?? (a.daysToRenewal != null ? `${a.daysToRenewal}d` : '—')}</td>
              <td>{a.expansionPercentile ?? a.expansionScore ?? '—'}</td>
              <td>{a.daysCurrentOwner ?? '—'}</td>
              <td>
                {RYG_DOT(a.healthRating)}
                {a.healthDescription && <span className="bc-cell-note" title={a.healthDescription}> {a.healthDescription}</span>}
              </td>
              <td>
                {RYG_DOT(a.sentiment)}
                {a.sentimentReason && <span className="bc-cell-note" title={a.sentimentReason}> {a.sentimentReason}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Project when a new book reaches its fully-ramped target via new-business growth.
 * seeded = accounts carved now (pinned count else the book's seed target); full = the fully-ramped
 * target; perQuarter = expected new-biz accounts gained per quarter. Date = ramp + quarters-to-fill.
 */
function estimateFullBookLabel(
  book: NewBook,
  pinnedCount: number,
  fullTarget: number,
  perQuarter: number,
  projection?: ProjectionSettings | null,
  teamCtx?: { repBooks: TeamFillBook[]; otherBooks: TeamFillBook[] },
): string {
  const seeded = pinnedCount || book.targetCount || 0;
  if (fullTarget > 0 && seeded >= fullTarget) return 'Full at ramp';
  // Manual ramp override (homepage "release") wins over the startDate derivation.
  const rampIso = book.rampDate ?? rampDateFor(book.startDate);
  if (!rampIso) return 'add a start date to project';
  const rampMs = Date.parse(`${rampIso}T00:00:00Z`);
  const fmt = (ms: number) => new Date(ms).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
  // Monthly schedule (homepage Forecasting card) wins over the constant per-quarter rate.
  if (projection && projection.months.length > 0 && teamCtx) {
    const FOCAL = '__focal__';
    const startMs = Date.now();
    const sim = simulateTeamFill({
      projection,
      books: [
        { name: FOCAL, seed: seeded, target: fullTarget, rampMs, isNew: true, region: forecastRegionOf(book.region) },
        ...teamCtx.otherBooks,
        ...teamCtx.repBooks,
      ],
      startMs,
    });
    const mine = sim.byBook.get(FOCAL);
    if (!mine || mine.fillDay === null) return '> 10 yrs on current plan';
    return fmt(startMs + mine.fillDay * MS_DAY);
  }
  if (!(perQuarter > 0)) return 'set a growth rate';
  const quarters = (fullTarget - seeded) / perQuarter;
  return fmt(rampMs + quarters * 91.3 * MS_DAY);
}

function CarveConfigEditor({
  team,
  reps,
  allAccounts,
  mustKeepEntries,
  setMustKeepEntries,
  loadingReps,
  json,
  postJson,
  seedConfig,
  seedKey,
  mode,
  carveId,
  newBizStats,
  onChange,
  setError,
}: {
  team: 'egs' | 'sgs';
  reps: Rep[];
  allAccounts: UniverseAccount[];
  mustKeepEntries: MustKeepEntry[];
  setMustKeepEntries: React.Dispatch<React.SetStateAction<MustKeepEntry[]>>;
  loadingReps?: boolean;
  json: <T,>(p: string, i?: RequestInit & { timeoutMs?: number }) => Promise<T>;
  postJson: <T,>(p: string, b: unknown, t?: number) => Promise<T>;
  seedConfig: CarveConfig | null;
  seedKey: string;
  mode: 'create' | 'edit';
  carveId?: string;
  newBizStats?: NewBizStats | null;
  onChange: (patch: CarvePatch, dirty: boolean) => void;
  setError: (e: string | null) => void;
}) {
  const [st, setSt] = useState<EditorState>(() => seedEditorState(seedConfig, team, mode, allAccounts, mustKeepEntries, reps));
  const baselineRef = useRef<string | null>(null);
  const [pinSearch, setPinSearch] = useState('');
  const [pinTarget, setPinTarget] = useState('');
  const [showMustKeep, setShowMustKeep] = useState(false);
  const [stagedUnpins, setStagedUnpins] = useState<Set<string>>(new Set());
  const [savingMustKeep, setSavingMustKeep] = useState(false);
  const [expandedRep, setExpandedRep] = useState<string | null>(null);

  // Team projection settings (monthly new-account schedule + bias) for the full-book estimates.
  const [projection, setProjection] = useState<ProjectionSettings | null>(null);
  useEffect(() => {
    let cancelled = false;
    json<{ months: ProjectionMonth[]; newBookBias: number; targetAccounts?: number | null }>(`/projection-settings?team=${team}`)
      .then((d) => { if (!cancelled) setProjection({ months: d.months, newBookBias: d.newBookBias, targetAccounts: d.targetAccounts ?? null }); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [team, json]);

  // On create, the Forecasting card's Target Accounts seeds the fully-ramped target (ARR scales at
  // the same per-account rate). User edits win — this only applies while the field is untouched —
  // and saved configs (edit mode) always keep their own value.
  const targetTouched = useRef(false);
  const forecastTarget = projection?.targetAccounts ?? null;
  useEffect(() => {
    if (mode !== 'create' || targetTouched.current || !forecastTarget || forecastTarget <= 0) return;
    setSt((s) => {
      if (s.existingTargetCount === forecastTarget) return s;
      const perAcct = s.existingTargetCount > 0 ? s.existingTargetArr / s.existingTargetCount : (team === 'sgs' ? 210000 : 80000);
      return { ...s, existingTargetCount: forecastTarget, existingTargetArr: Math.round(forecastTarget * perAcct) };
    });
  }, [forecastTarget, mode, team]);

  // (Re)seed on seedKey change (team switch on create, Reset Rules / load on the carve page).
  useEffect(() => {
    setSt(seedEditorState(seedConfig, team, mode, allAccounts, mustKeepEntries, reps));
    baselineRef.current = null;
  }, [seedKey]);

  // Seed global must-keep entries as pins (active reps only), preserving session pins.
  useEffect(() => {
    const repNames = new Set(reps.map((r) => r.name));
    setSt((s) => {
      const session = s.extraPins.filter((p) => !p.global);
      const sessionIds = new Set(session.map((p) => p.accountId));
      const globals = mustKeepEntries
        .filter((e) => repNames.has(e.gs) && !sessionIds.has(e.accountId))
        .map((e) => ({ ...e, global: true }));
      return { ...s, extraPins: [...globals, ...session] };
    });
  }, [reps, mustKeepEntries]);

  // Emit the built patch + dirty flag whenever state changes.
  const builtConfig = useMemo(() => buildCarveConfig(st, reps, team), [st, reps, team]);
  const builtJson = useMemo(() => JSON.stringify(builtConfig), [builtConfig]);
  useEffect(() => {
    if (baselineRef.current == null) baselineRef.current = builtJson;
    onChange(builtConfig, builtJson !== baselineRef.current);
  }, [builtJson]);

  // Server-exact eligible-accounts count: debounced POST that reflects the full current ruleset
  // (ramp-aware locks, scope, rep settings) so managers can see the donor supply before running.
  const [elig, setElig] = useState<EligibilityStats | null>(null);
  const [eligLoading, setEligLoading] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setEligLoading(true);
    const t = setTimeout(() => {
      const body = { team, config: builtConfig, ...(carveId ? { carveId } : {}) };
      postJson<EligibilityStats>('/universe/eligibility', body)
        .then((r) => { if (!cancelled) setElig(r); })
        .catch(() => { if (!cancelled) setElig(null); })
        .finally(() => { if (!cancelled) setEligLoading(false); });
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [builtJson, team, carveId]);

  const patch = <K extends keyof EditorState>(key: K, value: EditorState[K]) => setSt((s) => ({ ...s, [key]: value }));
  const setScope = (v: EditorState['scope']) => patch('scope', v);
  const setLocks = (v: React.SetStateAction<LockConfig>) => setSt((s) => ({ ...s, locks: typeof v === 'function' ? (v as (p: LockConfig) => LockConfig)(s.locks) : v }));
  const setBalance = (v: React.SetStateAction<BalanceDims>) => setSt((s) => ({ ...s, balance: typeof v === 'function' ? (v as (p: BalanceDims) => BalanceDims)(s.balance) : v }));
  const updateBook = (i: number, p: Partial<NewBook>) =>
    setSt((s) => ({
      ...s,
      books: s.books.map((x, idx) => (idx === i ? { ...x, ...p } : x)),
    }));
  const setAction = (name: string, a: RepAction) => setSt((s) => ({ ...s, actions: { ...s.actions, [name]: a } }));
  const setRegion = (name: string, region: string) => setSt((s) => ({ ...s, regions: { ...s.regions, [name]: region } }));
  const setRepTarget = (name: string, p: { count?: number; arr?: number }) => setSt((s) => ({ ...s, repTargets: { ...s.repTargets, [name]: { ...s.repTargets[name], ...p } } }));

  const perAcct = team === 'sgs' ? 210000 : 80000;
  const perAcctTarget = st.existingTargetCount > 0 ? st.existingTargetArr / st.existingTargetCount : perAcct;
  const targetsDisabled = st.scope === 'newhire';
  const lockedTargetTip = 'Locked for New-Hire scope; Model automatically pulls from largest books to fill new hire books.';
  const actionOptions = repActionsForScope(st.scope);
  const managerCount = reps.filter((r) => r.isManager).length;
  const visibleReps = reps.filter((r) => st.includeManagers || !r.isManager);
  // Existing reps as tracked sim books so the overflow-handoff model knows when each crosses the
  // target and starts feeding pending books. Seeds are projected POST-carve counts: the optimizer
  // levels donors down from the top (largest first) until new-book real demand is met, so seeding
  // everyone at target would overstate handoff supply (the Leila-gets-10-handoffs bug). Frozen
  // reps keep their books, Empty Book reps go to zero, and seat-covered slots reduce the demand.
  // st.regions overrides the universe heuristic; any unknown region makes simulateTeamFill fall
  // back to a summed pool.
  const forecastRepSeeds: Map<string, number> = (() => {
    const counts = new Map<string, number>();
    const levelable: string[] = [];
    // Full targets as demand: the optimizer fills everything it can with reals first, and the
    // run derives seats only from what's left, so donor leveling assumes an all-real fill.
    let demand = st.books.reduce((sum, b) => sum + Math.max(0, b.targetCount || 0), 0);
    for (const r of visibleReps) {
      const action = st.actions[r.name] ?? 'rebalance';
      if (action === 'empty') {
        counts.set(r.name, 0);
        continue;
      }
      counts.set(r.name, r.accounts);
      if (action !== 'keep' && action !== 'keepsilent') levelable.push(r.name);
    }
    while (demand > 0 && levelable.length) {
      let best = '';
      let bestC = -1;
      for (const n of levelable) {
        const c = counts.get(n) ?? 0;
        if (c > bestC) {
          bestC = c;
          best = n;
        }
      }
      if (bestC <= 0) break;
      counts.set(best, bestC - 1);
      demand -= 1;
    }
    return counts;
  })();
  const forecastRepBooks: TeamFillBook[] = visibleReps.map((r) => ({
    name: `rep:${r.name}`,
    seed: forecastRepSeeds.get(r.name) ?? r.accounts,
    target: st.existingTargetCount || r.accounts,
    rampMs: null,
    isNew: false,
    region: forecastRegionOf(st.regions[r.name] ?? r.region),
  }));
  const repGridCols = ['minmax(120px, 1.4fr)', '64px', '92px', '1fr', '110px', '92px', '116px', '160px'].join(' ');

  // Exclude Accounts card (renders first in the book grid; stays visible once it has entries).
  const [excludeOpen, setExcludeOpen] = useState(false);
  const excludedIds = extractAccountIds(st.excludedText);

  const acctsByRep = useMemo(() => {
    const m = new Map<string, UniverseAccount[]>();
    for (const a of allAccounts) {
      const arr = m.get(a.currentGs) ?? [];
      arr.push(a);
      m.set(a.currentGs, arr);
    }
    return m;
  }, [allAccounts]);
  const nameById = useMemo(() => new Map(allAccounts.map((a) => [a.accountId, a.accountName])), [allAccounts]);
  const knownManagers = useMemo(
    () => [...new Set(reps.map((r) => r.manager).filter((m): m is string => !!m))].sort((x, y) => x.localeCompare(y)),
    [reps],
  );
  // SFDC user directory for the assignee autocomplete; an exact name match auto-fills the id.
  const [sfdcUsers, setSfdcUsers] = useState<{ name: string; userId: string }[]>([]);
  useEffect(() => {
    let cancelled = false;
    json<{ users: { name: string; userId: string }[] }>('/sfdc-users')
      .then((d) => { if (!cancelled) setSfdcUsers(d.users); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Shared Segment + Region Sorting list affecting this carve (read-only chips; managed on the homepage).
  const [overrideEntries, setOverrideEntries] = useState<AccountOverrideEntry[]>([]);
  useEffect(() => {
    let cancelled = false;
    json<{ entries: AccountOverrideEntry[] }>('/account-overrides')
      .then((d) => { if (!cancelled) setOverrideEntries(d.entries); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [json]);
  const overrideSummary = useMemo(() => {
    const segment = team === 'sgs' ? 'STRAT' : 'ENT';
    const ids = new Set(allAccounts.map((a) => a.accountId));
    return {
      transferIn: overrideEntries.filter((e) => e.targetSegment === segment).length,
      transferOut: overrideEntries.filter((e) => e.targetSegment && e.targetSegment !== segment && ids.has(e.accountId)).length,
      region: overrideEntries.filter((e) => e.region && (ids.has(e.accountId) || e.targetSegment === segment)).length,
    };
  }, [overrideEntries, allAccounts, team]);
  // Strict SFDC-user pick: an exact name match stores the user's id (and canonical casing); any
  // other text carries no id, and the input's onBlur clears unmatched text — so a book can never
  // hold an assignee name without its SFDC id (the Clay flow matches assignees by id only).
  const setAssignee = (i: number, name: string) => {
    const match = sfdcUsers.find((u) => u.name.trim().toLowerCase() === name.trim().toLowerCase());
    updateBook(i, match
      ? { assigneeName: match.name, assigneeSfdcUserId: match.userId }
      : { assigneeName: name, assigneeSfdcUserId: undefined });
  };

  const bookNames = st.books.map((b) => b.name.trim()).filter(Boolean);
  const pinnedIds = new Set(st.extraPins.map((p) => p.accountId));

  // Pin conflict detection: the same account pasted into two books (later book silently wins), or
  // pasted into a book while it also sits in the pin chips / global must-keep list (paste wins).
  // Both are flagged inline so a pin is never rebound without the user seeing it.
  const pinBooksById = useMemo(() => {
    const m = new Map<string, string[]>();
    st.books.forEach((b, i) => {
      const label = b.name.trim() || `Book ${i + 1}`;
      for (const id of extractAccountIds(b.pinnedText)) {
        const arr = m.get(id) ?? [];
        arr.push(label);
        m.set(id, arr);
      }
    });
    return m;
  }, [st.books]);
  const chipConflicts = useMemo(
    () => new Map(st.extraPins.filter((p) => pinBooksById.has(p.accountId)).map((p) => [p.accountId, pinBooksById.get(p.accountId)!])),
    [st.extraPins, pinBooksById],
  );
  const pinMatches = pinSearch.trim()
    ? allAccounts.filter((a) => !pinnedIds.has(a.accountId) && a.accountName.toLowerCase().includes(pinSearch.trim().toLowerCase())).slice(0, 8)
    : [];
  const addPin = (a: UniverseAccount) => {
    if (!pinTarget) return;
    setSt((s) => ({ ...s, extraPins: [...s.extraPins.filter((p) => p.accountId !== a.accountId), { accountId: a.accountId, accountName: a.accountName, gs: pinTarget }] }));
    setPinSearch('');
  };
  const removePin = (accountId: string) => setSt((s) => ({ ...s, extraPins: s.extraPins.filter((p) => p.accountId !== accountId) }));
  const repNameSet = new Set(reps.map((r) => r.name));
  const saveToMustKeep = async (pin: { accountId: string; accountName: string; gs: string }) => {
    if (!repNameSet.has(pin.gs)) return;
    try {
      await postJson('/must-keep', { team, accountId: pin.accountId, accountName: pin.accountName, gs: pin.gs });
      setMustKeepEntries((es) => [...es.filter((e) => e.accountId !== pin.accountId), { accountId: pin.accountId, accountName: pin.accountName, gs: pin.gs }]);
      setSt((s) => ({ ...s, extraPins: s.extraPins.map((p) => (p.accountId === pin.accountId ? { ...p, global: true } : p)) }));
    } catch (e) {
      setError(String(e));
    }
  };
  const toggleStagedUnpin = (accountId: string) =>
    setStagedUnpins((s) => {
      const next = new Set(s);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });
  const commitUnpins = async () => {
    if (stagedUnpins.size === 0) return;
    setSavingMustKeep(true);
    try {
      const ids = [...stagedUnpins];
      for (const accountId of ids) {
        await json('/must-keep', { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ team, accountId }) });
      }
      const removed = new Set(ids);
      setMustKeepEntries((es) => es.filter((e) => !removed.has(e.accountId)));
      setSt((s) => ({ ...s, extraPins: s.extraPins.filter((p) => !(p.global && removed.has(p.accountId))) }));
      setStagedUnpins(new Set());
    } catch (e) {
      setError(String(e));
    } finally {
      setSavingMustKeep(false);
    }
  };

  return (
    <>
      <div className="bc-setup-section">
        <div className="bc-row bc-spread bc-rules-head">
          <div className="bc-label bc-label-lg">Carve Rules</div>
          {(overrideSummary.transferIn > 0 || overrideSummary.transferOut > 0 || overrideSummary.region > 0) && (
            <span
              className="bc-elig-pill"
              title={'Shared Segment + Region Sorting list (managed on the homepage) — applied automatically at run time.\nIn: accounts transferring into this segment. Out: accounts in this universe transferring away. Region: manual region pins, binding even with Region Lock off — the account only lands on a matching-region book or rep.'}
            >
              Segment + Region Sorting: {overrideSummary.transferIn} in · {overrideSummary.transferOut} out · {overrideSummary.region} region
            </span>
          )}
          {(() => {
            const lines: string[] = [];
            if (elig) {
              if (elig.frontLoadActive) {
                lines.push(`In front-load window: ${elig.frontLoadNewCustomers} new + ${elig.frontLoadRenewedOrEstablished} renewed/established = ${elig.inFrontLoadWindow}`);
              }
              lines.push(
                `Locked → onboarding ${elig.locked.onboarding}, renewing ${elig.locked.renewing}, re-owned ${elig.locked.reOwned}, frozen ${elig.locked.frozen}, pinned ${elig.locked.pinned}, region ${elig.locked.region}`,
              );
            }
            const tip = elig ? lines.join('\n') : 'Movable donor pool under the full current ruleset (region + ramp-shifted renewal & onboarding).';
            const label = elig ? `Eligible accounts: ${elig.eligible} / ${elig.total}` : eligLoading ? 'Eligible accounts: …' : 'Eligible accounts: —';
            return <span className={`bc-elig-pill ${eligLoading ? 'bc-elig-loading' : ''}`} title={tip}>{label}</span>;
          })()}
        </div>
        <p className="bc-muted">The standing logic this carve runs on. Toggle a rule off or change a value to override it for this carve.</p>
        <RulesEditor
          scope={st.scope} setScope={setScope}
          cutoverDate={st.cutoverDate} setCutoverDate={(v) => patch('cutoverDate', v)}
          locks={st.locks} setLocks={setLocks}
          capEnabled={st.capEnabled} setCapEnabled={(v) => patch('capEnabled', v)}
          capPct={st.capPct} setCapPct={(v) => patch('capPct', v)}
          geoLock={st.geoLock} setGeoLock={(v) => patch('geoLock', v)}
          balance={st.balance} setBalance={setBalance}
          existingTargetCount={st.existingTargetCount} setExistingTargetCount={(v) => { targetTouched.current = true; patch('existingTargetCount', v); }}
          existingTargetArr={st.existingTargetArr} setExistingTargetArr={(v) => patch('existingTargetArr', v)}
          protectRedHealth={st.protectRedHealth} setProtectRedHealth={(v) => patch('protectRedHealth', v)}
          team={team}
        />
      </div>

      <div className="bc-divider" />

      <div className="bc-setup-section">
        <div className="bc-row bc-spread">
          <div className="bc-label bc-label-lg">New books to carve</div>
          <label
            className="bc-rule-check"
            title={'Reserve part of each book for expected new business: seats are month-named placeholders sized and paced from the homepage Forecasting schedule. Seats inside the 90-day aging window ("August New Customer 1") are filled by fresh arrivals held for the book; later seats ("November Handoff 1") are tenured accounts reps above target hand off at ramp. The rest of the target is carved from real donor accounts. Seats are written to the finalized table as open Expected New Business rows that the Clay routing flow ties to real arrivals.'}
          >
            <input
              type="checkbox"
              checked={st.includeDummyAccounts}
              onChange={(e) => patch('includeDummyAccounts', e.target.checked)}
            />
            <span>Reserve seats for expected new business</span>
          </label>
          {st.includeDummyAccounts && (
            <label className="bc-field bc-field-rate" title="Minimum REAL accounts each new book gets before the general fill — new-logo seats don't count, and seat sizing leaves this much room under the book's target. Anchors late books (whose demand mostly converts to seats) on some tenured, renewal-fit accounts.">
              <span className="bc-field-label">Min real accts / book</span>
              <input
                className="bc-num"
                type="number"
                min={0}
                max={10}
                step={1}
                value={st.renewalBallast}
                onChange={(e) => patch('renewalBallast', Math.max(0, Math.min(10, Math.round(Number(e.target.value) || 0))))}
              />
            </label>
          )}
          {projection && projection.months.length > 0 ? (
            <span
              className="bc-muted bc-proj-override-note"
              title="The homepage Forecasting card's monthly new-account schedule drives all fill projections for this team, so the per-carve quarterly rate is ignored."
            >
              Fill projections use the homepage Forecasting schedule
            </span>
          ) : (
            <label className="bc-field bc-field-rate" title="Expected new-business accounts a rep gains per quarter, used for the Estimated Full Book projection. Defaults to the trailing-12mo historical rate for this team.">
              <span className="bc-field-label">Est. new-biz accts / quarter</span>
              <input
                className="bc-num"
                type="number"
                min={0}
                step={0.5}
                value={st.estNewBizPerQuarter ?? (newBizStats?.perRepPerQuarter ?? 2)}
                onChange={(e) => patch('estNewBizPerQuarter', e.target.value === '' ? undefined : Number(e.target.value))}
              />
            </label>
          )}
        </div>
        <p className="bc-muted">Account count and ARR target per book (defaults from the existing-GS target above: {st.existingTargetCount} accts · {money(st.existingTargetArr)}). ARR auto-derives from the count unless you edit it. {st.geoLock ? 'Set a region to hard-restrict a book to accounts of that region.' : 'Each book is balanced toward its ARR target with spread renewals.'}{st.includeDummyAccounts ? ' Expected new-biz seats are derived by the run: real accounts are carved first, and any slots donors can\u2019t cover become supply-backed seats (held fresh arrivals + tenured handoffs from reps above target).' : ''}</p>
        <div className="bc-book-grid">
        {(excludeOpen || excludedIds.length > 0) && (
          <div className="bc-book bc-book-exclude">
            {excludedIds.length === 0 && (
              <button type="button" className="bc-book-remove" title="Close" aria-label="Close Exclude Accounts" onClick={() => setExcludeOpen(false)}>✕</button>
            )}
            <div className="bc-label">Exclude Accounts ({excludedIds.length})</div>
            <p className="bc-muted">Removed from this carve entirely — never donated, pinned, or counted (velocity books, protected books, …). Exclusion beats pins and must-keeps; conflicts are called out in the run's warnings.</p>
            <BookPinEditor value={st.excludedText} onChange={(t) => patch('excludedText', t)} accounts={allAccounts} nameById={nameById} />
            {(() => {
              const conflicts = excludedIds.filter((id) => (pinBooksById.get(id) ?? []).length > 0 || st.extraPins.some((p) => p.accountId === id));
              if (!conflicts.length) return null;
              return (
                <p className="bc-warn bc-pin-conflict">
                  ⚠ Also pinned elsewhere (exclusion wins): {conflicts.map((id) => nameById.get(id) ?? id).join(', ')}.
                </p>
              );
            })()}
          </div>
        )}
        {st.books.map((b, i) => {
          const pinned = extractAccountIds(b.pinnedText);
          return (
            <div key={i} className="bc-book">
              {st.books.length > 1 && (
                <button
                  type="button"
                  className="bc-book-remove"
                  aria-label={`Remove ${b.name.trim() || 'book'}`}
                  title="Remove this book"
                  onClick={() => setSt((s) => ({ ...s, books: s.books.filter((_, idx) => idx !== i) }))}
                >
                  ×
                </button>
              )}
              <div className="bc-book-fields">
                <label className="bc-field bc-field-name">
                  <span className="bc-field-label">Book / new owner name</span>
                  <Input placeholder="New owner / book name" value={b.name} onChange={(e) => updateBook(i, { name: e.target.value })} />
                </label>
                <label className="bc-field">
                  <span className="bc-field-label">Target starting accounts</span>
                  <input className="bc-num" type="number" min={Math.max(1, pinned.length)} value={b.targetCount} onChange={(e) => updateBook(i, { targetCount: Math.max(pinned.length, Number(e.target.value) || 1) })} title="Starting account count — pinned accounts seed the book, the rest is carved to reach this target. The book then grows to full size via new business over time." />
                </label>
                <label className="bc-field">
                  <span className="bc-field-label">Target ARR ($)</span>
                  <input className="bc-num bc-num-arr" type="number" min={0} step={10000} value={b.targetArr ?? Math.round(b.targetCount * perAcctTarget)} onChange={(e) => updateBook(i, { targetArr: Number(e.target.value) || 0 })} title="ARR target ($) — auto-derived from the account count; edit to override" />
                </label>
                <label className="bc-field">
                  <span className="bc-field-label">Start date</span>
                  <input className={`bc-date${b.startDate ? '' : ' bc-input-required'}`} type="date" value={b.startDate ?? ''} onChange={(e) => updateBook(i, { startDate: e.target.value })} title="Required to finalize — it drives the assignment date sent to Clay. Books arrive on the first Friday of the first full Mon–Fri week: starts on the 1st–14th get books the next month; the 15th–31st, the month after next. The renewal hold, onboarding hold, and front-load window are all measured from that date." />
                  {b.startDate && fiscalQuarterLabel(b.startDate) && <span className="bc-muted bc-fq">{fiscalQuarterLabel(b.startDate)}</span>}
                </label>
                <label className="bc-field">
                  <span className="bc-field-label">Books on</span>
                  <span className="bc-muted" title={b.rampDate ? 'Manually set from the homepage Finalized Books card (overrides the start-date derivation).' : 'Computed from the start date: first Friday of the first full Mon–Fri week, one month out for 1st–14th starts, two months out for 15th–31st starts.'}>
                    {b.rampDate ?? rampDateFor(b.startDate) ?? '—'}
                  </span>
                </label>
              </div>
              <div className="bc-book-fields">
                <label className="bc-field">
                  <span className="bc-field-label">Assignee (new hire)</span>
                  <input
                    className="bc-mgr-input"
                    list="bc-setup-assignee-list"
                    placeholder="Optional — SFDC user"
                    value={b.assigneeName ?? ''}
                    onChange={(e) => setAssignee(i, e.target.value)}
                    onBlur={() => { if ((b.assigneeName ?? '').trim() && !b.assigneeSfdcUserId) setAssignee(i, ''); }}
                    title="The real new hire receiving this book (the book name can stay a placeholder). Must match an SFDC user — their user id is stored and sent to Clay and the finalized-assignments table on finalize. Names that don't match are cleared."
                  />
                  {b.assigneeSfdcUserId && <span className="bc-muted bc-fq">✓ linked to SFDC</span>}
                </label>
                <label className="bc-field">
                  <span className="bc-field-label">Manager</span>
                  <input className="bc-mgr-input" list="bc-setup-mgr-list" placeholder="Optional" value={b.manager ?? ''} onChange={(e) => updateBook(i, { manager: e.target.value })} title="Optional GS manager this new book rolls up to (Book summary grouping). Free text; suggestions are existing managers." />
                </label>
                <label className="bc-field">
                  <span className="bc-field-label">Region</span>
                  <select
                    value={b.region ?? ''}
                    onChange={(e) => updateBook(i, { region: e.target.value })}
                    title={st.geoLock
                      ? 'Region (hard): this book only takes accounts whose recommended region matches'
                      : "Book's region label — stamped on the finalized rows (Clay's region matching for new hires) and used for the fill-forecast region split. Not an assignment restriction unless the geo lock is on."}
                  >
                    <option value="">{st.geoLock ? 'Any region' : 'No region'}</option>
                    {REGIONS.map((rg) => <option key={rg} value={rg}>{rg}</option>)}
                  </select>
                </label>
              </div>
              <BookPinEditor value={b.pinnedText} onChange={(t) => updateBook(i, { pinnedText: t })} accounts={allAccounts} nameById={nameById} />
              {(() => {
                const label = b.name.trim() || `Book ${i + 1}`;
                const dups = pinned.filter((id) => (pinBooksById.get(id) ?? []).length > 1);
                if (!dups.length) return null;
                return (
                  <p className="bc-warn bc-pin-conflict">
                    ⚠ Pasted into more than one book: {dups.map((id) => `${nameById.get(id) ?? id} (also ${(pinBooksById.get(id) ?? []).filter((n) => n !== label).join(', ') || 'this book'})`).join('; ')} — remove it from one, or the book listed later silently keeps it.
                  </p>
                );
              })()}
              {st.geoLock && b.name.trim() && !(b.region ?? '').trim() && (
                <p className="bc-warn bc-pin-conflict">
                  ⚠ Region Lock is on but this book has no region — without one it would take accounts from every region, so the run is blocked until a region is set.
                </p>
              )}
              <div className="bc-row bc-book-meta">
                <span className="bc-muted bc-fullbook-est" title={`Full book = ${st.existingTargetCount} accounts. ${projection && projection.months.length > 0 ? 'Projected from the ramp date and the monthly new-account schedule (homepage Forecasting card), split across active books.' : `Projected from the ramp date and ${(st.estNewBizPerQuarter ?? (newBizStats?.perRepPerQuarter ?? 2))} new-biz accounts/quarter.`}`}>
                  Estimated Full Book by <strong>{estimateFullBookLabel(b, pinned.length, st.existingTargetCount, st.estNewBizPerQuarter ?? (newBizStats?.perRepPerQuarter ?? 2), projection, { repBooks: forecastRepBooks, otherBooks: st.books.filter((_, j) => j !== i).map((bk, j) => { const iso = bk.rampDate ?? rampDateFor(bk.startDate); return { name: `draft-${j}`, seed: bk.targetCount || 0, target: st.existingTargetCount, rampMs: iso ? Date.parse(`${iso}T00:00:00Z`) : null, isNew: true, region: forecastRegionOf(bk.region) }; }) })}</strong>
                </span>
              </div>
            </div>
          );
        })}
        </div>
        <div className="bc-row">
          <Button variant="neutral" onClick={() => setSt((s) => ({ ...s, books: [...s.books, { name: '', targetCount: SEGMENT_TARGETS[team].startCount, pinnedText: '' }] }))}>+ Add book</Button>
          {!excludeOpen && excludedIds.length === 0 && (
            <Button variant="neutral" onClick={() => setExcludeOpen(true)} title="Remove specific accounts from this carve entirely (velocity books, protected books, …).">+ Exclude accounts</Button>
          )}
        </div>
        <datalist id="bc-setup-mgr-list">
          {knownManagers.map((mn) => <option key={mn} value={mn} />)}
        </datalist>
        <datalist id="bc-setup-assignee-list">
          {sfdcUsers.map((u) => <option key={u.userId} value={u.name} />)}
        </datalist>
      </div>

      <div className="bc-divider" />

      <div className="bc-setup-section">
        <div className="bc-label bc-label-lg">Must Keep Accounts</div>
        <p className="bc-muted">Search any account and lock it to an existing rep or a new book above.</p>
        <div className="bc-mustkeep">
          <button type="button" className="bc-mustkeep-toggle" onClick={() => setShowMustKeep((s) => !s)}>
            {showMustKeep ? '▾' : '▸'} Global must-keep list ({mustKeepEntries.length})
          </button>
          {showMustKeep && (
            <div className="bc-mustkeep-list">
              <p className="bc-muted">Saved account → rep relationships applied automatically to every new carve. Unpin and save to remove.</p>
              {mustKeepEntries.length === 0 ? (
                <p className="bc-muted">No accounts saved yet.</p>
              ) : (
                mustKeepEntries.map((e) => {
                  const staged = stagedUnpins.has(e.accountId);
                  return (
                    <div key={e.accountId} className={`bc-mustkeep-row ${staged ? 'bc-mustkeep-staged' : ''}`}>
                      <span>{e.accountName} → {e.gs}</span>
                      <button type="button" className="bc-link bc-link-danger" onClick={() => toggleStagedUnpin(e.accountId)}>{staged ? 'Undo' : 'Unpin'}</button>
                    </div>
                  );
                })
              )}
              {stagedUnpins.size > 0 && (
                <Button variant="neutral" onClick={() => { void commitUnpins(); }} disabled={savingMustKeep}>{savingMustKeep ? 'Saving…' : `Save (${stagedUnpins.size} removed)`}</Button>
              )}
            </div>
          )}
        </div>
        <div className="bc-row">
          <select value={pinTarget} onChange={(e) => setPinTarget(e.target.value)} title="Assign pinned account to">
            <option value="">Assign to…</option>
            {bookNames.length > 0 && (
              <optgroup label="New books">{bookNames.map((n) => <option key={`b-${n}`} value={n}>{n}</option>)}</optgroup>
            )}
            <optgroup label="Existing reps">{reps.map((r) => <option key={`r-${r.name}`} value={r.name}>{r.name}</option>)}</optgroup>
          </select>
          <Input placeholder={pinTarget ? 'Search account by name…' : 'Choose a GS first'} value={pinSearch} disabled={!pinTarget} onChange={(e) => setPinSearch(e.target.value)} />
        </div>
        {pinMatches.length > 0 && (
          <div className="bc-pin-results">
            {pinMatches.map((a) => (
              <button key={a.accountId} type="button" className="bc-pin-result" onClick={() => addPin(a)}>
                <span>{a.accountName}</span>
                <span className="bc-muted">{a.currentGs} · {money(a.arr)}</span>
              </button>
            ))}
          </div>
        )}
        {st.extraPins.length > 0 && (
          <div className="bc-pin-chips">
            {st.extraPins.map((p) => {
              const savable = repNameSet.has(p.gs);
              return (
                <span key={p.accountId} className={`bc-chip ${p.global ? 'bc-chip-global' : ''}`}>
                  {p.accountName} → {p.gs}
                  {chipConflicts.has(p.accountId) && (
                    <span
                      className="bc-chip-conflict"
                      title={`Also pasted into ${chipConflicts.get(p.accountId)!.join(', ')} — the book paste wins for this carve. Remove the paste (or this pin) to change that.`}
                    >
                      ⚠
                    </span>
                  )}
                  {p.global ? (
                    <span className="bc-chip-star" title="Saved to the global must-keep list">★</span>
                  ) : (
                    <button type="button" className="bc-chip-save" onClick={() => { void saveToMustKeep(p); }} disabled={!savable} title={savable ? 'Save to the global must-keep list' : 'Only existing-rep pins can be saved to the must-keep list'}>☆</button>
                  )}
                  <button type="button" onClick={() => removePin(p.accountId)} aria-label="Remove pin">×</button>
                </span>
              );
            })}
          </div>
        )}
      </div>

      <div className="bc-setup-section">
        <div className="bc-row bc-spread">
          <div className="bc-label bc-label-lg">{team.toUpperCase()} reps — confirm who participates</div>
          <label
            className="bc-rule-check bc-mgr-toggle"
            title={managerCount > 0
              ? "Managers end the carve owning nothing, and the accounts they own today are redistributed to the reps who report to them — they stay in the carve, they don't leave their team, and they never route back onto a manager. Uncheck to let managers hold and receive accounts like any other rep."
              : 'No managers detected in this universe (manager_ic_c = MGR). Refresh the universe if this looks wrong.'}
          >
            <input
              type="checkbox"
              checked={!st.includeManagers}
              disabled={managerCount === 0}
              onChange={(e) => patch('includeManagers', !e.target.checked)}
            />
            <span>Managers don&rsquo;t own accounts ({managerCount} detected)</span>
          </label>
        </div>
        {loadingReps ? (
          <p className="bc-muted">Loading reps…</p>
        ) : (
          <div className="bc-reps">
            <div className="bc-rep bc-rep-head" style={{ gridTemplateColumns: repGridCols }}>
              <span>Rep</span><span>Accts</span><span>ARR</span>
              <span>Manager</span>
              <span>Region {!st.geoLock && <Tip text="Optional — feeds the region badges, pills, and forecast splits. Only enforced as an assignment restriction when Region Lock is on." />}</span>
              <span>Target accts {targetsDisabled && <Tip icon="🔒" text={lockedTargetTip} />}</span>
              <span>Target ARR {targetsDisabled && <Tip icon="🔒" text={lockedTargetTip} />}</span>
              <span>Action</span>
            </div>
            {visibleReps.map((r) => {
              const expanded = expandedRep === r.name;
              return (
                <Fragment key={r.name}>
                  <div className={`bc-rep bc-rep-click ${(st.actions[r.name] ?? 'rebalance') !== 'rebalance' ? 'bc-rep-flagged' : ''} ${r.isManager ? 'bc-rep-mgr' : ''}`} style={{ gridTemplateColumns: repGridCols }}>
                    <span className="bc-rep-name" onClick={() => setExpandedRep(expanded ? null : r.name)}>
                      <span className="bc-rep-caret">{expanded ? '▾' : '▸'}</span> {r.name}{r.isManager && <span className="bc-rep-badge" title="Manager">MGR</span>}
                    </span>
                    <span>{r.accounts}</span>
                    <span>{money(r.arr)}</span>
                    <span className="bc-muted">{r.manager ?? '—'}</span>
                    <select value={st.regions[r.name] ?? r.region ?? ''} onChange={(e) => setRegion(r.name, e.target.value)}>
                      <option value="">No preference</option>
                      {REGIONS.map((rg) => <option key={rg} value={rg}>{rg}</option>)}
                    </select>
                    <input className="bc-num" type="number" min={0} value={st.repTargets[r.name]?.count ?? st.existingTargetCount} disabled={targetsDisabled} title="Desired fully-ramped book size (accounts). Defaults from the global target; edit to override this rep." onChange={(e) => setRepTarget(r.name, { count: Math.max(0, Number(e.target.value) || 0) })} />
                    <input className="bc-num bc-num-arr" disabled={targetsDisabled} title="Desired fully-ramped book ARR. Defaults from the global target; edit to override this rep." {...arrInputProps(st.repTargets[r.name]?.arr ?? st.existingTargetArr, (n) => setRepTarget(r.name, { arr: n }))} />
                    <select value={st.actions[r.name] ?? 'rebalance'} onChange={(e) => setAction(r.name, e.target.value as RepAction)}>
                      {actionOptions.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                    </select>
                  </div>
                  {expanded && (
                    <div className="bc-rep-accordion">
                      <RepAccountList accounts={acctsByRep.get(r.name) ?? []} />
                    </div>
                  )}
                </Fragment>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

// ────────────────────────────── New carve setup ──────────────────────────────

function NewCarveSetup({
  json,
  postJson,
  baseConfig,
  onCreated,
}: {
  json: <T,>(p: string, i?: RequestInit & { timeoutMs?: number }) => Promise<T>;
  postJson: <T,>(p: string, b: unknown, t?: number) => Promise<T>;
  baseConfig: CarveConfig | null;
  onCreated: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [team, setTeam] = useState<'egs' | 'sgs'>('egs');
  const [reps, setReps] = useState<Rep[]>([]);
  const [universe, setUniverse] = useState<{ accountCount: number; enrichmentFetchedAt: string } | null>(null);
  const [newBizStats, setNewBizStats] = useState<NewBizStats | null>(null);
  const [loadingReps, setLoadingReps] = useState(false);
  const [allAccounts, setAllAccounts] = useState<UniverseAccount[]>([]);
  const [mustKeepEntries, setMustKeepEntries] = useState<MustKeepEntry[]>([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The editor produces this config patch; create() posts it verbatim.
  const [pendingConfig, setPendingConfig] = useState<CarvePatch | null>(null);

  // Teams already self-healed (force-refreshed) this session, so we don't loop or re-query Snowflake.
  const refreshedTeams = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingReps(true);
    json<{ reps: Rep[]; accountCount: number; enrichmentFetchedAt: string; newBizStats: NewBizStats | null }>(`/universe?team=${team}`, { timeoutMs: 120_000 })
      .then(async (d) => {
        if (cancelled) return;
        const noManagers = d.reps.every((r) => !r.isManager);
        // 0 managers usually means a stale cached universe from before manager detection existed —
        // force one refresh per team to repopulate manager_ic_c, then re-read.
        if (noManagers && !refreshedTeams.current.has(team)) {
          refreshedTeams.current.add(team);
          try {
            await postJson('/universe/refresh', { team }, 90_000);
            const d2 = await json<{ reps: Rep[]; accountCount: number; enrichmentFetchedAt: string; newBizStats: NewBizStats | null }>(`/universe?team=${team}`, { timeoutMs: 120_000 });
            if (cancelled) return;
            setReps(d2.reps);
            setUniverse({ accountCount: d2.accountCount, enrichmentFetchedAt: d2.enrichmentFetchedAt });
            setNewBizStats(d2.newBizStats ?? null);
            return;
          } catch {
            /* fall through to the original result */
          }
        }
        setReps(d.reps);
        setUniverse({ accountCount: d.accountCount, enrichmentFetchedAt: d.enrichmentFetchedAt });
        setNewBizStats(d.newBizStats ?? null);
      })
      .catch((e) => { if (!cancelled) setError(String(e)); })
      .finally(() => { if (!cancelled) setLoadingReps(false); });
    return () => { cancelled = true; };
  }, [open, team, json, postJson]);

  useEffect(() => {
    if (!open) return;
    json<{ accounts: UniverseAccount[] }>(`/universe/accounts?team=${team}`, { timeoutMs: 120_000 })
      .then((d) => setAllAccounts(d.accounts))
      .catch(() => setAllAccounts([]));
    json<{ entries: MustKeepEntry[] }>(`/must-keep?team=${team}`, { timeoutMs: 30_000 })
      .then((d) => setMustKeepEntries(d.entries))
      .catch(() => setMustKeepEntries([]));
  }, [open, team, json]);

  // Kick a background universe refresh the moment the form opens (or the team flips) so the data
  // is near-live by the time "Create draft carve" is clicked. Server-side cooldown makes this cheap.
  useEffect(() => {
    if (!open) return;
    postJson('/universe/prefetch', { team }).catch(() => { /* warm cache still works */ });
  }, [open, team, postJson]);

  // Create + first run in one step: the run pulls the latest universe (first-run re-freeze) and the
  // carve opens showing results. If the run fails, the carve still exists — it lands in All Carves
  // as a draft with the error shown here, so nothing is lost and no duplicate is created on retry.
  const createdIdRef = useRef<string | null>(null);
  const create = async () => {
    if (!pendingConfig) return;
    setCreating(true);
    setError(null);
    try {
      if (!createdIdRef.current) {
        const r = await postJson<{ id: string }>('/carves', { title, team, config: pendingConfig }, 90_000);
        createdIdRef.current = r.id;
      }
      await postJson(`/carves/${createdIdRef.current}/run`, { config: pendingConfig, saveMode: 'overwrite' }, 180_000);
      onCreated(createdIdRef.current);
    } catch (e) {
      setError(`${String(e)}${createdIdRef.current ? ' — the carve was saved as a draft; fix the setup and try again (no duplicate will be created).' : ''}`);
    } finally {
      setCreating(false);
    }
  };

  if (!open) {
    return (
      <Card className="bc-card-clay" noPadding>
        <div className="bc-card-pad-tight">
          <div className="bc-row bc-spread">
            <div>
              <h2 className="bc-h2">Start a new carve</h2>
              <p className="bc-muted">Carve new books from eligible donor accounts (each donor loses ≤25% of their book). Choose the team, pick which reps participate, then create &amp; run — you can keep editing and re-running from the carve page (ReRun applies your changes without losing anything).</p>
            </div>
            <Button className="bc-cta" onClick={() => setOpen(true)}>+ New carve</Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="bc-card-pad">
        <h2 className="bc-h2">New carve</h2>
        <Input className="bc-title-input" placeholder="Carve title (e.g. Q3 new-hire seeding + full EGS rebalance)" value={title} onChange={(e) => setTitle(e.target.value)} />

        <div className="bc-setup-section">
          <div className="bc-label bc-label-lg">Team</div>
          <div className="bc-toggle">
            <button className={team === 'egs' ? 'bc-toggle-on' : ''} onClick={() => setTeam('egs')}>EGS members</button>
            <button className={team === 'sgs' ? 'bc-toggle-on' : ''} onClick={() => setTeam('sgs')}>SGS members</button>
          </div>
          {universe && <span className="bc-muted">{universe.accountCount} accounts · data as of {timeAgo(universe.enrichmentFetchedAt)} · refreshing in background</span>}
        </div>

        <CarveConfigEditor
          team={team}
          reps={reps}
          allAccounts={allAccounts}
          mustKeepEntries={mustKeepEntries}
          setMustKeepEntries={setMustKeepEntries}
          loadingReps={loadingReps}
          json={json}
          postJson={postJson}
          seedConfig={baseConfig}
          seedKey={`${team}:${baseConfig ? 'base' : 'none'}`}
          mode="create"
          newBizStats={newBizStats}
          onChange={(patch) => setPendingConfig(patch)}
          setError={setError}
        />

        {error && <p className="bc-error">{error}</p>}
        {(() => {
          const blockers = regionLockBlockers(pendingConfig);
          return (
            <div className="bc-row">
              <Button
                className="bc-cta"
                onClick={() => { void create(); }}
                disabled={creating || !pendingConfig || blockers.length > 0}
                title={blockers.length ? `Region Lock is on but these books have no region: ${blockers.join(', ')}` : 'Creates the carve and runs it on the latest account universe.'}
              >
                {creating ? 'Running…' : 'Create & run carve'}
              </Button>
              <Button variant="neutral" onClick={() => setOpen(false)}>Cancel</Button>
              {blockers.length > 0 && <span className="bc-warn">Set a region on: {blockers.join(', ')} (Region Lock is on).</span>}
            </div>
          );
        })()}
      </div>
    </Card>
  );
}

// ────────────────────────────── Carve view ──────────────────────────────

function CarveView({
  id,
  json,
  postJson,
  apiFetchRaw,
  onBack,
}: {
  id: string;
  json: <T,>(p: string, i?: RequestInit & { timeoutMs?: number }) => Promise<T>;
  postJson: <T,>(p: string, b: unknown, t?: number) => Promise<T>;
  apiFetchRaw: ReturnType<typeof useApi>['apiFetchRaw'];
  onBack: () => void;
}) {
  const [detail, setDetail] = useState<CarveDetail | null>(null);
  const [run, setRun] = useState<RunResult | null>(null);
  const [chatInput, setChatInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Chat edits default to Compare so a chat-driven rule change never silently destroys the current carve.
  const [chatSaveMode, setChatSaveMode] = useState<'overwrite' | 'compare'>('compare');
  const [chatLabel, setChatLabel] = useState('');
  const [activeTab, setActiveTab] = useState<string>('current');
  // Inline title rename (pencil edit on the header).
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const chatEndRef = useRef<HTMLDivElement>(null);
  // After a successful run, scroll up to the results. Flag + effect (not an inline call) because on
  // a draft's first run the results panel isn't in the DOM until React re-renders with the new run.
  const resultsRef = useRef<HTMLDivElement>(null);
  const scrollToResultsRef = useRef(false);
  // Live team reps (fresh /universe) — used to source manager data even when the carve's frozen
  // snapshot predates manager capture, so the Book summary manager grouping isn't silently empty.
  const [liveReps, setLiveReps] = useState<Rep[]>([]);
  // Team projection settings (monthly new-account schedule + bias) for the per-rep fill charts.
  const [projection, setProjection] = useState<ProjectionSettings | null>(null);
  const projTeam = detail?.team;
  useEffect(() => {
    if (!projTeam) return;
    let cancelled = false;
    json<{ months: ProjectionMonth[]; newBookBias: number }>(`/projection-settings?team=${projTeam}`)
      .then((d) => { if (!cancelled) setProjection({ months: d.months, newBookBias: d.newBookBias }); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [projTeam, json]);
  // Editable Build settings panel state.
  const [bsAccounts, setBsAccounts] = useState<UniverseAccount[]>([]);
  const [bsMustKeep, setBsMustKeep] = useState<MustKeepEntry[]>([]);
  const [bsSeed, setBsSeed] = useState<CarveConfig | null>(null);
  const [bsKey, setBsKey] = useState(0);
  const [bsPatch, setBsPatch] = useState<CarvePatch | null>(null);
  const [bsDirty, setBsDirty] = useState(false);
  const [bsLabel, setBsLabel] = useState('');
  // Variant comparison view.
  const [compareMode, setCompareMode] = useState(false);
  const [cmpA, setCmpA] = useState<string>('current');
  const [cmpB, setCmpB] = useState<string>('');
  // The setup panel opens automatically for drafts (never-run carves) — that phase is all setup.
  // User toggles are kept in state so re-renders don't fight the <details> element.
  const [setupOpen, setSetupOpen] = useState(false);
  useEffect(() => {
    if (detail && !detail.latestRun && (detail.variants?.length ?? 0) === 0) setSetupOpen(true);
  }, [detail?.id, detail?.latestRun]);
  const load = useCallback(() => {
    json<CarveDetail>(`/carves/${id}`, { timeoutMs: 30_000 })
      .then((d) => {
        setDetail(d);
        if (d.latestRun) setRun(d.latestRun.result);
      })
      .catch((e) => setError(String(e)));
  }, [json, id]);
  useEffect(load, [load]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [detail?.messages.length]);
  useEffect(() => {
    if (!scrollToResultsRef.current) return;
    scrollToResultsRef.current = false;
    resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [run, activeTab]);

  // Seed the Build settings editor from the carve's current config once detail arrives, and load
  // the team-scoped account universe + must-keep list it needs. Universe fetches can trigger a
  // cold-cache Snowflake refresh (~30–60s) after a manager-override / exclusion CRUD invalidates
  // the shared cache, so we run them with a generous 120s timeout instead of the default 15s.
  useEffect(() => {
    if (!detail) return;
    setBsSeed((prev) => prev ?? detail.currentConfig);
    json<{ accounts: UniverseAccount[] }>(`/universe/accounts?team=${detail.team}`, { timeoutMs: 120_000 }).then((d) => setBsAccounts(d.accounts)).catch(() => setBsAccounts([]));
    json<{ entries: MustKeepEntry[] }>(`/must-keep?team=${detail.team}`, { timeoutMs: 30_000 }).then((d) => setBsMustKeep(d.entries)).catch(() => setBsMustKeep([]));
    json<{ reps: Rep[] }>(`/universe?team=${detail.team}`, { timeoutMs: 120_000 }).then((d) => setLiveReps(d.reps)).catch(() => setLiveReps([]));
  }, [detail?.id]);

  const resetBuildSettings = () => {
    if (!detail) return;
    setBsSeed(detail.initialConfig);
    setBsKey((k) => k + 1);
  };
  const runEdited = async (mode: 'overwrite' | 'compare') => {
    if (!bsPatch || !detail) return;
    setBusy(true); setError(null);
    try {
      const body = mode === 'compare' ? { config: bsPatch, saveMode: 'compare', label: bsLabel.trim() || undefined } : { config: bsPatch, saveMode: 'overwrite' };
      // Generous timeout: a draft's first run re-freezes the universe from Snowflake first.
      const r = await postJson<{ result: RunResult; runId: string }>(`/carves/${id}/run`, body, 180_000);
      scrollToResultsRef.current = true;
      if (mode === 'compare') {
        setActiveTab(r.runId);
      } else {
        setRun(r.result);
        setActiveTab('current');
        // New current becomes the editor baseline so dirty resets.
        setBsSeed(bsPatch as unknown as CarveConfig);
        setBsKey((k) => k + 1);
      }
      load();
    } catch (e) { setError(String(e)); } finally { setBusy(false); }
  };

  // Top-level Re-run always overwrites the current result (Compare lives in chat + the editor panel).
  // Generous timeout: a draft's first run re-freezes the universe from Snowflake first.
  const runCarve = async () => {
    setBusy(true); setError(null);
    try {
      const r = await postJson<{ result: RunResult; runId: string }>(`/carves/${id}/run`, {}, 180_000);
      scrollToResultsRef.current = true;
      setRun(r.result); setActiveTab('current');
      load();
    } catch (e) { setError(String(e)); } finally { setBusy(false); }
  };

  // Refresh universe: re-fetch Snowflake enrichment/roster in-place on an already-run carve.
  // Skips creating a new carve just to pick up SFDC changes made during review (e.g. Justin Allen's
  // 4 account moves post-carve-creation). Post-refresh the operator can Re-run to apply the fresh
  // snapshot — the run-config-hash cache invalidates automatically via snapshot.enrichmentFetchedAt.
  const refreshUniverse = async () => {
    if (!window.confirm('Re-fetch the account universe from Snowflake for this carve? Any Re-runs after this will use the fresh snapshot.')) return;
    setBusy(true); setError(null);
    try {
      const r = await postJson<{
        accountCount: number;
        universeFetchedAt: string;
        previousAccountCount: number;
        previousUniverseFetchedAt: string | null;
        driftWarnings: string[];
      }>(`/carves/${id}/refresh-universe`, {}, 180_000);
      const delta = r.accountCount - r.previousAccountCount;
      const deltaStr = delta === 0 ? 'same' : `${delta > 0 ? '+' : ''}${delta}`;
      const summary =
        `Universe refreshed. ${r.accountCount} accounts (${deltaStr} vs previous ${r.previousAccountCount}). ` +
        (r.driftWarnings.length ? `Drift: ${r.driftWarnings.slice(0, 3).join(' | ')}${r.driftWarnings.length > 3 ? ` (+${r.driftWarnings.length - 3} more — see next Re-run warnings)` : ''}` : 'No ownership drift detected.') +
        `\n\nClick Re-run to apply the fresh snapshot.`;
      window.alert(summary);
      load();
    } catch (e) { setError(String(e)); } finally { setBusy(false); }
  };

  // Delete a saved variant: confirm, hit the API, then optimistically drop it and repair any state
  // that pointed at it (active tab + both compare selectors) before the background reload lands.
  const deleteVariant = async (variantId: string, label: string) => {
    if (!window.confirm(`Delete variant "${label || 'Untitled'}"? This can't be undone.`)) return;
    try {
      await json(`/carves/${id}/runs/${variantId}`, { method: 'DELETE' });
      setDetail((d) => (d ? { ...d, variants: (d.variants ?? []).filter((v) => v.id !== variantId) } : d));
      if (activeTab === variantId) setActiveTab('current');
      if (cmpA === variantId) setCmpA('current');
      if (cmpB === variantId) setCmpB('');
      load();
    } catch (e) { setError(String(e)); }
  };

  // No auto-run: a never-run carve is a shared draft — managers finish the setup (each team's
  // books, must-keep review) and someone explicitly runs it when everyone's in.

  const chatBody = () =>
    chatSaveMode === 'compare' ? { saveMode: 'compare', label: chatLabel.trim() || undefined } : { saveMode: 'overwrite' };

  const send = async () => {
    const message = chatInput.trim();
    if (!message) return;
    setChatInput(''); setBusy(true); setError(null);
    try {
      const r = await postJson<{ run: { result: RunResult; runId: string } | null }>(`/carves/${id}/chat`, { message, ...chatBody() });
      if (r.run) {
        scrollToResultsRef.current = true;
        if (chatSaveMode === 'compare') setActiveTab(r.run.runId);
        else { setRun(r.run.result); setActiveTab('current'); }
      }
      load();
    } catch (e) { setError(String(e)); } finally { setBusy(false); }
  };

  const saveLocal = async () => {
    await postJson(`/carves/${id}/save-local`, {}).then(() => setNotice('Saved as a local logic version.')).catch((e) => setError(String(e)));
  };
  // Rename the carve (pencil edit). No-op when unchanged/empty; finalized carves also get their
  // Snowflake CARVE_NAME synced server-side.
  const saveTitle = async () => {
    setEditingTitle(false);
    const t = titleDraft.trim();
    if (!detail || !t || t === (detail.title ?? '')) return;
    try {
      await postJson(`/carves/${id}/rename`, { title: t });
      setDetail((d) => (d ? { ...d, title: t } : d));
    } catch (e) {
      setError(String(e));
    }
  };
  // Persist pending setup edits (books, pins, rules) without running the optimizer; when the
  // carve is finalized, also refresh the finalized Snowflake rows in place (no webhook).
  const syncFinalizedMeta = async () => {
    await postJson(`/carves/${id}/sync-finalized-meta`, bsPatch ? { config: bsPatch } : {}, 120_000);
    setNotice(
      detail?.finalizedRunId
        ? 'Finalized table updated (start dates, assignees, managers).'
        : 'Carve setup saved — no run performed. Other managers now see these edits.',
    );
    // The saved config becomes the editor baseline so the dirty flag resets.
    if (bsPatch) {
      setBsSeed(bsPatch as unknown as CarveConfig);
      setBsKey((k) => k + 1);
    }
    load();
  };
  const propose = async () => {
    const rationale = window.prompt('Briefly, why should this become the base logic?') ?? '';
    await postJson(`/carves/${id}/propose`, { rationale })
      .then(() => setNotice('Proposed to base logic — now open for a vote on the home page.'))
      .catch((e) => setError(String(e)));
  };
  const exportCsv = async () => {
    const res = await apiFetchRaw(`${API}/carves/${id}/export`, { timeoutMs: 60_000 });
    if (!res.ok) { setError('No run to export yet.'); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `carve_${id}.csv`; a.click();
    URL.revokeObjectURL(url);
  };
  /** Pins in effect for this carve: the shared list vs this carve's own pins, and how each resolved. */
  const exportPinsCsv = async () => {
    const res = await apiFetchRaw(`${API}/carves/${id}/must-keep/export`, { timeoutMs: 60_000 });
    if (!res.ok) { setError('Could not export pins for this carve.'); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `must-keep_${id}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (!detail) return <div className="bc-loading">{error ?? 'Loading carve…'}</div>;

  const variants = detail.variants ?? [];
  // Draft = never run: the shared setup phase where managers add their teams' books before the first run.
  const isDraft = !run && !detail.latestRun && variants.length === 0;
  const activeVariant = activeTab === 'current' ? null : variants.find((v) => v.id === activeTab) ?? null;
  const displayedRun = activeVariant ? activeVariant.result : run;
  const m = displayedRun?.metrics;
  const runOptions: { id: string; label: string; result: RunResult }[] = [
    ...(run ? [{ id: 'current', label: 'Current', result: run }] : []),
    ...variants.map((v) => ({ id: v.id, label: v.label, result: v.result })),
  ];
  return (
    <div className="bc-carve">
      {/* Header: identity + navigation + current logic */}
      <Card className="bc-card-clay">
        <div className="bc-card-pad">
          <div className="bc-row bc-spread">
            <div className="bc-row bc-title-row">
              <div>
                {editingTitle ? (
                  <input
                    className="bc-title-edit-input"
                    value={titleDraft}
                    autoFocus
                    onChange={(e) => setTitleDraft(e.target.value)}
                    onBlur={() => { void saveTitle(); }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void saveTitle();
                      else if (e.key === 'Escape') setEditingTitle(false);
                    }}
                  />
                ) : (
                  <h2 className="bc-h2">
                    <span
                      className={`bc-seg-badge bc-seg-${detail.team}`}
                      title={detail.team === 'sgs'
                        ? 'Strategic Growth Segment — SGS reps, region-hard carve, ~10 accts / $2.1M target'
                        : 'Enterprise segment — EGS reps, ~20 accts / $1.6M target'}
                    >
                      {detail.team === 'sgs' ? 'Strategic (SGS)' : 'Enterprise (EGS)'}
                    </span>
                    {detail.title ?? 'Untitled carve'}
                    <button
                      type="button"
                      className="bc-title-edit"
                      title="Rename carve"
                      aria-label="Rename carve"
                      onClick={() => { setTitleDraft(detail.title ?? ''); setEditingTitle(true); }}
                    >
                      ✎
                    </button>
                  </h2>
                )}
                <p className="bc-muted">
                  {detail.accountCount} accounts · data as of {timeAgo(detail.universeFetchedAt)}
                </p>
              </div>
              <Button variant="neutral" onClick={onBack}>← All carves</Button>
            </div>
            <div className="bc-row">
              {/* Header run always applies the setup panel's edits (draft or not) — otherwise a
                  Re-run here would silently discard regions/books changed in the panel below. */}
              <Button
                variant={isDraft ? undefined : 'neutral'}
                className={isDraft ? 'bc-cta' : undefined}
                onClick={() => { void (bsPatch ? runEdited('overwrite') : runCarve()); }}
                disabled={busy || regionLockBlockers(bsPatch).length > 0}
                title={regionLockBlockers(bsPatch).length ? `Region Lock is on but these books have no region: ${regionLockBlockers(bsPatch).join(', ')}` : undefined}
              >
                {busy ? 'Running…' : isDraft ? 'Run carve' : 'Re-run'}
              </Button>
              {!isDraft && (
                <Button
                  variant="neutral"
                  onClick={() => { void refreshUniverse(); }}
                  disabled={busy}
                  title="Re-fetch the account universe from Snowflake. Use after SFDC changes made mid-review — the frozen snapshot won't otherwise pick them up on Re-run."
                >
                  Refresh universe
                </Button>
              )}
              <Button variant="neutral" onClick={() => { void exportCsv(); }} disabled={!run}>Export CSV</Button>
              <Button
                variant="neutral"
                onClick={() => { void exportPinsCsv(); }}
                title="Export every pin applying to this carve — the shared Must Keep list, this carve's own pins, and how each conflict resolved."
              >
                Export pins
              </Button>
            </div>
          </div>
          {isDraft && (
            <p className="bc-notice">
              Draft — this carve hasn't been run yet. Edit the setup below and Save without running; any manager can
              open it to add their team's books and review the Must Keep pins before someone runs it.
            </p>
          )}
          <details className="bc-base">
            <summary>Current logic</summary>
            <Markdown text={detail.currentSummaryMd} />
            <div className="bc-row">
              <Button variant="neutral" onClick={() => { void saveLocal(); }}>Save as local logic</Button>
              <Button variant="neutral" onClick={() => { void propose(); }}>Propose to base (vote)</Button>
            </div>
          </details>
          {(() => {
            // Carve Notes: the manual exceptions someone reading this carve needs to know about —
            // frozen (Keep Book) reps, emptied (donate-all) reps, and fully excluded accounts.
            const cfg = detail.currentConfig;
            const frozen = Object.entries(cfg.perRepOverrides ?? {}).filter(([, o]) => o?.frozen).map(([n]) => n).sort();
            const noHandoffs = Object.entries(cfg.perRepOverrides ?? {}).filter(([, o]) => o?.noHandoffs).map(([n]) => n).sort();
            const donateAll = Object.entries(cfg.perRepOverrides ?? {}).filter(([, o]) => o?.donateAll).map(([n]) => n).sort();
            const excluded = cfg.excludedAccountIds ?? [];
            if (!frozen.length && !noHandoffs.length && !donateAll.length && !excluded.length) return null;
            const nameById = new Map(bsAccounts.map((a) => [a.accountId, a.accountName]));
            return (
              <details className="bc-base bc-carve-notes">
                <summary>Carve Notes ({frozen.length + noHandoffs.length + donateAll.length + excluded.length})</summary>
                {frozen.length > 0 && (
                  <p><strong>Frozen books (Keep Book):</strong> {frozen.join(', ')} — no accounts moved in or out at carve time; once above target they still supply handoffs.</p>
                )}
                {noHandoffs.length > 0 && (
                  <p><strong>No handoff supply:</strong> {noHandoffs.join(', ')} — excluded from handoff-supply forecasts even when above target.</p>
                )}
                {donateAll.length > 0 && (
                  <p><strong>Emptied books (donate all):</strong> {donateAll.join(', ')} — every eligible account was donated.</p>
                )}
                {excluded.length > 0 && (
                  <p><strong>Excluded accounts ({excluded.length}):</strong> {excluded.map((eid) => nameById.get(eid) ?? eid).join(', ')} — removed from this carve entirely (never donated, pinned, or counted).</p>
                )}
              </details>
            );
          })()}
          {notice && <p className="bc-notice">{notice}</p>}
          {error && <p className="bc-error">{error}</p>}
        </div>
      </Card>

      {/* Carve Setup (drafts) / Rerun or Create Comparison — above the results so the setup is
          always in reach and a run lands you on the Book Summary right below it. */}
      <Card>
        <details
          className="bc-base bc-buildsettings"
          open={setupOpen}
          onToggle={(e) => setSetupOpen((e.currentTarget as HTMLDetailsElement).open)}
        >
          <summary>{isDraft ? 'Carve Setup' : 'Rerun or Create Comparison'}</summary>
          <div className="bc-card-pad">
            <p className="bc-muted">
              {isDraft
                ? 'Set up this carve\'s rules, books, pins, and reps. Save without running shares the draft with other managers; Run carve when the setup is complete.'
                : 'Edit this carve\'s rules, books, pins, and reps, then ReRun to overwrite or Compare to spin off a labeled variant.'}
            </p>
            {bsSeed && (
              <CarveConfigEditor
                team={detail.team}
                reps={detail.reps}
                allAccounts={bsAccounts}
                mustKeepEntries={bsMustKeep}
                setMustKeepEntries={setBsMustKeep}
                json={json}
                postJson={postJson}
                seedConfig={bsSeed}
                seedKey={`bs:${bsKey}`}
                mode="edit"
                carveId={detail.id}
                newBizStats={detail.newBizStats}
                onChange={(patch, dirty) => { setBsPatch(patch); setBsDirty(dirty); }}
                setError={setError}
              />
            )}
            <div className="bc-row bc-buildsettings-actions">
              <Button
                variant="neutral"
                className={bsDirty ? 'bc-reset-on' : ''}
                onClick={resetBuildSettings}
                title="Reset carve rules back to the initial rules for this carve set-up"
              >
                Reset Rules
              </Button>
              {!detail.finalizedRunId && (
                <Button
                  variant="neutral"
                  onClick={() => { setBusy(true); syncFinalizedMeta().catch((e) => setError(String(e))).finally(() => setBusy(false)); }}
                  disabled={busy || !bsDirty}
                  title="Persist books, pins, and rules to this carve without running the optimizer — other managers see your edits when they open it."
                >
                  Save without running
                </Button>
              )}
              {(() => {
                const blockers = regionLockBlockers(bsPatch);
                return (
                  <Button
                    className="bc-cta"
                    onClick={() => { void runEdited('overwrite'); }}
                    disabled={busy || !bsPatch || blockers.length > 0}
                    title={blockers.length ? `Region Lock is on but these books have no region: ${blockers.join(', ')}` : isDraft ? 'Run the carve on these settings (first run pulls the latest account universe).' : 'Overwrite existing carve and rules with the new rules.'}
                  >
                    {busy ? 'Running…' : isDraft ? 'Run carve' : 'ReRun'}
                  </Button>
                );
              })()}
              <div className="bc-row bc-compare-inline">
                <Input placeholder="Variant label" value={bsLabel} onChange={(e) => setBsLabel(e.target.value)} />
                <Button variant="neutral" onClick={() => { void runEdited('compare'); }} disabled={busy || !bsPatch} title="Generate a new Carve for comparison.">
                  Compare
                </Button>
              </div>
            </div>
          </div>
        </details>
      </Card>

      {/* Results anchor: runs scroll back up here once the new result renders. */}
      <div ref={resultsRef} />

      {/* Variant tabs + compare toggle */}
      {variants.length > 0 && (
        <div className="bc-row bc-spread bc-tabs-row">
          <div className="bc-tabs">
            <button className={`bc-tab${activeTab === 'current' ? ' bc-tab-on' : ''}`} onClick={() => setActiveTab('current')}>Current</button>
            {variants.map((v) => (
              <span key={v.id} className={`bc-tab bc-tab-variant${activeTab === v.id ? ' bc-tab-on' : ''}`}>
                <button className="bc-tab-label" onClick={() => setActiveTab(v.id)}>{v.label}</button>
                <button className="bc-tab-del" title="Delete this variant" aria-label="Delete variant" onClick={() => { void deleteVariant(v.id, v.label); }}>×</button>
              </span>
            ))}
          </div>
          {runOptions.length > 1 && (
            <Button
              variant={compareMode ? 'success' : 'neutral'}
              onClick={() => {
                setCompareMode((v) => !v);
                if (!compareMode) { setCmpA(runOptions[0].id); setCmpB(runOptions[1].id); }
              }}
            >
              {compareMode ? 'Close comparison' : 'Compare variants'}
            </Button>
          )}
        </div>
      )}

      {/* Results: comparison view, or the book summary for the active run */}
      {compareMode && runOptions.length > 1 ? (
        <CompareView
          options={runOptions}
          a={cmpA} b={cmpB}
          setA={setCmpA} setB={setCmpB}
          reps={detail.reps}
          liveReps={liveReps}
          config={detail.currentConfig}
        />
      ) : displayedRun && m ? (
        <>
        <CarveHandoffToggle
          carveId={id}
          runId={activeVariant ? activeVariant.id : (detail.latestRun?.id ?? null)}
          json={json}
        />
        <ResultsPanel
          run={displayedRun}
          carveId={id}
          postJson={postJson}
          onApplied={(result) => { setRun(result); setActiveTab('current'); load(); }}
          reps={detail.reps}
          liveReps={liveReps}
          config={detail.currentConfig}
          newBizStats={detail.newBizStats}
          projection={projection}
          team={detail.team}
          displayedRunId={activeVariant ? activeVariant.id : (detail.latestRun?.id ?? null)}
          isFinalized={!!detail.finalizedRunId}
          hasEdits={bsDirty}
          onFinalized={load}
          onSyncMeta={syncFinalizedMeta}
          onRerun={() => { void runCarve(); }}
        />
        </>
      ) : null}

      {/* Chat: tune the carve via natural language; defaults to Compare so edits spin off a
          variant. Hidden for now (SHOW_TUNE_CHAT) — the setup panel covers the same edits. */}
      {SHOW_TUNE_CHAT && (
      <Card>
        <div className="bc-card-pad bc-chat-pad">
          <div className="bc-row bc-spread">
            <h3 className="bc-h3">Tune the logic</h3>
            <div className="bc-savemode" title="Compare spins off a labeled variant; Overwrite replaces the current carve.">
              <label className="bc-rule-check">
                <input type="radio" name="bc-chat-savemode" checked={chatSaveMode === 'compare'} onChange={() => setChatSaveMode('compare')} />
                <span>Compare</span>
              </label>
              <label className="bc-rule-check">
                <input type="radio" name="bc-chat-savemode" checked={chatSaveMode === 'overwrite'} onChange={() => setChatSaveMode('overwrite')} />
                <span>Overwrite</span>
              </label>
              {chatSaveMode === 'compare' && (
                <Input placeholder="Variant label" value={chatLabel} onChange={(e) => setChatLabel(e.target.value)} />
              )}
            </div>
          </div>
          <div className="bc-messages">
            {detail.messages.length === 0 && (
              <p className="bc-muted">
                Tell me how to adjust the carve, e.g. “seed a 13-account book for Dana, ~$70k avg”, “don't give Jordan
                any new accounts”, or “drop the renewal lock to 60 days”. Then say “run it”.
              </p>
            )}
            {detail.messages.map((msg) => (
              <div key={msg.id} className={`bc-msg bc-msg-${msg.role}`}>
                <div className="bc-msg-content">{msg.content}</div>
                {msg.proposal && msg.proposal.diff.length > 0 && (
                  <details className="bc-msg-diff">
                    <summary>{msg.proposal.diff.length} rule change{msg.proposal.diff.length === 1 ? '' : 's'}</summary>
                    <DiffList diff={msg.proposal.diff} />
                  </details>
                )}
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
          <div className="bc-chat-input">
            <Textarea
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder="Describe a change, or say 'run it'…"
              rows={3}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void send(); }
              }}
            />
            <Button className="bc-cta" onClick={() => { void send(); }} disabled={busy || !chatInput.trim()}>{busy ? 'Thinking…' : 'Send'}</Button>
          </div>
        </div>
      </Card>
      )}

      {!compareMode && displayedRun && displayedRun.bookAlternates && Object.keys(displayedRun.bookAlternates).length > 0 && (
        <Card>
          <div className="bc-card-pad">
            <h3 className="bc-h3">Recommended alternates per new book</h3>
            <p className="bc-muted">
              Eligible runner-ups that meet this book's criteria (renewal window, locks; region preferred), ranked by fit — a bench to swap in.
            </p>
            {Object.entries(displayedRun.bookAlternates).map(([name, alts]) => (
              <AlternatesBlock key={name} name={name} alts={alts} hasRegion={alts.some((a) => a.region)} />
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

// ────────────────────────────── Participants & targets ──────────────────────────────

// ────────────────────────────── Results (combined + interactive) ──────────────────────────────

const prePost = (pre: number, post: number) => (
  <><span className="bc-muted">{pre}</span>→{post}</>
);

/** Calendar quarter (1-4) a renewal label ends in, e.g. "26Q3" → 3; 0 when missing/unparseable. */
function dispQ(label: string | null): number {
  if (!label) return 0;
  const m = label.trim().toUpperCase().match(/Q([1-4])$/);
  return m ? Number(m[1]) : 0;
}

interface BalRow {
  gs: string;
  isNewOwner: boolean;
  region: string | null;
  /** REAL accounts only — seats are excluded from both counts (they live in arrivalSeats/handoffSeats). */
  totalPre: number; totalPost: number;
  lost: number; added: number;
  arrPre: number; arrPost: number;
  onbPre: number; onbPost: number;
  qPre: number[]; qPost: number[];
  scorePre: number; scorePost: number;
  pctPre: number; pctPost: number;
  /** All seats (arrival + handoff) in the post book — at-ramp size = totalPost + dummyPost. */
  dummyPost: number;
  /** "X New Customer N" seats: fresh arrivals expected to land and be held for this book. */
  arrivalSeats: number;
  /** "X Handoff N" seats: tenured donations expected from over-target reps at ramp. */
  handoffSeats: number;
  /** Average per-component expansion value across the post book, keyed by component key. */
  comp: Record<string, number>;
  /** This rep's book is frozen in the run config (no accounts move in or out). */
  frozen: boolean;
  /** This rep is a manager (drives the Hide managers filter). */
  isManager: boolean;
}
interface RegionAgg { region: string; reps: number; accounts: number; arr: number; q: number[]; pct: number }
interface Balance {
  rows: BalRow[];
  accountsMoved: number;
  countSpread: number;
  arrSpreadK: number;
  regions: RegionAgg[];
  hasRegions: boolean;
  gsNames: string[];
  accountsByGs: Map<string, AssignmentRow[]>;
  /** Expansion-component keys present across the assignments (for the heatmapped detail columns). */
  componentKeys: string[];
  /** Out-of-segment owners of pulled-in accounts, hidden from the table (see BalanceOptions.segmentReps). */
  hiddenDonors: { gs: string; pulledIn: number }[];
}

interface BalanceOptions {
  managerNames?: Set<string>;
  frozenNames?: Set<string>;
  hideManagers?: boolean;
  hideUnchanged?: boolean;
  /** When set, existing-rep rows whose GS is NOT in this roster are dropped from every figure
   * (table, regions, spreads, move targets) and reported in `hiddenDonors`. These are other-segment
   * owners of pulled-in pinned / segment-moved accounts — e.g. an ENT rep donating one account to a
   * STRAT book. They still appear as the From / Prev GS on the receiving book's account list. */
  segmentReps?: Set<string>;
}

/** Recompute every per-rep / per-region figure from the raw assignments plus the manual-move overlay. */
function computeBalance(run: RunResult, moves: Map<string, string>, regionFallback?: Map<string, string>, opts?: BalanceOptions): Balance {
  const meta = new Map(run.perGsSummary.map((g) => [g.gs, { isNewOwner: g.isNewOwner, region: g.region }]));
  const managerNames = opts?.managerNames ?? new Set<string>();
  const frozenNames = opts?.frozenNames ?? new Set<string>();
  const eff = (a: AssignmentRow) => moves.get(a.accountId) ?? a.postGs;

  const names = new Set<string>();
  for (const g of run.perGsSummary) names.add(g.gs);
  for (const a of run.assignments) { names.add(a.preGs); names.add(eff(a)); }

  const componentKeys: string[] = [];
  const seenKeys = new Set<string>();
  for (const a of run.assignments) {
    for (const k of Object.keys(a.expansionComponents ?? {})) {
      if (!seenKeys.has(k)) { seenKeys.add(k); componentKeys.push(k); }
    }
  }

  interface Acc {
    totalPre: number; totalPost: number; lost: number; added: number;
    arrPre: number; arrPost: number; onbPre: number; onbPost: number;
    qPre: number[]; qPost: number[]; scorePre: number; scorePost: number;
    pctPreSum: number; pctPreN: number; pctPostSum: number; pctPostN: number;
    dummyPost: number; arrivalSeats: number; handoffSeats: number;
    compSum: Record<string, number>; compN: Record<string, number>;
  }
  const mk = (): Acc => ({
    totalPre: 0, totalPost: 0, lost: 0, added: 0, arrPre: 0, arrPost: 0, onbPre: 0, onbPost: 0,
    qPre: [0, 0, 0, 0], qPost: [0, 0, 0, 0], scorePre: 0, scorePost: 0,
    pctPreSum: 0, pctPreN: 0, pctPostSum: 0, pctPostN: 0,
    dummyPost: 0, arrivalSeats: 0, handoffSeats: 0, compSum: {}, compN: {},
  });
  const agg = new Map<string, Acc>();
  for (const n of names) if (n) agg.set(n, mk());
  const byGs = new Map<string, AssignmentRow[]>();

  for (const a of run.assignments) {
    const e = eff(a);
    const q = dispQ(a.renewalQuarter);
    const pre = agg.get(a.preGs);
    const post = agg.get(e);
    if (pre) {
      // Seats are excluded from the account COUNTS (they get their own columns) but still
      // contribute ARR / quarters / score so those balance views keep the at-ramp picture.
      if (!a.isDummy) pre.totalPre += 1;
      pre.arrPre += a.activeArr; pre.onbPre += a.onboarding ? 1 : 0;
      if (q) pre.qPre[q - 1] += 1;
      pre.scorePre += a.expansionScore ?? 0;
      if (a.expansionPercentile != null) { pre.pctPreSum += a.expansionPercentile; pre.pctPreN += 1; }
    }
    if (post) {
      if (!a.isDummy) post.totalPost += 1;
      post.arrPost += a.activeArr; post.onbPost += a.onboarding ? 1 : 0;
      if (q) post.qPost[q - 1] += 1;
      post.scorePost += a.expansionScore ?? 0;
      if (a.expansionPercentile != null) { post.pctPostSum += a.expansionPercentile; post.pctPostN += 1; }
      if (a.isDummy) {
        post.dummyPost += 1;
        if (/\bHandoff\b/i.test(a.accountName)) post.handoffSeats += 1;
        else post.arrivalSeats += 1;
      }
      for (const [k, v] of Object.entries(a.expansionComponents ?? {})) {
        if (typeof v === 'number' && Number.isFinite(v)) {
          post.compSum[k] = (post.compSum[k] ?? 0) + v;
          post.compN[k] = (post.compN[k] ?? 0) + 1;
        }
      }
    }
    if (a.preGs !== e) {
      if (pre) pre.lost += 1;
      if (post) post.added += 1;
      // Also list the moved account under its old rep (rendered greyed / "moved out") so it shows in both books.
      if (!byGs.has(a.preGs)) byGs.set(a.preGs, []);
      byGs.get(a.preGs)!.push(a);
    }
    if (!byGs.has(e)) byGs.set(e, []);
    byGs.get(e)!.push(a);
  }

  const allRows: BalRow[] = [...agg.entries()].map(([gs, g]) => {
    const comp: Record<string, number> = {};
    for (const k of componentKeys) comp[k] = g.compN[k] ? Math.round((g.compSum[k] / g.compN[k]) * 10) / 10 : 0;
    return {
      gs,
      isNewOwner: meta.get(gs)?.isNewOwner ?? false,
      region: meta.get(gs)?.region ?? regionFallback?.get(gs) ?? null,
      totalPre: g.totalPre, totalPost: g.totalPost, lost: g.lost, added: g.added,
      arrPre: Math.round(g.arrPre), arrPost: Math.round(g.arrPost),
      onbPre: g.onbPre, onbPost: g.onbPost, qPre: g.qPre, qPost: g.qPost,
      scorePre: Math.round(g.scorePre * 10) / 10, scorePost: Math.round(g.scorePost * 10) / 10,
      pctPre: g.pctPreN ? Math.round((g.pctPreSum / g.pctPreN) * 10) / 10 : 0,
      pctPost: g.pctPostN ? Math.round((g.pctPostSum / g.pctPostN) * 10) / 10 : 0,
      dummyPost: g.dummyPost,
      arrivalSeats: g.arrivalSeats,
      handoffSeats: g.handoffSeats,
      comp,
      frozen: frozenNames.has(gs),
      isManager: managerNames.has(gs),
    };
  });

  // Apply the view filters (managers / unchanged / emptied / out-of-segment) to a single visible
  // set so the table, region balance, spreads, score, and move-target list all stay consistent.
  const roster = opts?.segmentReps;
  const isForeignDonor = (r: BalRow): boolean =>
    !!roster && roster.size > 0 && !r.isNewOwner && !roster.has(r.gs) && r.gs !== 'Unassigned';
  const hiddenDonors = allRows
    .filter(isForeignDonor)
    .map((r) => ({ gs: r.gs, pulledIn: Math.max(r.totalPre, r.totalPost) }))
    .sort((a, b) => b.pulledIn - a.pulledIn || a.gs.localeCompare(b.gs));
  const isUnchangedOrEmpty = (r: BalRow) => !r.isNewOwner && ((r.lost === 0 && r.added === 0) || r.totalPost === 0);
  const rows: BalRow[] = allRows.filter((r) => {
    if (isForeignDonor(r)) return false;
    if (opts?.hideManagers && r.isManager) return false;
    if (opts?.hideUnchanged && isUnchangedOrEmpty(r)) return false;
    return true;
  });

  const spr = (vals: number[]) => (vals.length ? Math.max(...vals) - Math.min(...vals) : 0);
  const existing = rows.filter((r) => !r.isNewOwner);
  const accountsMoved = run.assignments.filter((a) => eff(a) !== a.preGs).length;
  const countSpread = spr(existing.map((r) => r.totalPost));
  const arrSpreadK = Math.round(spr(existing.map((r) => r.arrPost / 1000)) * 100) / 100;

  const hasRegions = rows.some((r) => r.region);
  const regMap = new Map<string, { reps: number; accounts: number; arr: number; q: number[]; pctSum: number }>();
  if (hasRegions) {
    for (const r of rows) {
      if (r.totalPost === 0 && r.totalPre === 0) continue;
      const key = r.region ?? 'No region';
      if (!regMap.has(key)) regMap.set(key, { reps: 0, accounts: 0, arr: 0, q: [0, 0, 0, 0], pctSum: 0 });
      const ra = regMap.get(key)!;
      ra.reps += 1; ra.accounts += r.totalPost; ra.arr += r.arrPost;
      for (let i = 0; i < 4; i++) ra.q[i] += r.qPost[i];
      ra.pctSum += r.pctPost * r.totalPost;
    }
  }
  const regions: RegionAgg[] = [...regMap.entries()]
    .map(([region, ra]) => ({
      region, reps: ra.reps, accounts: ra.accounts, arr: ra.arr, q: ra.q,
      pct: ra.accounts ? Math.round((ra.pctSum / ra.accounts) * 10) / 10 : 0,
    }))
    .sort((a, b) => a.region.localeCompare(b.region));

  // Move targets come from the visible set so hidden reps aren't offered as destinations.
  const gsNames = rows.map((r) => r.gs).filter((n) => n).sort();
  return { rows, accountsMoved, countSpread, arrSpreadK, regions, hasRegions, gsNames, accountsByGs: byGs, componentKeys, hiddenDonors };
}

/**
 * Overall Book Balance Score (0-100): for each ENABLED balance dimension, a "tightness" score over
 * the existing (non-new) reps — 100 when perfectly even, lower as the spread vs the mean widens —
 * then a weighted average using each dimension's importance. ARR uses a winsor-lite trim so a couple
 * of whales don't tank the score. Returns null when there's nothing to score.
 */
function bookBalanceScore(balance: Balance, dims: BalanceDims): number | null {
  const existing = balance.rows.filter((r) => !r.isNewOwner && (r.totalPost > 0 || r.totalPre > 0));
  if (existing.length < 2) return null;
  const tightness = (vals: number[]): number => {
    const mean = vals.reduce((s, v) => s + v, 0) / vals.length;
    if (mean <= 0) return 100;
    // Coefficient of variation → score; cv 0 = 100, cv >= 1 ≈ 0.
    const variance = vals.reduce((s, v) => s + (v - mean) ** 2, 0) / vals.length;
    const cv = Math.sqrt(variance) / mean;
    return Math.max(0, Math.min(100, Math.round((1 - cv) * 100)));
  };
  const quarterEvenness = (): number => {
    // How evenly each rep spreads their own renewals across the 4 quarters, averaged across reps.
    const per = existing.map((r) => {
      const tot = r.qPost.reduce((s, v) => s + v, 0);
      if (tot <= 0) return 100;
      const ideal = tot / 4;
      const dev = r.qPost.reduce((s, v) => s + Math.abs(v - ideal), 0) / (2 * tot); // 0..1
      return Math.max(0, Math.min(100, Math.round((1 - dev) * 100)));
    });
    return Math.round(per.reduce((s, v) => s + v, 0) / per.length);
  };

  const parts: { score: number; weight: number }[] = [];
  if (dims.accounts.on) parts.push({ score: tightness(existing.map((r) => r.totalPost)), weight: dims.accounts.importance });
  if (dims.arr.on) parts.push({ score: tightness(existing.map((r) => r.arrPost)), weight: dims.arr.importance });
  if (dims.renewal.on) parts.push({ score: quarterEvenness(), weight: dims.renewal.importance });
  if (dims.expansion.on) parts.push({ score: tightness(existing.map((r) => r.scorePost)), weight: dims.expansion.importance });
  const totalW = parts.reduce((s, p) => s + p.weight, 0);
  if (!parts.length || totalW <= 0) return null;
  return Math.round(parts.reduce((s, p) => s + p.score * p.weight, 0) / totalW);
}

/** Map a value within [min,max] to a teal heatmap background; higher = more saturated. */
function heatBg(value: number, min: number, max: number): string | undefined {
  if (!Number.isFinite(value) || max <= min) return undefined;
  const t = Math.max(0, Math.min(1, (value - min) / (max - min)));
  if (t <= 0) return undefined;
  return `rgba(45, 212, 191, ${(0.1 + t * 0.45).toFixed(3)})`;
}

/** Derive the four balance dimensions (on + importance) from a carve config's weights. */
function configBalanceDims(cfg: CarveConfig | null | undefined): BalanceDims {
  const w = cfg?.weights;
  const dim = (key: BalanceKey, coef: number | undefined): BalanceDim =>
    coef == null
      ? { on: true, importance: IMPORTANCE_DEFAULTS[key] }
      : { on: coef > 0, importance: coef > 0 ? coefToImportance(key, coef) : IMPORTANCE_DEFAULTS[key] };
  return {
    accounts: dim('accounts', w?.accounts),
    arr: dim('arr', w?.newOwnerArrBalanceK),
    renewal: dim('renewal', w?.newOwnerQuarterSpread),
    expansion: dim('expansion', w?.newOwnerExpansionK),
  };
}

/** Expansion Score v4 component key → short label, for the heatmapped per-account breakdown columns. */
const V4_COMPONENT_LABELS: Record<string, string> = {
  seatHeadroom: 'Seat headroom',
  empGrowthYoy: 'HC growth YoY',
  fundingScale: 'Funding / scale',
  pacing: 'Pacing',
  execEngagement: 'Exec engagement',
  healthModifier: 'Health modifier',
  peBacked: 'PE-backed (−)',
  multiYear: 'Multi-year (−)',
  contraction: 'Contraction (−)',
  saturation: 'Saturation (−)',
  recentCw: 'Renewed L3M (−)',
  cwL3_6m: 'Renewed L3-6M (−)',
  amendmentBonus: 'Amendment (+)',
  fundingEvent: 'Funding event (+)',
  teamBonus: 'Team penetration (+)',
  startupOverride: 'Startup override',
};

/** key → human label for the (fixed v4) expansion components shown in the results breakdown. */
function componentLabels(_cfg?: CarveConfig | null): Record<string, string> {
  return V4_COMPONENT_LABELS;
}

/** Finalize preflight drift payload (from POST /carves/:id/finalize/preflight). */
interface DriftConflict {
  accountId: string;
  accountName: string;
  kind: 'ownerChanged' | 'alreadyThere' | 'leftUniverse';
  runOwner: string;
  currentOwner: string | null;
  targetOwner: string;
  pinned: boolean;
  defaultAction: 'keep' | 'drop';
}
interface DriftPayload {
  clean: boolean;
  conflicts: DriftConflict[];
  unchangedDrifted: number;
  newArrivals: number;
}

function ResultsPanel({ run, carveId, postJson, onApplied, reps, liveReps, config, newBizStats, projection, team, displayedRunId, isFinalized, hasEdits, onFinalized, onSyncMeta, onRerun }: {
  run: RunResult;
  carveId: string;
  postJson: <T,>(p: string, b: unknown, t?: number) => Promise<T>;
  onApplied: (result: RunResult) => void;
  reps: Rep[];
  liveReps: Rep[];
  config: CarveConfig | null;
  newBizStats?: NewBizStats | null;
  projection?: ProjectionSettings | null;
  team: 'egs' | 'sgs';
  displayedRunId: string | null;
  isFinalized: boolean;
  /** True when the editor has unsaved book edits (drives the non-finalized Save button). */
  hasEdits: boolean;
  onFinalized: () => void;
  /** Save book edits (dates/assignees/managers) without a re-run; refreshes the finalized Snowflake rows in place when finalized. */
  onSyncMeta: () => Promise<void>;
  /** Escape hatch from the drift review modal: rebuild the whole result on fresh data instead. */
  onRerun: () => void;
}) {
  const [moves, setMoves] = useState<Map<string, string>>(new Map());
  const [applying, setApplying] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  // Drift review: the preflight found accounts whose owner changed since this run.
  const [drift, setDrift] = useState<DriftPayload | null>(null);
  const [driftActions, setDriftActions] = useState<Record<string, 'keep' | 'drop'>>({});
  const [err, setErr] = useState<string | null>(null);
  const [hideManagers, setHideManagers] = useState(false);
  const [hideUnchanged, setHideUnchanged] = useState(false);
  const [showMoved, setShowMoved] = useState(false);
  const [mgrFilter, setMgrFilter] = useState<string>('all');
  // Region pill filter: 'all' or a region key ('No region' groups the unlabeled rows).
  const [regionFilter, setRegionFilter] = useState<string>('all');
  // Local, instantly-applied manager edits (also persisted to the carve config via the API).
  const [managerEdits, setManagerEdits] = useState<Record<string, string>>({});
  // A fresh run (re-run / chat / apply) supersedes any in-flight what-if moves.
  useEffect(() => { setMoves(new Map()); }, [run]);

  // Display-time region fallback: older runs (and ENT carves) baked null regions — fall back to each
  // rep's inferred region so Region Balance + badges populate without forcing a re-run.
  const regionFallback = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of reps) if (r.region) m.set(r.name, r.region);
    return m;
  }, [reps]);

  // Manager data prefers live /universe reps, falling back to the carve's frozen snapshot reps, so the
  // grouping still populates when the snapshot predates manager capture.
  const managerNames = useMemo(() => {
    const s = new Set<string>();
    for (const r of reps) if (r.isManager) s.add(r.name);
    for (const r of liveReps) if (r.isManager) s.add(r.name);
    return s;
  }, [reps, liveReps]);
  const repManager = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of reps) if (r.manager) m.set(r.name, r.manager);
    for (const r of liveReps) if (r.manager) m.set(r.name, r.manager);
    for (const [n, mn] of Object.entries(config?.repManagers ?? {})) if (mn) m.set(n, mn);   // explicit config overrides
    for (const [n, mn] of Object.entries(managerEdits)) { if (mn) m.set(n, mn); else m.delete(n); } // live in-session edits
    return m;
  }, [reps, liveReps, config, managerEdits]);
  const mgrOf = (gs: string) => repManager.get(gs) ?? '—';
  const frozenNames = useMemo(() => {
    const s = new Set<string>();
    for (const [name, ov] of Object.entries(config?.perRepOverrides ?? {})) if (ov?.frozen) s.add(name);
    for (const n of config?.frozenReps ?? []) s.add(n);
    return s;
  }, [config]);
  // Segment roster (snapshot + live): existing-rep rows outside it are other-segment owners of
  // pulled-in accounts — hidden from the summary, listed in a note, still shown as Prev GS.
  const segmentReps = useMemo(() => {
    const s = new Set<string>();
    for (const r of reps) s.add(r.name);
    for (const r of liveReps) s.add(r.name);
    return s;
  }, [reps, liveReps]);

  const balance = useMemo(
    () => computeBalance(run, moves, regionFallback, { managerNames, frozenNames, hideManagers, hideUnchanged, segmentReps }),
    [run, moves, regionFallback, managerNames, frozenNames, hideManagers, hideUnchanged, segmentReps],
  );
  const compLabels = useMemo(() => componentLabels(config), [config]);
  const managerOptions = useMemo(
    () => [...new Set(balance.rows.map((r) => mgrOf(r.gs)))].sort((x, y) => x.localeCompare(y)),
    [balance, repManager],
  );
  // Roll the visible per-rep books up to each GS manager (across all teams, ignoring the Manager filter
  // so this stays a stable team overview). Respects the Hide managers / Hide unchanged toggles.
  const managerRows = useMemo(() => {
    const agg = new Map<string, { nReps: number; acPre: number; acPost: number; arPre: number; arPost: number }>();
    for (const r of balance.rows) {
      const mn = mgrOf(r.gs);
      const g = agg.get(mn) ?? { nReps: 0, acPre: 0, acPost: 0, arPre: 0, arPost: 0 };
      g.nReps += 1;
      g.acPre += r.totalPre; g.acPost += r.totalPost;
      g.arPre += r.arrPre; g.arPost += r.arrPost;
      agg.set(mn, g);
    }
    return [...agg.entries()].map(([mn, g]) => ({ mn, ...g })).sort((p, q) => p.mn.localeCompare(q.mn));
  }, [balance, repManager]);

  const setMove = (accountId: string, target: string, originalPost: string) => {
    setMoves((prev) => {
      const next = new Map(prev);
      if (target === originalPost) next.delete(accountId);
      else next.set(accountId, target);
      return next;
    });
  };

  // Assign/clear a rep's manager (display-only). Applies instantly, then persists to the carve config
  // without a re-run (manager doesn't affect the optimizer).
  const setManager = async (gs: string, manager: string) => {
    setManagerEdits((p) => ({ ...p, [gs]: manager.trim() }));
    try { await postJson(`/carves/${carveId}/rep-managers`, { name: gs, manager: manager.trim() }); }
    catch (e) { setErr(String(e)); }
  };
  // Suggestions for the manager editor: everything we already know maps to, sorted.
  const knownManagers = useMemo(
    () => [...new Set([...repManager.values()].filter(Boolean))].sort((x, y) => x.localeCompare(y)),
    [repManager],
  );

  const apply = async () => {
    setApplying(true); setErr(null);
    try {
      const r = await postJson<{ result: RunResult }>(
        `/carves/${carveId}/apply-moves`,
        { moves: [...moves.entries()].map(([accountId, gs]) => ({ accountId, gs })) },
        90_000,
      );
      onApplied(r.result);
      setMoves(new Map());
    } catch (e) { setErr(String(e)); } finally { setApplying(false); }
  };

  const teamLabel = team === 'sgs' ? 'Strat' : 'EGS';
  // Finalize this run as THE book for its segment: replaces the current finalized team book and
  // writes the per-account assignments to Snowflake for downstream reassignment.
  const finalize = async () => {
    if (!window.confirm(`Finalize as the ${teamLabel} book? This replaces the current finalized ${teamLabel} book and writes the assignments to Snowflake.`)) return;
    setFinalizing(true); setErr(null);
    try {
      // Drift preflight: compare this run against SFDC right now. Clean → finalize immediately;
      // conflicts → open the review modal (Keep/Drop per drifted account) instead.
      const pf = await postJson<DriftPayload>(`/carves/${carveId}/finalize/preflight`, { runId: displayedRunId }, 120_000);
      if (!pf.clean) {
        setDriftActions(Object.fromEntries(
          pf.conflicts.filter((c) => c.kind === 'ownerChanged').map((c) => [c.accountId, c.defaultAction]),
        ));
        setDrift(pf);
        return;
      }
      await postJson(`/carves/${carveId}/finalize`, { runId: displayedRunId }, 120_000);
      onFinalized();
    } catch (e) { setErr(String(e)); } finally { setFinalizing(false); }
  };
  // Confirm from the drift modal: finalize with the chosen per-account resolutions.
  const finalizeWithResolutions = async () => {
    setFinalizing(true); setErr(null);
    try {
      await postJson(`/carves/${carveId}/finalize`, {
        runId: displayedRunId,
        resolutions: Object.entries(driftActions).map(([accountId, action]) => ({ accountId, action })),
      }, 120_000);
      setDrift(null);
      onFinalized();
    } catch (e) { setErr(String(e)); } finally { setFinalizing(false); }
  };
  const unfinalize = async () => {
    if (!window.confirm(`Unfinalize this ${teamLabel} book? It will be removed from the finalized Snowflake table.`)) return;
    setFinalizing(true); setErr(null);
    try {
      await postJson(`/carves/${carveId}/unfinalize`, {}, 120_000);
      onFinalized();
    } catch (e) { setErr(String(e)); } finally { setFinalizing(false); }
  };

  return (
    <>
      {balance.hasRegions && (
        <Card className="bc-card-clay">
          <div className="bc-card-pad">
            <h3 className="bc-h3">Region balance</h3>
            <div className="bc-region-grid">
              {balance.regions.map((r) => (
                <div key={r.region} className="bc-region-cell">
                  <div className="bc-region-name">{r.region}</div>
                  <div className="bc-region-stats">
                    <span><strong>{r.accounts}</strong> accts · {r.reps} reps</span>
                    <span>{money(r.arr)}</span>
                    <span className="bc-muted">Q {r.q.join('/')}</span>
                    <span className="bc-muted">{r.pct}% exp</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      <Card className="bc-card-clay">
        <div className="bc-card-pad">
          <div className="bc-row bc-spread">
            <h3 className="bc-h3">Book summary</h3>
            <div className="bc-row bc-summary-filters">
              {balance.hasRegions && balance.regions.map((r) => (
                <button
                  key={r.region}
                  type="button"
                  className={`bc-region-mini${regionFilter === r.region ? ' bc-region-mini-on' : ''}`}
                  title={`${r.region}: ${r.reps} reps · ${r.accounts} accounts · ${money(r.arr)} post-carve ARR — click to filter the table`}
                  onClick={() => setRegionFilter((cur) => (cur === r.region ? 'all' : r.region))}
                >
                  <strong>{r.region}</strong> {r.reps} reps · {r.accounts} accts
                </button>
              ))}
              {managerOptions.length > 1 && (
                <label className="bc-cmp-pick">Manager
                  <select value={mgrFilter} onChange={(e) => setMgrFilter(e.target.value)}>
                    <option value="all">All managers</option>
                    {managerOptions.map((mn) => <option key={mn} value={mn}>{mn}</option>)}
                  </select>
                </label>
              )}
              {managerNames.size > 0 && (
                <label className="bc-toggle-inline" title="Hide manager rows from the table and region balance.">
                  <input type="checkbox" checked={hideManagers} onChange={(e) => setHideManagers(e.target.checked)} />
                  Hide managers ({managerNames.size})
                </label>
              )}
              <label className="bc-toggle-inline" title="Hide existing reps whose book didn't change or was carved to 0 accounts.">
                <input type="checkbox" checked={hideUnchanged} onChange={(e) => setHideUnchanged(e.target.checked)} />
                Hide unchanged / emptied
              </label>
            </div>
            {moves.size > 0 && (
              <div className="bc-row bc-moves-bar">
                <span className="bc-muted">{moves.size} unsaved move{moves.size === 1 ? '' : 's'}</span>
                <Button variant="neutral" onClick={() => setMoves(new Map())} disabled={applying}>Reset</Button>
                <Button className="bc-cta" onClick={() => { void apply(); }} disabled={applying}>{applying ? 'Applying…' : 'Apply moves'}</Button>
              </div>
            )}
          </div>
          <div className="bc-stats">
            <Stat
              label="Accounts moved"
              value={String(balance.accountsMoved)}
              active={showMoved}
              onClick={() => setShowMoved((v) => !v)}
            />
            <Stat label="Count spread (existing)" value={String(balance.countSpread)} />
            <Stat label="ARR spread (existing)" value={`$${Math.round(balance.arrSpreadK)}k`} />
            <Stat label="Run time" value={`${run.metrics.elapsedMs} ms`} />
          </div>
          <div className="bc-row bc-finalize-bar">
            {isFinalized ? (
              <>
                <span className="bc-finalized-badge">✓ Finalized {teamLabel} book</span>
                <Button
                  variant="neutral"
                  onClick={() => { setFinalizing(true); setErr(null); onSyncMeta().catch((e) => setErr(String(e))).finally(() => setFinalizing(false)); }}
                  disabled={finalizing}
                  title="Save book edits (start dates, assignees, managers) and refresh the finalized Snowflake rows in place. No webhook is sent and reassignment stamps survive. Renaming a book or changing accounts requires a full re-finalize."
                >
                  {finalizing ? 'Working…' : 'Update Finalized Table'}
                </Button>
                <Button variant="neutral" onClick={() => { void unfinalize(); }} disabled={finalizing}>
                  {finalizing ? 'Working…' : 'Unfinalize'}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="neutral"
                  onClick={() => { setFinalizing(true); setErr(null); onSyncMeta().catch((e) => setErr(String(e))).finally(() => setFinalizing(false)); }}
                  disabled={finalizing || !hasEdits}
                  title="Save book edits (start dates, assignees, managers) to the carve without re-running the optimizer, so a later finalize picks them up."
                >
                  {finalizing ? 'Working…' : 'Save book edits'}
                </Button>
                <Button className="bc-cta" onClick={() => { void finalize(); }} disabled={finalizing}>
                  {finalizing ? 'Finalizing…' : `Finalize as ${teamLabel} book`}
                </Button>
              </>
            )}
          </div>
          {err && <p className="bc-error">{err}</p>}
          {drift && (
            <div className="bc-drift-overlay" onClick={() => setDrift(null)}>
              <div className="bc-drift-modal" onClick={(e) => e.stopPropagation()}>
                <h3 className="bc-h3">Data changed since this run</h3>
                <p className="bc-muted">
                  SFDC moved underneath this result. Decide what happens to each drifted account — the rest of the
                  book stays exactly as reviewed.
                </p>
                {drift.conflicts.some((c) => c.kind === 'ownerChanged') && (
                  <>
                    <h4 className="bc-h4">Owner changed ({drift.conflicts.filter((c) => c.kind === 'ownerChanged').length})</h4>
                    <table className="bc-table bc-drift-table">
                      <thead>
                        <tr><th>Account</th><th>Owner at run</th><th>Owner now</th><th>Planned book</th><th>Resolution</th></tr>
                      </thead>
                      <tbody>
                        {drift.conflicts.filter((c) => c.kind === 'ownerChanged').map((c) => (
                          <tr key={c.accountId}>
                            <td>{c.accountName}{c.pinned ? <span className="bc-pin-flag" title="Pinned (must-keep or manual pin) in this carve."> 📌</span> : null}</td>
                            <td>{c.runOwner}</td>
                            <td>{c.currentOwner}</td>
                            <td>{c.targetOwner}</td>
                            <td>
                              <select
                                value={driftActions[c.accountId] ?? c.defaultAction}
                                onChange={(e) => setDriftActions((p) => ({ ...p, [c.accountId]: e.target.value as 'keep' | 'drop' }))}
                              >
                                <option value="keep">Keep move (from {c.currentOwner})</option>
                                <option value="drop">Drop (stays with {c.currentOwner})</option>
                              </select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
                {drift.conflicts.some((c) => c.kind === 'alreadyThere') && (
                  <>
                    <h4 className="bc-h4">Already moved in SFDC ({drift.conflicts.filter((c) => c.kind === 'alreadyThere').length})</h4>
                    <p className="bc-muted">Recorded as unchanged — no duplicate reassignment is sent.</p>
                    <ul className="bc-drift-list">
                      {drift.conflicts.filter((c) => c.kind === 'alreadyThere').map((c) => (
                        <li key={c.accountId}>{c.accountName} — already with {c.targetOwner}</li>
                      ))}
                    </ul>
                  </>
                )}
                {drift.conflicts.some((c) => c.kind === 'leftUniverse') && (
                  <>
                    <h4 className="bc-h4">Left the universe ({drift.conflicts.filter((c) => c.kind === 'leftUniverse').length})</h4>
                    <p className="bc-muted">No longer eligible (churned, re-tiered, or owner left) — dropped from the finalized book.</p>
                    <ul className="bc-drift-list">
                      {drift.conflicts.filter((c) => c.kind === 'leftUniverse').map((c) => (
                        <li key={c.accountId}>{c.accountName} — was going to {c.targetOwner}</li>
                      ))}
                    </ul>
                  </>
                )}
                {(drift.unchangedDrifted > 0 || drift.newArrivals > 0) && (
                  <p className="bc-muted">
                    Also: {drift.unchangedDrifted > 0 ? `${drift.unchangedDrifted} non-moving account${drift.unchangedDrifted === 1 ? '' : 's'} auto-corrected to their current owner` : ''}
                    {drift.unchangedDrifted > 0 && drift.newArrivals > 0 ? ' · ' : ''}
                    {drift.newArrivals > 0 ? `${drift.newArrivals} new account${drift.newArrivals === 1 ? '' : 's'} entered the universe since the run (untouched)` : ''}.
                  </p>
                )}
                <div className="bc-row bc-drift-actions">
                  <Button className="bc-cta" onClick={() => { void finalizeWithResolutions(); }} disabled={finalizing}>
                    {finalizing ? 'Finalizing…' : 'Finalize with resolutions'}
                  </Button>
                  <Button variant="neutral" onClick={() => { setDrift(null); onRerun(); }} disabled={finalizing} title="Discard these resolutions and rebuild the whole result on today's data instead.">
                    Re-run on fresh data
                  </Button>
                  <Button variant="neutral" onClick={() => setDrift(null)} disabled={finalizing}>Cancel</Button>
                </div>
              </div>
            </div>
          )}
          {run.warnings.length > 0 && (
            <details className="bc-run-details">
              <summary>⚠ Warnings ({run.warnings.length})</summary>
              <div className="bc-warnings">{run.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}</div>
            </details>
          )}
          {run.seedShortfall && run.seedShortfall.books.length > 0 && (() => {
            // Real vs placeholder split per book: "filled" counts expected-new-biz seat dummies too.
            const seatsByBook = new Map<string, number>();
            for (const a of run.assignments) {
              if (a.isDummy) seatsByBook.set(a.postGs, (seatsByBook.get(a.postGs) ?? 0) + 1);
            }
            return (
            <details className="bc-run-details bc-shortfall">
              <summary>Seeding shortfall — why books came up short ({run.seedShortfall.books.length} books)</summary>
              <p className="bc-muted">
                {run.seedShortfall.filled} of {run.seedShortfall.demand} demanded accounts were placed.{' '}
                {run.seedShortfall.donorsAtCap} of {run.seedShortfall.donors} donors hit their movement cap
                ({run.seedShortfall.capRemaining} cap capacity left, re-owned lock at {run.seedShortfall.usedOwnedRecentlyDays}d).
                Each cell counts the not-carved accounts blocked from that book for that reason.
                Filled shows real accounts + expected-new-biz seats separately; &quot;→ Seats&quot; = unfilled slots
                converted into seats the Clay flow fills with real arrivals before ramp. A book still short after
                that needs more supply in this run — loosen the movement cap or add donors.
              </p>
              <div className="bc-table-scroll">
                <table className="bc-table bc-shortfall-table">
                  <thead>
                    <tr>
                      <th className="bc-sticky-col">Book</th>
                      <th title="Real accounts + expected-new-biz seats / target">Filled</th>
                      <th title="Ramp date — earlier-ramping books claim scarce supply first">Ramp</th>
                      <th title="Pinned elsewhere (a book paste or must-keep pin)">Pinned</th>
                      <th title="Owner has Keep Book (frozen)">Frozen</th>
                      <th title="Changed owner too recently (re-owned lock)">Re-owned</th>
                      <th title="Renewing within the hold window measured at this book's ramp date">Renewing</th>
                      <th title="Still onboarding at this book's ramp date">Onboarding</th>
                      <th title="Region-gated away from this book (Region Lock or an explicit region pin)">Region</th>
                      <th title="Eligible, but every holder exhausted their movement cap">Donor capped</th>
                      <th title="Unfilled slots converted into expected-new-business seats (filled by forecast arrivals via the Clay flow before ramp)">→ Seats</th>
                    </tr>
                  </thead>
                  <tbody>
                    {run.seedShortfall.books.map((b) => {
                      const seats = seatsByBook.get(b.name) ?? 0;
                      const real = Math.max(0, b.filled - seats);
                      return (
                      <tr key={b.name}>
                        <td className="bc-sticky-col">{b.name}</td>
                        <td className={b.filled < b.target ? 'bc-shortfall-gap' : ''} title={seats > 0 ? `${real} real accounts + ${seats} expected-new-biz seats of ${b.target}` : undefined}>
                          {seats > 0 ? `${real} + ${seats} seats / ${b.target}` : `${b.filled}/${b.target}`}
                        </td>
                        <td>{b.rampDate}</td>
                        <td>{b.pinned || ''}</td>
                        <td>{b.frozen || ''}</td>
                        <td>{b.reOwned || ''}</td>
                        <td>{b.renewing || ''}</td>
                        <td>{b.onboarding || ''}</td>
                        <td>{b.regionMismatch || ''}</td>
                        <td>{b.donorCapped || ''}</td>
                        <td>
                          {b.convertedToSeats
                            ? b.convertedToSeats
                            : b.filled < b.target && seatWindowClosed(b.rampDate)
                              ? <span className="bc-no-window">No available accounts (eligible customers filled earlier books; too soon for future customers to finish onboarding)</span>
                              : ''}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </details>
            );
          })()}
          <p className="bc-muted">
            New books are pinned to the top; the rep name stays fixed as you scroll right. Click a rep to see
            their accounts and move one to another rep — the table, region balance, and score update live.
            Use <strong>+ components</strong> to reveal the heatmapped expansion-score breakdown.
          </p>
          {showMoved && (
            <MovedAccountsTable run={run} moves={moves} setMove={setMove} gsNames={balance.gsNames} />
          )}
          {balance.hiddenDonors.length > 0 && (
            <p className="bc-muted">
              {balance.hiddenDonors.length} out-of-segment donor{balance.hiddenDonors.length === 1 ? '' : 's'} hidden
              ({balance.hiddenDonors.reduce((s, d) => s + d.pulledIn, 0)} pulled-in account{balance.hiddenDonors.reduce((s, d) => s + d.pulledIn, 0) === 1 ? '' : 's'}) —
              they appear as the From rep on the receiving books: {balance.hiddenDonors.map((d) => d.gs).join(', ')}.
            </p>
          )}
          <ResultsTable balance={balance} moves={moves} setMove={setMove} compLabels={compLabels} config={config} newBizStats={newBizStats} projection={projection} repManager={repManager} mgrFilter={mgrFilter} regionFilter={regionFilter} setManager={(gs, m) => { void setManager(gs, m); }} knownManagers={knownManagers} />
          {managerOptions.length > 1 && (
            <>
              <h4 className="bc-h4">By manager team</h4>
              <div className="bc-table-scroll">
                <table className="bc-table bc-book-summary">
                  <thead>
                    <tr>
                      <th className="bc-sticky-col">Manager</th>
                      <th title="Reps reporting to this manager in the visible set">Reps</th>
                      <th>Accts</th>
                      <th title="Net accounts (post − pre)">Net</th>
                      <th>ARR</th>
                      <th title="Net ARR (post − pre)">Net ARR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {managerRows.map((g) => (
                      <tr key={g.mn}>
                        <td className="bc-sticky-col">{g.mn}</td>
                        <td>{g.nReps}</td>
                        <td>{g.acPost}</td>
                        <td>{deltaSpan(g.acPost - g.acPre, String)}</td>
                        <td>{money(g.arPost)}</td>
                        <td>{deltaSpan(g.arPost - g.arPre, money)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </Card>
    </>
  );
}

const deltaSpan = (n: number, fmt: (x: number) => string) => (
  <span className={n < 0 ? 'bc-neg' : n > 0 ? 'bc-pos' : ''}>{n > 0 ? '+' : ''}{fmt(n)}</span>
);

const QUARTER_LABELS = ['Q1', 'Q2', 'Q3', 'Q4'] as const;

/** Clean per-rep Book Summary: sticky rep column, net columns, heatmapped quarters + expansion components. */
/** Inline, unobtrusive manager editor: shows the current manager as text, edits in place on focus. */
function ManagerCell({ value, onCommit, listId }: { value: string; onCommit: (v: string) => void; listId: string }) {
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);
  return (
    <input
      className="bc-mgr-cell"
      list={listId}
      value={v}
      placeholder="—"
      title="GS manager (Book summary grouping). Type to assign; clear to unassign."
      onChange={(e) => setV(e.target.value)}
      onBlur={() => { if (v.trim() !== value.trim()) onCommit(v); }}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
    />
  );
}

function ResultsTable({ balance, moves, setMove, compLabels, config, newBizStats, projection, repManager, mgrFilter, regionFilter, setManager, knownManagers }: {
  balance: Balance;
  moves: Map<string, string>;
  setMove: (accountId: string, target: string, originalPost: string) => void;
  compLabels: Record<string, string>;
  config: CarveConfig | null;
  newBizStats?: NewBizStats | null;
  projection?: ProjectionSettings | null;
  repManager: Map<string, string>;
  mgrFilter: string;
  /** 'all' or a region key ('No region' groups the unlabeled rows) — set by the summary pills. */
  regionFilter: string;
  setManager: (gs: string, manager: string) => void;
  knownManagers: string[];
}) {
  const mgrOf = (gs: string) => repManager.get(gs) ?? '—';
  const [sortKey, setSortKey] = useState('deltaArr');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [showComps, setShowComps] = useState(false);
  // All books for the joint conserved fill simulation in the expanded-row charts.
  // The chart's seed must include seats (totalPost is real-only) — seats sit "inside" the seed there.
  const teamBooks = useMemo(
    () => teamBooksFrom(balance.rows.map((r) => ({ ...r, totalPost: r.totalPost + r.dummyPost, seats: r.dummyPost })), config),
    [balance, config],
  );
  const hasRegion = balance.hasRegions;
  const componentKeys = balance.componentKeys;

  const toggle = (gs: string) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(gs)) next.delete(gs); else next.add(gs);
    return next;
  });

  const sortVals: Record<string, (g: BalRow) => number> = {
    totalPost: (g) => g.totalPost,
    deltaTotal: (g) => g.totalPost - g.totalPre,
    arrPost: (g) => g.arrPost,
    deltaArr: (g) => g.arrPost - g.arrPre,
    arrivalSeats: (g) => g.arrivalSeats,
    handoffSeats: (g) => g.handoffSeats,
    scorePost: (g) => g.scorePost,
  };
  const visibleRows = balance.rows.filter((r) =>
    (mgrFilter === 'all' || mgrOf(r.gs) === mgrFilter) &&
    (regionFilter === 'all' || (r.region ?? 'No region') === regionFilter));
  // New-book dates: manual ramp override wins over the startDate derivation (same layering as everywhere).
  const datesByBook = useMemo(() => {
    const m = new Map<string, { start: string | null; ramp: string | null }>();
    for (const o of config?.newOwners ?? []) {
      m.set(o.name, { start: o.startDate ?? null, ramp: o.rampDate ?? rampDateFor(o.startDate) ?? null });
    }
    return m;
  }, [config]);
  const sorted = [...visibleRows].sort((a, b) => {
    if (a.isNewOwner !== b.isNewOwner) return a.isNewOwner ? -1 : 1; // new books pinned top
    if (a.isNewOwner && b.isNewOwner) {
      // New books order by ramp date (earliest first, unknown ramp last), name tie-break —
      // regardless of the column sort, which still governs the existing reps below.
      const ra = datesByBook.get(a.gs)?.ramp ?? '9999-12-31';
      const rb = datesByBook.get(b.gs)?.ramp ?? '9999-12-31';
      if (ra !== rb) return ra < rb ? -1 : 1;
      return a.gs.localeCompare(b.gs);
    }
    if (sortKey === 'gs' || sortKey === 'manager') {
      const cmp = sortKey === 'manager' ? mgrOf(a.gs).localeCompare(mgrOf(b.gs)) : a.gs.localeCompare(b.gs);
      return sortDir === 'asc' ? cmp : -cmp;
    }
    const sv = sortVals[sortKey] ?? sortVals.deltaArr;
    const cmp = sv(a) - sv(b);
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const onSort = (key: string) => {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'gs' || key === 'manager' ? 'asc' : 'desc'); }
  };
  const sortArrow = (key: string) => (key === sortKey ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  // Heatmap ranges (per quarter, per component) across all rows.
  const qRange = QUARTER_LABELS.map((_, i) => {
    const vals = balance.rows.map((r) => r.qPost[i]);
    return [Math.min(...vals), Math.max(...vals)] as const;
  });
  const compRange: Record<string, readonly [number, number]> = {};
  for (const k of componentKeys) {
    const vals = balance.rows.map((r) => r.comp[k] ?? 0);
    compRange[k] = [Math.min(...vals), Math.max(...vals)];
  }

  const fixedCols = 7 + QUARTER_LABELS.length + 2 + 1; // GS + Manager + accts + Δ + ARR + ΔARR + (Q*) + new logos + handoffs + exp
  const colSpan = fixedCols + (showComps ? componentKeys.length : 0);

  const allExpanded = balance.rows.length > 0 && balance.rows.every((r) => expanded.has(r.gs));

  return (
    <div className="bc-table-scroll">
      <datalist id="bc-results-mgr-list">
        {knownManagers.map((mn) => <option key={mn} value={mn} />)}
      </datalist>
      <div className="bc-row bc-table-controls">
        <button
          type="button"
          className="bc-pill-toggle"
          onClick={() => setExpanded(allExpanded ? new Set() : new Set(balance.rows.map((r) => r.gs)))}
        >
          {allExpanded ? 'Collapse all' : 'Expand all'}
        </button>
        {componentKeys.length > 0 && (
          <button type="button" className="bc-pill-toggle" onClick={() => setShowComps((v) => !v)}>
            {showComps ? '− components' : '+ components'}
          </button>
        )}
      </div>
      <table className="bc-table bc-book-summary">
        <thead>
          <tr>
            <th className="bc-sticky-col" onClick={() => onSort('gs')}>GS{sortArrow('gs')}</th>
            <th onClick={() => onSort('manager')} title="GS manager this rep reports to">Manager{sortArrow('manager')}</th>
            <th onClick={() => onSort('totalPost')} title="Real accounts only. At-ramp size = Accts + New Logos + Handoffs">Accts{sortArrow('totalPost')}</th>
            <th onClick={() => onSort('deltaTotal')} title="Net accounts (post − pre)">Net{sortArrow('deltaTotal')}</th>
            <th onClick={() => onSort('arrPost')}>ARR{sortArrow('arrPost')}</th>
            <th onClick={() => onSort('deltaArr')} title="Net ARR (post − pre)">Net ARR{sortArrow('deltaArr')}</th>
            {QUARTER_LABELS.map((q) => <th key={q} title="Renewal balance (post; pre→post on hover)">{q}</th>)}
            <th onClick={() => onSort('arrivalSeats')} title="Seats reserved for fresh new customers expected to land before ramp">New Logos{sortArrow('arrivalSeats')}</th>
            <th onClick={() => onSort('handoffSeats')} title="Seats expected to be filled by handoffs from over-target reps at ramp">Handoffs{sortArrow('handoffSeats')}</th>
            <th onClick={() => onSort('scorePost')} title="Aggregate expansion (sum of raw 0-83 scores)">Exp{sortArrow('scorePost')}</th>
            {showComps && componentKeys.map((k) => (
              <th key={k} className="bc-comp-col" title={`Avg ${compLabels[k] ?? k} across the book`}>{compLabels[k] ?? k}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((g) => (
            <Fragment key={g.gs}>
              <tr className={g.isNewOwner ? 'bc-newrow' : ''}>
                <td className="bc-sticky-col">
                  <button type="button" className="bc-gs-toggle" onClick={() => toggle(g.gs)}>
                    <span className="bc-caret">{expanded.has(g.gs) ? '▾' : '▸'}</span>
                    {g.gs}{g.isNewOwner ? ' ★' : ''}
                    {hasRegion && g.region ? <span className="bc-region-badge">{g.region}</span> : null}
                    {g.frozen ? <span className="bc-frozen-badge" title="Book frozen - no accounts moved in or out this run">Frozen</span> : null}
                  </button>
                  {g.isNewOwner && (() => {
                    const d = datesByBook.get(g.gs);
                    if (!d?.start && !d?.ramp) return null;
                    const fmt = (iso: string | null) => iso ? new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }) : '—';
                    return (
                      <div className="bc-muted bc-book-dates" title={`Start ${d.start ?? '—'} · books ${d.ramp ?? '—'}`}>
                        {fmt(d.start)} → books {fmt(d.ramp)}
                      </div>
                    );
                  })()}
                </td>
                <td className="bc-mgr-td"><ManagerCell value={repManager.get(g.gs) ?? ''} onCommit={(val) => setManager(g.gs, val)} listId="bc-results-mgr-list" /></td>
                <td>{prePost(g.totalPre, g.totalPost)}</td>
                <td>{deltaSpan(g.totalPost - g.totalPre, String)}</td>
                <td><span className="bc-muted">{money(g.arrPre)}</span>→{money(g.arrPost)}</td>
                <td>{deltaSpan(g.arrPost - g.arrPre, money)}</td>
                {QUARTER_LABELS.map((q, i) => (
                  <td key={q} className="bc-heat" style={{ background: heatBg(g.qPost[i], qRange[i][0], qRange[i][1]) }} title={`${g.qPre[i]}→${g.qPost[i]}`}>
                    {g.qPost[i]}
                  </td>
                ))}
                <td>{g.arrivalSeats || <span className="bc-muted">0</span>}</td>
                <td>{g.handoffSeats || <span className="bc-muted">0</span>}</td>
                <td title={`${g.scorePre}→${g.scorePost}`}>{g.scorePost}</td>
                {showComps && componentKeys.map((k) => (
                  <td key={k} className="bc-heat bc-comp-col" style={{ background: heatBg(g.comp[k] ?? 0, compRange[k][0], compRange[k][1]) }}>
                    {g.comp[k] ?? 0}
                  </td>
                ))}
              </tr>
              {expanded.has(g.gs) && (
                <tr className="bc-sub-row">
                  <td colSpan={colSpan}>
                    <BookFillChart
                      row={{ gs: g.gs, isNewOwner: g.isNewOwner, totalPost: g.totalPost + g.dummyPost }}
                      config={config}
                      newBizStats={newBizStats}
                      projection={projection}
                      teamBooks={teamBooks}
                    />
                    <RepAccounts
                      gs={g.gs}
                      accounts={balance.accountsByGs.get(g.gs) ?? []}
                      gsNames={balance.gsNames}
                      moves={moves}
                      setMove={setMove}
                      componentKeys={componentKeys}
                      compLabels={compLabels}
                      showComps={showComps}
                    />
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Small inline projection of how a book fills from its carved seat count toward the fully-ramped
 * team target, drawn as a whole-account staircase. With a Forecasting schedule the per-region
 * monthly totals are split across the whole team jointly under the overflow-handoff model
 * (conserved — see simulateTeamFill): existing reps plateau at target, with each catch beyond it
 * handed to the earliest-ramping pending book with open seats; they only step past target when no
 * pending book can take the unit. Without a schedule, each book independently gains
 * ~perQuarter/quarter (bias-x under half) and stops at target. New books start filling at their
 * ramp date (first full-week Friday derived from the start date — see engine/ramp.ts); existing
 * reps start from today. Default window is 1 year with an "extend to full" toggle for slower books.
 */
function BookFillChart({ row, config, newBizStats, projection, teamBooks }: {
  row: Pick<BalRow, 'gs' | 'isNewOwner' | 'totalPost'>;
  config: CarveConfig | null;
  newBizStats?: NewBizStats | null;
  /** Team projection settings (monthly schedule + bias). Falls back to the constant rate when absent/empty. */
  projection?: ProjectionSettings | null;
  /** All team books for the joint conserved-split simulation (must include this row). */
  teamBooks?: TeamFillBook[] | null;
}) {
  const DAY = 86_400_000;
  const YEAR_DAYS = 365;
  const QUARTER_DAYS = 91.3;
  const MONTH_DAYS = 30.44;
  const now = Date.now();
  const [extend, setExtend] = useState(false);

  const seed = row.totalPost;
  const ownerCfg = config?.newOwners?.find((o) => o.name === row.gs);
  const isNewBook = row.isNewOwner || !!ownerCfg;

  const fullTarget = isNewBook
    ? (config?.existingTargetCount ?? Math.max(seed, ownerCfg?.targetCount ?? seed))
    : (config?.perRepOverrides?.[row.gs]?.targetCount ?? config?.existingTargetCount ?? seed);
  const perQuarter = config?.estNewBizPerQuarter ?? newBizStats?.perRepPerQuarter ?? 2;
  const sched = projection && projection.months.length > 0 && teamBooks && teamBooks.length > 0 ? projection : null;
  const bias = projection && projection.newBookBias > 0 ? projection.newBookBias : 2;

  const startMs = ownerCfg?.startDate && Number.isFinite(Date.parse(ownerCfg.startDate))
    ? Date.parse(`${ownerCfg.startDate}T00:00:00Z`)
    : now;
  // Manual ramp override (homepage "release") wins over the startDate derivation.
  const rampIso = ownerCfg?.rampDate ?? rampDateFor(ownerCfg?.startDate);
  const rampMs = isNewBook && rampIso ? Date.parse(`${rampIso}T00:00:00Z`) : now;
  const fmtDate = (ms: number) => new Date(ms).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });

  // Without a forecast schedule books stop at target, so an at-target book has nothing to chart.
  // With one, accounts keep round-robining in past target — chart it with target as a reference line.
  if (!(fullTarget > 0) || (!sched && seed >= fullTarget)) {
    return <div className="bc-fillchart bc-fillchart-note">At full book ({seed} account{seed === 1 ? '' : 's'})</div>;
  }
  if (!sched && !(perQuarter > 0)) {
    return (
      <div className="bc-fillchart bc-fillchart-note">
        {seed}/{fullTarget} accounts — set a new-biz growth rate to project fill over time.
      </div>
    );
  }

  // Per-book fill as an integer staircase, one step per assigned account. With a forecast schedule
  // the whole team is simulated jointly (simulateTeamFill) so per-region gains always sum to
  // finance's forecast — bias reweights toward new books, it never inflates the total — and counts
  // keep stepping past target. Without a schedule this book independently gains ~perQuarter/quarter
  // (bias-x under half) and stops at target.
  const daysPerAccount = QUARTER_DAYS / perQuarter;
  const half = fullTarget / 2;
  const seats = teamBooks?.find((b) => b.name === row.gs)?.seats ?? ownerCfg?.expectedAccounts ?? 0;
  let steps: { day: number; count: number }[];
  let fillMs = Infinity;
  let regionMode = false;
  // Pre-ramp forming line (new books with reserved seats): donors are carved on day one, then each
  // incoming overflow unit (a fresh arrival held by a ramped rep, or a tenured handoff committed
  // for ramp) steps the dashed line up toward the full seed. Points are absolute ms.
  let heldPts: [number, number][] = [];
  if (sched) {
    const sim = simulateTeamFill({ projection: sched, books: teamBooks!, startMs: now });
    regionMode = sim.regionMode;
    const mine = sim.byBook.get(row.gs);
    // Simulation steps are day offsets from now; the staircase below plots offsets from rampMs.
    steps = [
      { day: 0, count: seed },
      ...(mine?.steps ?? []).map((s) => ({ day: (now + s.day * MS_DAY - rampMs) / MS_DAY, count: s.count })),
    ];
    if (mine && mine.fillDay !== null) fillMs = now + mine.fillDay * MS_DAY;
    if (isNewBook && seats > 0 && rampMs > now && mine) {
      const donors = Math.max(0, seed - seats);
      heldPts = [[now, donors]];
      let held = donors;
      for (const h of mine.held) {
        const ms = now + h.day * MS_DAY;
        if (ms >= rampMs) break;
        heldPts.push([ms, held]);
        held = donors + h.count;
        heldPts.push([ms, held]);
      }
      heldPts.push([Math.min(rampMs, now + SIM_HORIZON_DAYS * MS_DAY), held]);
    }
  } else {
    steps = [{ day: 0, count: seed }];
    let count = seed, day = 0;
    let guard = 0;
    while (count < fullTarget && guard++ < 10_000) {
      day += daysPerAccount / (count < half ? bias : 1);
      count += 1;
      steps.push({ day, count });
    }
    fillMs = rampMs + steps[steps.length - 1].day * DAY;
  }

  // X window: from the rep's start (new) or today (existing) out 1 year by default; the "extend to
  // full" toggle stretches the window to the fill date for books that need longer than a year.
  const xStart = isNewBook ? startMs : now;
  const defaultEndMs = xStart + YEAR_DAYS * DAY;
  const fillsWithinYear = fillMs <= defaultEndMs;
  const windowEndMs = fillsWithinYear ? defaultEndMs : (extend && Number.isFinite(fillMs) ? fillMs + 15 * DAY : defaultEndMs);

  // Geometry (viewBox units; the SVG itself scales to the container width). The y-axis tops out at
  // the target OR the last count visible in the window — under the forecast schedule books keep
  // gaining past target, so the staircase can climb above the target reference line.
  const W = 720, H = 170, padL = 40, padR = 56, padT = 16, padB = 28;
  const innerW = W - padL - padR, innerH = H - padT - padB;
  // With a pre-ramp forming line the y-axis starts at the donor floor (seed - seats) instead.
  const yMin = Math.max(0, (heldPts.length ? Math.max(0, seed - seats) : seed) - 1);
  let lastVisibleCount = seed;
  for (const s of steps) {
    if (rampMs + s.day * DAY > windowEndMs) break;
    lastVisibleCount = s.count;
  }
  const yMax = Math.max(fullTarget, lastVisibleCount, yMin + 1);
  const xOf = (ms: number) => padL + ((ms - xStart) / (windowEndMs - xStart)) * innerW;
  const yOf = (val: number) => padT + (1 - (val - yMin) / (yMax - yMin)) * innerH;
  const yBase = yOf(yMin);

  // Staircase points (ms space), clipped to the window; hold flat at the last visible count.
  const stepPts: [number, number][] = [];
  let lastCount = seed;
  for (const s of steps) {
    const ms = rampMs + s.day * DAY;
    if (ms > windowEndMs) break;
    stepPts.push([ms, lastCount]); // horizontal run at previous count
    stepPts.push([ms, s.count]);   // vertical jump to new count
    lastCount = s.count;
  }
  if (stepPts.length === 0) stepPts.push([rampMs, seed]);
  stepPts.push([windowEndMs, lastCount]); // hold flat to the right edge
  const line = stepPts.map(([ms, v]) => `${xOf(ms).toFixed(1)},${yOf(v).toFixed(1)}`).join(' ');
  const areaStartMs = Math.max(rampMs, xStart);
  const area = `${xOf(areaStartMs).toFixed(1)},${yBase.toFixed(1)} ${line} ${xOf(windowEndMs).toFixed(1)},${yBase.toFixed(1)}`;
  // Pre-ramp forming line (dashed): donors + seat claims held by ramped reps until ramp.
  const heldLine = heldPts
    .filter(([ms]) => ms >= xStart && ms <= windowEndMs)
    .map(([ms, v]) => `${xOf(ms).toFixed(1)},${yOf(v).toFixed(1)}`)
    .join(' ');

  // Monthly x-axis ticks, thinned so labels never collide (~6 max).
  const totalDays = (windowEndMs - xStart) / DAY;
  const totalMonths = totalDays / MONTH_DAYS;
  const stepM = Math.max(1, Math.ceil(totalMonths / 6));
  const xTicks: number[] = [];
  for (let k = 0; k * stepM * MONTH_DAYS <= totalDays + 0.5; k++) xTicks.push(xStart + k * stepM * MONTH_DAYS * DAY);

  // Integer y-axis gridlines, thinned to ~5 labels; the target always gets its own (reference) line.
  const stepY = Math.max(1, Math.ceil((yMax - yMin) / 5));
  const yTicks: number[] = [];
  for (let v = yMin; v < yMax; v += stepY) if (Math.abs(v - fullTarget) >= stepY / 2) yTicks.push(v);
  if (yMax !== fullTarget) yTicks.push(yMax);
  if (fullTarget > yMin) yTicks.push(fullTarget);

  const clampX = (x: number) => Math.min(Math.max(x, padL + 26), W - padR - 26);
  const crossesTarget = seed < fullTarget;
  const fullVisible = crossesTarget && fillMs <= windowEndMs;

  // Start sits at the left edge for new books, so its clamped label center collides with Ramp's
  // whenever the two dates are close relative to the window. When that happens, hug each label to
  // its own line (Start ends left of its line, Ramp starts right of its line) instead of centering.
  const startLabelX = clampX(xOf(startMs));
  const rampLabelX = clampX(xOf(rampMs));
  const milestonesCollide = isNewBook && Math.abs(rampLabelX - startLabelX) < 44;

  return (
    <div className="bc-fillchart">
      <div className="bc-fillchart-head">
        <span className="bc-fillchart-title">Projected book fill</span>
        <span className="bc-fillchart-sub">
          {crossesTarget
            ? `${seed} now → target ${fullTarget} by ${fullVisible ? fmtDate(fillMs) : '> 1yr'}`
            : `${seed} now · at/above target (${fullTarget})`} ·{' '}
          {sched
            ? `plan: ${scheduleTrajectoryLabel(sched.months, now)} team-wide (${bias}x new books, overflow above ${fullTarget} hands off) · ${regionMode ? 'by region' : 'pooled'}`
            : `~1 acct / ${Math.round(daysPerAccount)}d (${bias}x under half)`}
          {isNewBook && seats > 0 ? ` · ${seats} seat${seats === 1 ? '' : 's'} reserved` : ''}
        </span>
        {!fillsWithinYear && Number.isFinite(fillMs) && (
          <button type="button" className="bc-fillchart-extend" onClick={() => setExtend((e) => !e)}>
            {extend ? 'Show 1yr' : 'Extend to full'}
          </button>
        )}
      </div>
      <svg className="bc-fillchart-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" role="img" aria-label={`Book fills from ${seed} to ${fullTarget} accounts${fullVisible ? ` by ${fmtDate(fillMs)}` : ''}`}>
        {/* integer count gridlines + labels; the target row is a styled reference line */}
        {yTicks.map((v) => (
          <g key={`y${v}`}>
            <line className={v === fullTarget ? 'bc-fillchart-target' : 'bc-fillchart-grid'} x1={padL} y1={yOf(v)} x2={W - padR} y2={yOf(v)} />
            <text className="bc-fillchart-ylabel" x={padL - 6} y={yOf(v) + 3}>{v}</text>
          </g>
        ))}

        {/* monthly gridlines + date ticks */}
        {xTicks.map((ms, i) => (
          <g key={`x${ms}`}>
            <line className="bc-fillchart-tick" x1={xOf(ms)} y1={padT} x2={xOf(ms)} y2={yBase} />
            <text className="bc-fillchart-tick-label" x={xOf(ms)} y={H - padB + 14}>
              {new Date(ms).toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' })}{i === 0 ? ` ’${new Date(ms).toLocaleDateString('en-US', { year: '2-digit', timeZone: 'UTC' })}` : ''}
            </text>
          </g>
        ))}

        {/* fill projection (integer staircase) */}
        <polygon className="bc-fillchart-area" points={area} />
        <polyline className="bc-fillchart-line" points={line} />

        {/* pre-ramp forming line: donors carved on day one + seat claims (arrivals held by ramped
            reps for transfer at ramp) stepping up toward the full seed */}
        {heldLine && <polyline className="bc-fillchart-held" points={heldLine} />}

        {/* new-rep milestones */}
        {isNewBook && startMs >= xStart && startMs <= windowEndMs && (
          <g>
            <line className="bc-fillchart-milestone" x1={xOf(startMs)} y1={padT} x2={xOf(startMs)} y2={yBase} />
            {milestonesCollide ? (
              <text className="bc-fillchart-milestone-label" textAnchor="end" x={xOf(startMs) - 4} y={padT - 4}>Start</text>
            ) : (
              <text className="bc-fillchart-milestone-label" x={startLabelX} y={padT - 4}>Start</text>
            )}
          </g>
        )}
        {isNewBook && rampMs >= xStart && rampMs <= windowEndMs && (
          <g>
            <line className="bc-fillchart-milestone" x1={xOf(rampMs)} y1={padT} x2={xOf(rampMs)} y2={yBase} />
            {milestonesCollide ? (
              <text className="bc-fillchart-milestone-label" textAnchor="start" x={Math.min(xOf(rampMs) + 4, W - padR - 40)} y={padT - 4}>Ramp</text>
            ) : (
              <text className="bc-fillchart-milestone-label" x={rampLabelX} y={padT - 4}>Ramp</text>
            )}
          </g>
        )}

        {/* full marker */}
        {fullVisible && (
          <g>
            <line className="bc-fillchart-full" x1={xOf(fillMs)} y1={padT} x2={xOf(fillMs)} y2={yBase} />
            <text className="bc-fillchart-full-label" x={clampX(xOf(fillMs))} y={padT - 4}>Full · {fmtDate(fillMs)}</text>
            <circle className="bc-fillchart-dot bc-fillchart-dot-full" cx={xOf(fillMs)} cy={yOf(fullTarget)} r={3.5} />
          </g>
        )}
        <circle className="bc-fillchart-dot bc-fillchart-dot-seed" cx={xOf(rampMs)} cy={yOf(seed)} r={3.5} />
      </svg>
    </div>
  );
}

// ────────────────────────────── Variant comparison ──────────────────────────────

function CompareView({ options, a, b, setA, setB, reps, liveReps, config }: {
  options: { id: string; label: string; result: RunResult }[];
  a: string; b: string;
  setA: (id: string) => void; setB: (id: string) => void;
  reps: Rep[];
  liveReps: Rep[];
  config: CarveConfig | null;
}) {
  const regionFallback = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of reps) if (r.region) m.set(r.name, r.region);
    return m;
  }, [reps]);
  const repManager = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of reps) if (r.manager) m.set(r.name, r.manager);
    for (const r of liveReps) if (r.manager) m.set(r.name, r.manager);
    for (const [n, mn] of Object.entries(config?.repManagers ?? {})) if (mn) m.set(n, mn);
    return m;
  }, [reps, liveReps, config]);
  const mgrOf = (gs: string) => repManager.get(gs) ?? '—';
  const [mgrFilter, setMgrFilter] = useState<string>('all');
  const runA = options.find((o) => o.id === a)?.result ?? options[0].result;
  const runB = options.find((o) => o.id === b)?.result ?? options[Math.min(1, options.length - 1)].result;
  const labelA = options.find((o) => o.id === a)?.label ?? 'A';
  const labelB = options.find((o) => o.id === b)?.label ?? 'B';
  // Same out-of-segment donor filter as the Book Summary, so variant metrics compare like-for-like.
  const segmentReps = useMemo(() => {
    const s = new Set<string>();
    for (const r of reps) s.add(r.name);
    for (const r of liveReps) s.add(r.name);
    return s;
  }, [reps, liveReps]);
  const balA = useMemo(() => computeBalance(runA, new Map(), regionFallback, { segmentReps }), [runA, regionFallback, segmentReps]);
  const balB = useMemo(() => computeBalance(runB, new Map(), regionFallback, { segmentReps }), [runB, regionFallback, segmentReps]);
  const seatTot = (bal: Balance, key: 'arrivalSeats' | 'handoffSeats') => bal.rows.reduce((s, r) => s + r[key], 0);

  // ARR spread is only meaningful within a like-sized cohort, so compare groups separately.
  // "Smaller books" = new books plus any book carrying under half the largest book's account count.
  const spr = (vals: number[]) => (vals.length ? Math.max(...vals) - Math.min(...vals) : 0);
  const countSpr = (bal: Balance, pred: (r: BalRow) => boolean) => spr(bal.rows.filter(pred).map((r) => r.totalPost));
  const arrSprK = (bal: Balance, pred: (r: BalRow) => boolean) =>
    Math.round(spr(bal.rows.filter(pred).map((r) => r.arrPost / 1000)) * 100) / 100;
  const maxCount = (bal: Balance) => (bal.rows.length ? Math.max(...bal.rows.map((r) => r.totalPost)) : 0);
  const isSmall = (bal: Balance) => {
    const half = maxCount(bal) / 2;
    return (r: BalRow) => r.isNewOwner || r.totalPost < half;
  };
  const isLarge = (bal: Balance) => {
    const small = isSmall(bal);
    return (r: BalRow) => !small(r);
  };
  const all = () => true;

  // Scoreboard rows; `better` says which direction wins (higher/lower) for highlighting.
  const metrics: { label: string; a: number | null; b: number | null; fmt: (n: number) => string; better: 'high' | 'low' }[] = [
    { label: 'Accounts moved', a: balA.accountsMoved, b: balB.accountsMoved, fmt: String, better: 'low' },
    { label: 'Count spread — new / smaller books', a: countSpr(balA, isSmall(balA)), b: countSpr(balB, isSmall(balB)), fmt: String, better: 'low' },
    { label: 'ARR spread — new / smaller books', a: arrSprK(balA, isSmall(balA)), b: arrSprK(balB, isSmall(balB)), fmt: (n) => `$${Math.round(n)}k`, better: 'low' },
    { label: 'Count spread — larger books', a: countSpr(balA, isLarge(balA)), b: countSpr(balB, isLarge(balB)), fmt: String, better: 'low' },
    { label: 'ARR spread — larger books', a: arrSprK(balA, isLarge(balA)), b: arrSprK(balB, isLarge(balB)), fmt: (n) => `$${Math.round(n)}k`, better: 'low' },
    { label: 'Count spread — overall', a: countSpr(balA, all), b: countSpr(balB, all), fmt: String, better: 'low' },
    { label: 'ARR spread — overall', a: arrSprK(balA, all), b: arrSprK(balB, all), fmt: (n) => `$${Math.round(n)}k`, better: 'low' },
    { label: 'New logo seats', a: seatTot(balA, 'arrivalSeats'), b: seatTot(balB, 'arrivalSeats'), fmt: String, better: 'low' },
    { label: 'Handoff seats', a: seatTot(balA, 'handoffSeats'), b: seatTot(balB, 'handoffSeats'), fmt: String, better: 'low' },
  ];
  const win = (m: { a: number | null; b: number | null; better: 'high' | 'low' }, side: 'a' | 'b'): boolean => {
    if (m.a == null || m.b == null || m.a === m.b) return false;
    const aWins = m.better === 'high' ? m.a > m.b : m.a < m.b;
    return side === 'a' ? aWins : !aWins;
  };

  // Per-rep A/B (union of reps across both runs).
  const aByGs = new Map(balA.rows.map((r) => [r.gs, r]));
  const bByGs = new Map(balB.rows.map((r) => [r.gs, r]));
  const repNames = [...new Set([...aByGs.keys(), ...bByGs.keys()])].sort((x, y) => {
    const nx = (aByGs.get(x)?.isNewOwner || bByGs.get(x)?.isNewOwner) ? 0 : 1;
    const ny = (aByGs.get(y)?.isNewOwner || bByGs.get(y)?.isNewOwner) ? 0 : 1;
    return nx - ny || mgrOf(x).localeCompare(mgrOf(y)) || x.localeCompare(y);
  });
  const managerOptions = [...new Set(repNames.map(mgrOf))].sort((x, y) => x.localeCompare(y));
  const filteredRepNames = mgrFilter === 'all' ? repNames : repNames.filter((n) => mgrOf(n) === mgrFilter);

  // Roll the per-rep books up to each GS manager (across the full union of reps, ignoring the filter).
  const managerAgg = new Map<string, { acA: number; acB: number; arA: number; arB: number; nReps: number }>();
  for (const name of repNames) {
    const mn = mgrOf(name);
    const g = managerAgg.get(mn) ?? { acA: 0, acB: 0, arA: 0, arB: 0, nReps: 0 };
    g.acA += aByGs.get(name)?.totalPost ?? 0;
    g.acB += bByGs.get(name)?.totalPost ?? 0;
    g.arA += aByGs.get(name)?.arrPost ?? 0;
    g.arB += bByGs.get(name)?.arrPost ?? 0;
    g.nReps += 1;
    managerAgg.set(mn, g);
  }
  const managerRows = [...managerAgg.entries()]
    .map(([mn, g]) => ({ mn, ...g }))
    .sort((p, q) => p.mn.localeCompare(q.mn));

  // Per-account owner diff.
  const ownerA = new Map(runA.assignments.map((x) => [x.accountId, x.postGs]));
  const nameById = new Map(runA.assignments.map((x) => [x.accountId, x.accountName]));
  for (const x of runB.assignments) if (!nameById.has(x.accountId)) nameById.set(x.accountId, x.accountName);
  const diffs = runB.assignments
    .filter((x) => ownerA.has(x.accountId) && ownerA.get(x.accountId) !== x.postGs)
    .map((x) => ({ accountId: x.accountId, name: x.accountName, a: ownerA.get(x.accountId)!, b: x.postGs }))
    .sort((p, q) => p.name.localeCompare(q.name));

  return (
    <Card>
      <div className="bc-card-pad">
        <div className="bc-row bc-spread">
          <h3 className="bc-h3">Compare variants</h3>
          <div className="bc-row bc-compare-pickers">
            <label className="bc-cmp-pick">A
              <select value={a} onChange={(e) => setA(e.target.value)}>
                {options.map((o) => <option key={o.id} value={o.id} disabled={o.id === b}>{o.label}</option>)}
              </select>
            </label>
            <label className="bc-cmp-pick">B
              <select value={b} onChange={(e) => setB(e.target.value)}>
                {options.map((o) => <option key={o.id} value={o.id} disabled={o.id === a}>{o.label}</option>)}
              </select>
            </label>
          </div>
        </div>

        <table className="bc-table bc-cmp-board">
          <thead>
            <tr><th>Metric</th><th>{labelA}</th><th>{labelB}</th></tr>
          </thead>
          <tbody>
            {metrics.map((mm) => (
              <tr key={mm.label}>
                <td>{mm.label}</td>
                <td className={win(mm, 'a') ? 'bc-cmp-win' : ''}>{mm.a == null ? '—' : mm.fmt(mm.a)}</td>
                <td className={win(mm, 'b') ? 'bc-cmp-win' : ''}>{mm.b == null ? '—' : mm.fmt(mm.b)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="bc-row bc-spread">
          <h4 className="bc-h4">Per-rep books</h4>
          {managerOptions.length > 1 && (
            <label className="bc-cmp-pick">Manager
              <select value={mgrFilter} onChange={(e) => setMgrFilter(e.target.value)}>
                <option value="all">All managers</option>
                {managerOptions.map((mn) => <option key={mn} value={mn}>{mn}</option>)}
              </select>
            </label>
          )}
        </div>
        <div className="bc-table-scroll">
          <table className="bc-table bc-book-summary">
            <thead>
              <tr>
                <th className="bc-sticky-col">GS</th>
                <th>Manager</th>
                <th>Accts {labelA}</th><th>Accts {labelB}</th><th>Δ accts</th>
                <th>ARR {labelA}</th><th>ARR {labelB}</th><th>Δ ARR</th>
              </tr>
            </thead>
            <tbody>
              {filteredRepNames.map((name) => {
                const ra = aByGs.get(name);
                const rb = bByGs.get(name);
                const acA = ra?.totalPost ?? 0, acB = rb?.totalPost ?? 0;
                const arA = ra?.arrPost ?? 0, arB = rb?.arrPost ?? 0;
                const diff = acA !== acB || arA !== arB;
                const isNew = ra?.isNewOwner || rb?.isNewOwner;
                return (
                  <tr key={name} className={`${isNew ? 'bc-newrow' : ''}${diff ? ' bc-cmp-diffrow' : ''}`}>
                    <td className="bc-sticky-col">{name}{isNew ? ' ★' : ''}</td>
                    <td className="bc-muted">{mgrOf(name)}</td>
                    <td>{acA}</td><td>{acB}</td><td>{deltaSpan(acB - acA, String)}</td>
                    <td><span className="bc-muted">{money(arA)}</span></td><td>{money(arB)}</td><td>{deltaSpan(arB - arA, money)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <h4 className="bc-h4">By manager</h4>
        <div className="bc-table-scroll">
          <table className="bc-table bc-book-summary">
            <thead>
              <tr>
                <th className="bc-sticky-col">Manager</th>
                <th title="Reps reporting to this manager across both carves">Reps</th>
                <th>Accts {labelA}</th><th>Accts {labelB}</th><th>Δ accts</th>
                <th>ARR {labelA}</th><th>ARR {labelB}</th><th>Δ ARR</th>
              </tr>
            </thead>
            <tbody>
              {managerRows.map((g) => {
                const diff = g.acA !== g.acB || g.arA !== g.arB;
                return (
                  <tr key={g.mn} className={diff ? 'bc-cmp-diffrow' : ''}>
                    <td className="bc-sticky-col">{g.mn}</td>
                    <td>{g.nReps}</td>
                    <td>{g.acA}</td><td>{g.acB}</td><td>{deltaSpan(g.acB - g.acA, String)}</td>
                    <td><span className="bc-muted">{money(g.arA)}</span></td><td>{money(g.arB)}</td><td>{deltaSpan(g.arB - g.arA, money)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <h4 className="bc-h4">Accounts that landed differently ({diffs.length})</h4>
        {diffs.length === 0 ? (
          <p className="bc-muted">Both variants assign every account to the same GS.</p>
        ) : (
          <div className="bc-table-scroll">
            <table className="bc-table">
              <thead><tr><th>Account</th><th>{labelA}</th><th>{labelB}</th></tr></thead>
              <tbody>
                {diffs.map((d) => (
                  <tr key={d.accountId}>
                    <td>{d.name}</td>
                    <td className="bc-muted">{d.a}</td>
                    <td>{d.b}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
}

/** Moved-only table (toggled by the Accounts moved KPI): every account whose owner changed this run. */
function MovedAccountsTable({ run, moves, setMove, gsNames }: {
  run: RunResult;
  moves: Map<string, string>;
  setMove: (accountId: string, target: string, originalPost: string) => void;
  gsNames: string[];
}) {
  const [sortKey, setSortKey] = useState('arr');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const eff = (a: AssignmentRow) => moves.get(a.accountId) ?? a.postGs;
  const moved = run.assignments.filter((a) => eff(a) !== a.preGs);

  // Each sortable column: a comparator that returns a negative/zero/positive number for a vs b.
  const cmps: Record<string, (a: AssignmentRow, b: AssignmentRow) => number> = {
    account: (a, b) => a.accountName.localeCompare(b.accountName),
    prev: (a, b) => a.preGs.localeCompare(b.preGs),
    next: (a, b) => eff(a).localeCompare(eff(b)),
    arr: (a, b) => a.activeArr - b.activeArr,
    renewal: (a, b) => (a.renewalQuarter ?? '').localeCompare(b.renewalQuarter ?? ''),
    exp: (a, b) => (a.expansionPercentile ?? -1) - (b.expansionPercentile ?? -1),
  };
  const sorted = [...moved].sort((a, b) => {
    const cmp = (cmps[sortKey] ?? cmps.arr)(a, b);
    return sortDir === 'asc' ? cmp : -cmp;
  });
  const onSort = (key: string) => {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'account' || key === 'prev' || key === 'next' ? 'asc' : 'desc'); }
  };
  const arrow = (key: string) => (key === sortKey ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');
  const cols: { key: string; label: string }[] = [
    { key: 'account', label: 'Account' },
    { key: 'prev', label: 'Prev rep' },
    { key: 'next', label: 'New rep' },
    { key: 'arr', label: 'ARR' },
    { key: 'renewal', label: 'Renewal' },
    { key: 'exp', label: 'Exp %' },
  ];

  return (
    <div className="bc-moved-panel">
      <h4 className="bc-h4">Moved accounts ({moved.length})</h4>
      {moved.length === 0 ? (
        <p className="bc-muted">No accounts moved in this carve.</p>
      ) : (
        <div className="bc-table-scroll">
          <table className="bc-table bc-sub-table">
            <thead>
              <tr>
                {cols.map((c) => (
                  <th key={c.key} className="bc-sortable" onClick={() => onSort(c.key)} title={`Sort by ${c.label}`}>
                    {c.label}{arrow(c.key)}
                  </th>
                ))}
                <th>Reassign</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((a) => (
                <tr key={a.accountId}>
                  <td>{a.accountName}</td>
                  <td className="bc-muted">{a.preGs}</td>
                  <td>{eff(a)}</td>
                  <td>{money(a.activeArr)}</td>
                  <td>{a.renewalQuarter ?? '—'}</td>
                  <td>{a.expansionPercentile ?? '—'}</td>
                  <td>
                    <select
                      className="bc-move-select"
                      value={eff(a)}
                      onChange={(e) => setMove(a.accountId, e.target.value, a.postGs)}
                    >
                      {gsNames.map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RepAccounts({ gs, accounts, gsNames, moves, setMove, componentKeys, compLabels, showComps }: {
  gs: string;
  accounts: AssignmentRow[];
  gsNames: string[];
  moves: Map<string, string>;
  setMove: (accountId: string, target: string, originalPost: string) => void;
  componentKeys: string[];
  compLabels: Record<string, string>;
  showComps: boolean;
}) {
  if (accounts.length === 0) return <p className="bc-muted bc-sub-empty">No accounts in this book.</p>;
  const effOwner = (a: AssignmentRow) => moves.get(a.accountId) ?? a.postGs;
  const round1 = (v: number) => Math.round(v * 10) / 10;
  // Accounts this rep keeps first, then accounts moving out of this rep's book (greyed), each ARR desc.
  const sorted = [...accounts].sort((a, b) => {
    const aOut = effOwner(a) !== gs ? 1 : 0;
    const bOut = effOwner(b) !== gs ? 1 : 0;
    if (aOut !== bOut) return aOut - bOut;
    return b.activeArr - a.activeArr;
  });
  return (
    <div className="bc-table-scroll">
      <table className="bc-table bc-sub-table">
        <thead>
          <tr>
            <th>Account</th>
            <th>Prev rep</th>
            <th>ARR</th>
            <th>Renewal</th>
            <th>Exp %</th>
            {showComps && componentKeys.map((k) => (
              <th key={k} className="bc-comp-col" title={compLabels[k] ?? k}>{compLabels[k] ?? k}</th>
            ))}
            <th>Move to</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((a) => {
            const owner = effOwner(a);
            const movedOut = owner !== gs;
            const moved = moves.has(a.accountId);
            const lockWarn = moved && a.locked;
            const prevRep = movedOut ? null : a.preGs !== gs ? a.preGs : null;
            return (
              <tr key={a.accountId} className={movedOut ? 'bc-moved-out' : moved ? 'bc-moved' : ''}>
                <td>
                  {a.accountName}
                  {lockWarn && (
                    <span className="bc-lock-warn" title="This account was locked (onboarding / renewal / recently-owned / region) — moving it overrides that lock.">⚠ locked</span>
                  )}
                </td>
                <td className="bc-muted">{prevRep ?? '—'}</td>
                <td>{money(a.activeArr)}</td>
                <td>{a.renewalQuarter ?? '—'}</td>
                <td>{a.expansionPercentile ?? '—'}</td>
                {showComps && componentKeys.map((k) => {
                  const v = a.expansionComponents?.[k];
                  return <td key={k} className="bc-comp-col">{v == null ? '—' : round1(v)}</td>;
                })}
                <td>
                  {movedOut ? (
                    <span className="bc-muted bc-moved-to" title={`Moved out of ${gs} to ${owner}`}>→ {owner}</span>
                  ) : (
                    <select
                      className="bc-move-select"
                      value={owner}
                      onChange={(e) => setMove(a.accountId, e.target.value, a.postGs)}
                    >
                      {gsNames.map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function AlternatesBlock({ name, alts, hasRegion }: { name: string; alts: AlternateAccount[]; hasRegion: boolean }) {
  return (
    <details className="bc-alts">
      <summary>{name} — {alts.length} alternate{alts.length === 1 ? '' : 's'}</summary>
      {alts.length === 0 ? (
        <p className="bc-muted">No eligible alternates for this book.</p>
      ) : (
        <div className="bc-table-scroll">
          <table className="bc-table bc-alts-table">
            <thead>
              <tr>
                <th>Account</th>
                <th>From</th>
                <th>ARR</th>
                <th>Renewal</th>
                {hasRegion && <th>Region</th>}
                <th title="Expansion percentile">Exp %</th>
                <th title="Blended fit score (expansion + ARR fit + tenure + renewal balance)">Score</th>
              </tr>
            </thead>
            <tbody>
              {alts.map((a) => (
                <tr key={a.accountId} className={a.regionMatch ? '' : 'bc-alt-mismatch'}>
                  <td>{a.accountName}</td>
                  <td className="bc-muted">{a.currentGs}</td>
                  <td>{money(a.activeArr)}</td>
                  <td>{a.renewalQuarter ?? '—'}</td>
                  {hasRegion && (
                    <td>
                      {a.region ?? '—'}
                      {!a.regionMatch && <span className="bc-mismatch-tag" title="Backfill: region doesn't match this book's region">≠</span>}
                    </td>
                  )}
                  <td>{a.expansionPercentile}</td>
                  <td><strong>{a.score}</strong></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </details>
  );
}

// ────────────────────────────── bits ──────────────────────────────

function Stat({ label, value, onClick, active }: { label: string; value: string; onClick?: () => void; active?: boolean }) {
  const cls = `bc-stat${onClick ? ' bc-stat-clickable' : ''}${active ? ' bc-stat-active' : ''}`;
  if (onClick) {
    return (
      <button type="button" className={cls} onClick={onClick} title="Show only the moved accounts">
        <div className="bc-stat-value">{value}</div>
        <div className="bc-stat-label">{label}</div>
      </button>
    );
  }
  return (
    <div className={cls}>
      <div className="bc-stat-value">{value}</div>
      <div className="bc-stat-label">{label}</div>
    </div>
  );
}

function DiffList({ diff }: { diff: ConfigDiffEntry[] }) {
  if (!diff || diff.length === 0) return null;
  const fmt = (v: unknown) => (v === undefined ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v));
  return (
    <ul className="bc-diff">
      {diff.map((d, i) => (
        <li key={i}><code>{d.path}</code>: <span className="bc-neg">{fmt(d.before)}</span> → <span className="bc-pos">{fmt(d.after)}</span></li>
      ))}
    </ul>
  );
}

/** Minimal markdown: **bold** and paragraph breaks (no external dep). */
function Markdown({ text }: { text: string }) {
  const parts = text.split('\n\n');
  return (
    <div className="bc-md">
      {parts.map((p, i) => (
        <p key={i} dangerouslySetInnerHTML={{ __html: escapeHtml(p).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }} />
      ))}
    </div>
  );
}
function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
